import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import EmojiPicker from "emoji-picker-react";
import { Archive as ArchiveIcon, ArchiveRestore, ArrowLeft, Ban, Brain, Check, CheckCheck, ChevronDown, Clock4,
        ClockAlert, ClockPlus, Copy, CornerUpLeft, CornerUpRight, FolderDown, FolderSearch, MessageSquare, MessageSquareDot, MessageSquarePlus, Mic,
        Paperclip, Pencil, Pin, PinOff, Save, Search, Send, SendHorizontal, Settings, ShieldX, Smile, SmilePlus, Tag, Trash2,
        X as XIcon, Zap, Phone, PhoneMissed, Video, UserCheck, UserRound, UserPlus,
      } from "lucide-react";
import SmartViews from "./SmartViews";
import Composer from "./Composer";
import "./PainelMensagens.css";


const REFRESH_MS = 4000;
const CHECK_UNREAD_BG_MS = 10000;

const btnDrop = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '10px 12px',
  background: 'white',
  border: 'none',
  borderTop: '1px solid #eee',
  cursor: 'pointer',
  color: '#075e54',
  textAlign: 'left'
};

const formatarMoedaBR = (valor) => {
  const n = Number(valor || 0);
  return n.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

// Converte 'YYYY-MM-DD HH:MM:SS' (MySQL) ou ISO para Date **em horário local**
function dateFromServer(s) {
  if (!s) return new Date(NaN);
  // ISO já ok
  if (/\dT\d/.test(s)) return new Date(s);

  // MySQL 'YYYY-MM-DD HH:MM:SS' (sem timezone)
  const m = String(s).match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/
  );
  if (!m) return new Date(s); // fallback
  const [_, Y, M, D, h, i, sec] = m;
  return new Date(
    Number(Y),
    Number(M) - 1,
    Number(D),
    Number(h),
    Number(i),
    Number(sec || 0)
  );
}

function parseWhatsAppText(text) {
  if (!text) return "";

  const escapeHtml = (value) =>
    String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  const escapeAttr = (value) =>
    String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  const normalizePhoneToCRM = (raw) => {
    let n = String(raw || "").replace(/\D+/g, "");

    if (!n) return null;

    // Remove prefixos comuns
    if (n.startsWith("00")) n = n.slice(2);

    // Se vier sem DDI: DDD + número
    if (n.length === 10 || n.length === 11) {
      n = "55" + n;
    }

    // Aceita apenas Brasil no padrão esperado
    if (!n.startsWith("55")) return null;
    if (n.length !== 12 && n.length !== 13) return null;

    const ddd = n.slice(2, 4);
    const local = n.slice(4);

    // Celular com 9 dígitos
    if (local.length === 9) {
      return `55${ddd}${local}`;
    }

    // Fixo ou celular sem 9
    if (local.length === 8) {
      const first = local[0];

      // Fixo
      if (/^[2-5]$/.test(first)) {
        return `55${ddd}${local}`;
      }

      // Celular sem 9
      return `55${ddd}9${local}`;
    }

    return null;
  };

  let safe = escapeHtml(text);
  const tokens = [];

  const addToken = (html) => {
    const token = `%%TOKEN${tokens.length}%%`;
    tokens.push(html);
    return token;
  };

  // 1) Links externos
  safe = safe.replace(/((?:https?:\/\/|www\.)[^\s<]+)/gi, (match) => {
    let urlTexto = match;
    let sufixo = "";

    while (/[.,!?;:)]$/.test(urlTexto)) {
      sufixo = urlTexto.slice(-1) + sufixo;
      urlTexto = urlTexto.slice(0, -1);
    }

    const href = urlTexto.startsWith("www.")
      ? `https://${urlTexto}`
      : urlTexto;

    return addToken(
      `<a href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer" class="msg-link">${urlTexto}</a>${sufixo}`
    );
  });

  // 2) Telefones BR
  safe = safe.replace(
    /(?:\+?55\s*)?(?:\(?\d{2}\)?\s*)?(?:9\s*)?\d{4,5}[-\s]?\d{4}/g,
    (match) => {
      const telefoneCRM = normalizePhoneToCRM(match);

      if (!telefoneCRM) return match;

return addToken(
  `<a href="#" data-phone="${escapeAttr(telefoneCRM)}" class="msg-phone-link">${match}</a>`
);
    }
  );

  // 3) Formatação WhatsApp
  safe = safe
    .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
    .replace(/_(.*?)_/g, "<em>$1</em>")
    .replace(/~(.*?)~/g, "<del>$1</del>")
    .replace(/`(.*?)`/g, "<code>$1</code>")
    .replace(/\n/g, "<br />");

  tokens.forEach((html, index) => {
    safe = safe.replace(`%%TOKEN${index}%%`, html);
  });

  return safe;
}

const resolveKeyToOpen = (conv) => {
  const telKeyRaw = String(conv?.tel_key || "").trim();
  const telRaw = String(conv?.telefone || "").trim();

  const isLidKey = (s) => typeof s === "string" && s.startsWith("lid:");
  const isGroupLike = (s) =>
    /@g\.us$|@lid$|-group$|\d+-\d+$/.test(String(s || "").trim());

  const onlyDigits = (s) => String(s || "").replace(/\D+/g, "");

  // 1) Se tiver tel_key no formato "lid:", usa direto
  if (isLidKey(telKeyRaw)) return telKeyRaw;

  // 2) Se "telefone" já parece grupo/jid, não mexe
  if (isGroupLike(telRaw)) return telRaw;

  // 3) Se tel_key for um grupo no formato concat numérico longo, tenta split
  const tkDigits = onlyDigits(telKeyRaw);
  if (/^\d{18,30}$/.test(tkDigits)) {
    const tk = tkDigits;

    const trySplit = (prefixLen) => {
      if (prefixLen !== 12 && prefixLen !== 13) return null;
      const prefix = tk.slice(0, prefixLen);
      const rest = tk.slice(prefixLen);
      if (!prefix || !rest) return null;
      if (!prefix.startsWith("55")) return null; // heurística BR
      return `${prefix}-${rest}`;
    };

    // BR: 55 + DDD(2) => índice 4 é o primeiro dígito após DDD
    const primeiroAposDDD = tk[4];
    const pareceCelularBR = primeiroAposDDD === "9";

    const candidate = pareceCelularBR
      ? (trySplit(13) || trySplit(12))
      : (trySplit(12) || trySplit(13));

    return candidate || tk;
  }

  // 4) Fallback: conversa 1:1 normal
  // (se tel_key existir e for útil, você pode preferir ele aqui — mas mantenho como era: telRaw)
  return telRaw;
};


function parseWhatsAppPreview(text) {
  if (!text) return "";

  return text
    // remove quebras de linha
    .replace(/\n+/g, " ")

    // escapar HTML
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")

    // mantém formatação inline
    .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
    .replace(/_(.*?)_/g, "<em>$1</em>")
    .replace(/~(.*?)~/g, "<del>$1</del>")
    .replace(/`(.*?)`/g, "<code>$1</code>");
}


function formatarHorarioLista(dataEnvio) {
  if (!dataEnvio) return '';

  const data = new Date(dataEnvio);
  const agora = new Date();

  const hoje = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
  const diaMensagem = new Date(data.getFullYear(), data.getMonth(), data.getDate());

  const diffMs = hoje - diaMensagem;
  const diffDias = diffMs / (1000 * 60 * 60 * 24);

  // Formatos
  const diasSemana = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

  if (diffDias === 0) {
    // HOJE → mostra horário
    return data.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  if (diffDias === 1) {
    // ONTEM
    return 'ontem';
  }

  if (diffDias >= 2 && diffDias <= 6) {
    // ANTEONTEM ATÉ 6 DIAS ATRÁS → dia da semana
    return diasSemana[data.getDay()];
  }

  // 7+ dias → data completa
  return data.toLocaleDateString('pt-BR');
}

// Ordena mensagens ASC por tempo (ts_ms > data_envio_iso > data_envio), com desempates estáveis
function sortMensagensAsc(list) {
  return list
    .map((m, i) => ({ m, i }))
    .sort((a, b) => {
      const ta =
        Number(a.m?.ts_ms ?? 0) ||
        (a.m?.data_envio_iso ? Date.parse(a.m.data_envio_iso) : 0) ||
        (a.m?.data_envio ? Date.parse(a.m.data_envio) : 0) ||
        0;

      const tb =
        Number(b.m?.ts_ms ?? 0) ||
        (b.m?.data_envio_iso ? Date.parse(b.m.data_envio_iso) : 0) ||
        (b.m?.data_envio ? Date.parse(b.m.data_envio) : 0) ||
        0;

      if (ta !== tb) return ta - tb;

      // desempate consistente por id
      const ida = Number(a.m?.id || 0);
      const idb = Number(b.m?.id || 0);
      if (ida !== idb) return ida - idb;

      // estabilidade
      return a.i - b.i;
    })
    .map(({ m }) => m);
}


// Chaves de dia (local) para "Hoje" / "Ontem" / data
function dayKey(d) {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}


const PAGE_SIZE = 30;
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const PRIMARY_COLOR = "#232332";

const ICON = { size: 26, strokeWidth: 2 };      // padrão da cápsula
const ICON_CLIP = { size: 22, strokeWidth: 1 }; // clip menor
const ICON_EMOJI = { size: 24, strokeWidth: 2 }; // 😀 maior

export const btnPrimary = {
  background: PRIMARY_COLOR,
  color: "#fff",
  border: "none",
  padding: "10px 14px",
  fontWeight: 400,
  cursor: "pointer",
};

export const btnPrimarySm = { ...btnPrimary, padding: "8px 12px", width: 110 };
export const btnPrimaryLg = { ...btnPrimary, padding: "12px 22px", minWidth: 190 };

export const ACTIONS_COL_MIN = 160;

export const btnIcon2 = {
  background: "transparent",
  border: "none",
  padding: 0,
  cursor: "pointer",
  lineHeight: 0,
};

export const iconProps = { size: 22, strokeWidth: 2, color: "#111", style: { stroke: "#111" } };

export const th = { textAlign: "left", padding: "10px 8px", fontWeight: 600, fontSize: 13, color: "#333" };
export const td = { padding: "10px 8px", fontSize: 13, color: "#333", verticalAlign: "top" };


// --- fora de PainelMensagens, no topo do arquivo ---
const ModalListaAcoes = React.memo(function ModalListaAcoes(props) {
  const {
    visible,
    onClose,
    acoes,
    carregandoAcoes,
    editandoId,
    edicaoBuffer,
    setEdicaoBuffer,
    setEditandoId,
    iniciarEdicao,
    salvarEdicao,
    excluirAcao,
    toggleStatus,
    togglingId,
    setExibirModalAcao,
    formatPtBr,
  } = props;

  if (!visible) return null;

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000
      }}
      onClick={onClose}
    >
      <div
        className="acoes-modal"
        style={{
          background: "#fff", width: "95%", maxWidth: 1200, borderRadius: 10,
          padding: 16, boxShadow: "0 10px 30px rgba(0,0,0,.25)"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Ações do Lead</h3>
          <div style={{ display: "flex", gap: 10 }}>
            <button style={btnPrimaryLg} onClick={() => setExibirModalAcao(true)} title="Criar nova ação">
              + Adicionar ação
            </button>
            <button style={btnPrimary} onClick={onClose}>Fechar</button>
          </div>
        </div>

        {carregandoAcoes ? (
          <div style={{ padding: 12 }}>Carregando...</div>
        ) : acoes.length === 0 ? (
          <div style={{ padding: 12, color: "#777" }}>Nenhuma ação cadastrada para este lead.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f5f5f7" }}>
                  <th style={th}>Data</th>
                  <th style={th}>Descrição</th>
                  <th style={th}>Status</th>
                  <th
                    style={{
                      ...th,
                      minWidth: ACTIONS_COL_MIN,
                      width: ACTIONS_COL_MIN,
                      textAlign: "center",
                    }}
                  >
                    Ações
                  </th>
                </tr>
              </thead>

              <tbody>
                {acoes.map((a) => {
                  const emEdicao = editandoId === a.id;
                  return (
                    <tr key={a.id} style={{ borderBottom: "1px solid #eee" }}>
                      <td style={td}>
                        {emEdicao ? (
                          <input
                            type="date"
                            value={edicaoBuffer.data_acao || ""}
                            onChange={(e) => setEdicaoBuffer(prev => ({ ...prev, data_acao: e.target.value }))}
                          />
                        ) : (
                          formatPtBr(a.data_acao)
                        )}
                      </td>

                      <td style={td}>
                        {emEdicao ? (
                          <textarea
                            rows={2}
                            style={{ width: "100%" }}
                            value={edicaoBuffer.descricao}
                            onChange={(e) => setEdicaoBuffer(prev => ({ ...prev, descricao: e.target.value }))}
                          />
                        ) : (
                          a.descricao
                        )}
                      </td>

                      <td style={td}>
                        {emEdicao ? (
                          <select
                            value={edicaoBuffer.status}
                            onChange={(e) => setEdicaoBuffer(prev => ({ ...prev, status: e.target.value }))}
                          >
                            <option value="Pendente">Pendente</option>
                            <option value="Concluída">Concluída</option>
                          </select>
                        ) : (
                          a.status
                        )}
                      </td>

                      <td style={{ ...td, minWidth: ACTIONS_COL_MIN, width: ACTIONS_COL_MIN }}>
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 16, width: "100%" }}>
                          {emEdicao ? (
                            <>
                              <button style={btnIcon2} onClick={() => salvarEdicao(a.id)} title="Salvar">
                                <Save {...iconProps} />
                              </button>
                              <button style={btnIcon2} onClick={() => setEditandoId(null)} title="Cancelar">
                                <XIcon {...iconProps} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button style={btnIcon2} onClick={() => iniciarEdicao(a)} title="Editar">
                                <Pencil {...iconProps} />
                              </button>

                              <button
                                style={btnIcon2}
                                onClick={() => toggleStatus(a)}
                                title={a.status === "Concluída" ? "Marcar como Pendente" : "Concluir ação"}
                                disabled={togglingId === a.id}
                              >
                                {togglingId === a.id ? (
                                  <ClockAlert {...iconProps} className="spin" />
                                ) : a.status === "Concluída" ? (
                                  <ClockAlert {...iconProps} />
                                ) : (
                                  <Check {...iconProps} />
                                )}
                              </button>

                              <button style={btnIcon2} onClick={() => excluirAcao(a.id)} title="Excluir">
                                <Trash2 {...iconProps} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
});

// --- fora de PainelMensagens, junto dos outros modais ---
const ModalArquivos = React.memo(function ModalArquivos({ visible, onClose, leadId }) {
  const [kind, setKind] = useState(null);                // null | 'imagens' | 'documentos'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hoverTab, setHoverTab] = useState(null);        // 'imagens' | 'documentos' | null
  const [filtroOrigem, setFiltroOrigem] = useState('todas'); // 'todas' | 'enviado' | 'recebido'

  const LIMIT = 40;
  const DAYS = 120;

  const resetState = useCallback(() => {
    setItems([]); setTotal(0); setOffset(0);
  }, []);

  const fetchFiles = useCallback(async (kindSel, off = 0) => {
    if (!leadId || !kindSel) return;
    setLoading(true);
    try {
      const url = new URL("/crm-wly/api/whatsapp/get_files.php", window.location.origin);
      url.searchParams.set("lead_id", String(leadId));
      url.searchParams.set("kind", kindSel);           // 'imagens' | 'documentos'
      url.searchParams.set("days", String(DAYS));
      url.searchParams.set("limit", String(LIMIT));
      url.searchParams.set("offset", String(off));

      const res = await fetch(url, { credentials: "include", cache: "no-store" });
      const data = await res.json();
      if (data?.success) {
        setItems(prev => off === 0 ? data.data : [...prev, ...data.data]);
        setTotal(Number(data.total || 0));
        setOffset(off);
      }
    } catch (e) {
      console.error("Erro ao carregar arquivos:", e);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (visible && kind) {
      resetState();
      fetchFiles(kind, 0);
    }
  }, [visible, kind, fetchFiles, resetState]);

  if (!visible) return null;

  // aplica filtro de origem no client (sem mais roundtrips ao back)
  const itemsFiltrados = items.filter(it => {
    if (filtroOrigem === 'todas') return true;
    return it?.origem === filtroOrigem;
  });

  const hasMore = items.length < total;

  const tabBase = (active) => ({
    width: 100,
    maxWidth: 100,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 10px",
    borderRadius: 10,
    border: "1px solid #075e54",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
    userSelect: "none",
    transition: "background .15s ease",
    color: active ? "#fff" : "#075e54",
    background: active ? "#075e54" : "#f2f2f2"
  });

  const tabHover = {
    background: "#075e54",
    color: "#fff"
  };

  const filtroBtnBase = (active) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px 10px",
    borderRadius: 8,
    border: active ? "1px solid #075e54" : "1px solid #ddd",
    background: active ? "#e7f3f0" : "#f7f7f7",
    color: "#075e54",
    fontSize: 12,
    fontWeight: active ? 700 : 600,
    cursor: "pointer"
  });

  return (
    <div className="pm-modal-backdrop" onClick={onClose}>
      <div className="pm-modal" onClick={(e) => e.stopPropagation()} style={{ width: 760, maxWidth: "96vw" }}>
        <div className="pm-modal-header" style={{ marginBottom: 6 }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontWeight: 800 }}>
              Arquivos do lead (últimos {DAYS} dias)
            </h3>
            <div style={{ fontSize: 12, color: "#6b6b6b", marginTop: 4 }}>
              <em>Arquivos que são excluídos pelo servidor do WhatsApp, não vão abrir.</em>
            </div>
          </div>
          <button className="pm-modal-close" onClick={onClose} title="Fechar">×</button>
        </div>




        <div className="pm-modal-body" style={{ paddingTop: 0 }}>
          {!kind && (
            <div style={{ display: "flex", gap: 16, justifyContent: "center", padding: "10px 0 18px" }}>
              {/* Tabs centralizadas com largura fixa e cor #075e54 */}
              <div
                onMouseEnter={() => setHoverTab('imagens')}
                onMouseLeave={() => setHoverTab(null)}
                style={{ ...(tabBase(false)), ...(hoverTab === 'imagens' ? tabHover : {}) }}
                onClick={() => setKind("imagens")}
                title="Ver imagens"
              >
                Imagens
              </div>
              <div
                onMouseEnter={() => setHoverTab('documentos')}
                onMouseLeave={() => setHoverTab(null)}
                style={{ ...(tabBase(false)), ...(hoverTab === 'documentos' ? tabHover : {}) }}
                onClick={() => setKind("documentos")}
                title="Ver documentos"
              >
                Documentos
              </div>
            </div>
          )}

          {kind && (
            <>
              {/* Tabs superiores (estado ativo) */}
              <div style={{ display: "flex", gap: 16, justifyContent: "center", marginBottom: 10 }}>
                <div
                  onMouseEnter={() => setHoverTab('imagens')}
                  onMouseLeave={() => setHoverTab(null)}
                  style={{ ...(tabBase(kind === 'imagens')), ...(hoverTab === 'imagens' && kind !== 'imagens' ? tabHover : {}) }}
                  onClick={() => setKind("imagens")}
                >
                  Imagens
                </div>
                <div
                  onMouseEnter={() => setHoverTab('documentos')}
                  onMouseLeave={() => setHoverTab(null)}
                  style={{ ...(tabBase(kind === 'documentos')), ...(hoverTab === 'documentos' && kind !== 'documentos' ? tabHover : {}) }}
                  onClick={() => setKind("documentos")}
                >
                  Documentos
                </div>
              </div>

              {/* SEGUNDO FILTRO: Enviadas / Recebidas / Todas */}
              <div style={{ display: "flex", gap: 8, justifyContent: "center", margin: "6px 0 14px" }}>
                <button
                  type="button"
                  onClick={() => setFiltroOrigem('todas')}
                  style={filtroBtnBase(filtroOrigem === 'todas')}
                  title="Mostrar todas"
                >
                  Todas
                </button>
                <button
                  type="button"
                  onClick={() => setFiltroOrigem('enviado')}
                  style={filtroBtnBase(filtroOrigem === 'enviado')}
                  title="Somente enviadas"
                >
                  Enviadas
                </button>
                <button
                  type="button"
                  onClick={() => setFiltroOrigem('recebido')}
                  style={filtroBtnBase(filtroOrigem === 'recebido')}
                  title="Somente recebidas"
                >
                  Recebidas
                </button>
              </div>

              {/* Lista */}
              {loading && items.length === 0 && <div style={{ padding: 16 }}>Carregando…</div>}

              {!loading && itemsFiltrados.length === 0 && (
                <div style={{ padding: 16, color: "#666" }}>
                  Nenhum arquivo encontrado para “{kind}” {filtroOrigem !== 'todas' ? `(${filtroOrigem})` : ''}.
                </div>
              )}

              {kind === "imagens" && itemsFiltrados.length > 0 && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", // miniatura um pouco menor
                    gap: 12,
                  }}
                >
                  {itemsFiltrados.map((it) => (
                    <a
                      key={it.id}
                      href={it.midia_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`${it.origem === "enviado" ? "Enviado" : "Recebido"} • ${new Date(it.data_envio).toLocaleString()}`}
                      className="card-arquivo"
                      style={{
                        display: "block",
                        border: "1px solid #e8e8e8",
                        borderRadius: 10,
                        overflow: "hidden",
                        textDecoration: "none",
                        background: "#f6f1ea"
                      }}
                    >
                      <img
                        src={it.midia_url}
                        alt=""
                        style={{ width: "100%", height: 90, objectFit: "cover", display: "block", background: "#fafafa" }}
                        loading="lazy"
                      />
                      <div style={{ padding: "6px 8px", fontSize: 12, color: "#444", display: "flex", justifyContent: "space-between" }}>
                        <span>{it.origem === "enviado" ? "Enviado" : "Recebido"}</span>
                        <span>{new Date(it.data_envio).toLocaleDateString()}</span>
                      </div>
                    </a>
                  ))}
                </div>
              )}

              {kind === "documentos" && itemsFiltrados.length > 0 && (
                <div style={{ display: "grid", gap: 10 }}>
                  {itemsFiltrados.map((it) => {
                    const nome = (() => {
                      try {
                        const u = new URL(it.midia_url, window.location.origin);
                        return decodeURIComponent((u.pathname.split("/").pop() || "").split("?")[0]) || "documento";
                      } catch {
                        const p = it.midia_url.split("?")[0];
                        return decodeURIComponent(p.split("/").pop() || "documento");
                      }
                    })();

                    return (
                      <a
                        key={it.id}
                        href={it.midia_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="card-arquivo"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "10px 12px",
                          border: "1px solid #eee",
                          borderRadius: 10,
                          textDecoration: "none",
                          color: "#333",
                          background: "#f6f1ea"
                        }}
                        title={`${it.origem === "enviado" ? "Enviado" : "Recebido"} • ${new Date(it.data_envio).toLocaleString()}`}
                      >
                        <div style={{ width: 8, height: 8, borderRadius: 8, background: it.origem === "enviado" ? "#2e7d32" : "#0277bd" }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{nome}</div>
                          <div style={{ fontSize: 12, color: "#666" }}>
                            {it.origem === "enviado" ? "Enviado" : "Recebido"} • {new Date(it.data_envio).toLocaleDateString()}
                          </div>
                        </div>
                      </a>
                    );
                  })}
                </div>
              )}

              {/* Paginação */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14 }}>
                <div style={{ fontSize: 12, color: "#666" }}>
                  {itemsFiltrados.length} de {total}
                </div>
                {hasMore && (
                  <button
                    type="button"
                    onClick={() => fetchFiles(kind, offset + LIMIT)}
                    disabled={loading}
                    style={{
                      background: "#111",
                      color: "#fff",
                      border: "none",
                      borderRadius: 8,
                      padding: "8px 12px",
                      cursor: "pointer"
                    }}
                  >
                    {loading ? "Carregando…" : "Carregar mais"}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
});



// === Lightbox (abre em real / fit; clique: 2x -> 1x) ===
function ZoomLightbox({ open, src, onClose, overscan = 0.92 }) {
  const imgRef = React.useRef(null);
  const [scale, setScale] = React.useState(1);       // escala atual (transform)
  const [baseScale, setBaseScale] = React.useState(1); // escala de abertura (1 = real; <1 = fit)
  const [origin, setOrigin] = React.useState("50% 50%");
  const [pos, setPos] = React.useState({ x: 0, y: 0 });
  const dragging = React.useRef(false);
  const last = React.useRef({ x: 0, y: 0 });

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  // Define a escala de abertura: 1x se couber; senão "fit" (<1)
  const handleLoad = React.useCallback(() => {
    const img = imgRef.current;
    if (!img) return;
    const natW = img.naturalWidth || 1;
    const natH = img.naturalHeight || 1;
    const maxW = window.innerWidth * overscan;
    const maxH = window.innerHeight * overscan;
    const fit = Math.min(maxW / natW, maxH / natH, 1); // nunca > 1
    setBaseScale(fit);
    setScale(fit);     // abre em 1x (se couber) ou em fit (se for maior que a tela)
    setPos({ x: 0, y: 0 });
  }, [overscan]);

  // Clique na imagem: se está em baseScale (1x/fit) -> 2x; se >1 -> 1x
  const onClickImg = (ev) => {
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const ox = ((ev.clientX - rect.left) / rect.width) * 100;
    const oy = ((ev.clientY - rect.top) / rect.height) * 100;
    setOrigin(`${ox}% ${oy}%`);

    setScale((s) => {
      const atBase = s <= baseScale + 0.01;
      if (atBase) return 2;        // 1º clique -> 2x
      // senão volta p/ 1x (tamanho real)
      setPos({ x: 0, y: 0 });
      return 1;
    });
  };

  // Pan quando com zoom > 1
  const onPointerDown = (e) => {
    if (scale <= 1) return;
    dragging.current = true;
    last.current = { x: e.clientX, y: e.clientY };
    e.preventDefault();
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    const dx = e.clientX - last.current.x;
    const dy = e.clientY - last.current.y;
    last.current = { x: e.clientX, y: e.clientY };
    setPos((p) => ({ x: p.x + dx, y: p.y + dy }));
  };
  const onPointerUp = () => { dragging.current = false; };

  // Zoom por roda (opcional): limita entre baseScale e 3x
  const onWheel = (e) => {
    if (!open) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.2 : 0.2;
    const minZ = baseScale; // não deixa menor que o estado de abertura
    const next = Math.max(minZ, Math.min(3, +(scale + delta).toFixed(2)));
    setScale(next);
    if (next <= 1.01) setPos({ x: 0, y: 0 });
  };

  if (!open) return null;

  return (
    <div
      className="zl-overlay"
      onClick={(e) => {
        if (e.target.classList.contains("zl-overlay")) onClose?.();
      }}
      onWheel={onWheel}
    >
      <img
        ref={imgRef}
        src={src}
        alt=""
        className="zl-img"
        onLoad={handleLoad}
        onClick={onClickImg}
        onMouseDown={onPointerDown}
        onMouseMove={onPointerMove}
        onMouseUp={onPointerUp}
        onMouseLeave={onPointerUp}
        draggable={false}
        // 1x = tamanho real; se for maior que a tela, baseScale < 1 encaixa
        style={{
          transformOrigin: origin,
          transform: `translate3d(${pos.x}px, ${pos.y}px, 0) scale(${scale})`,
        }}
      />
      <button className="zl-close" onClick={onClose} aria-label="Fechar">×</button>
      <div className="zl-hint">
        Abre em real (ou ajustado). Clique: 2×. Clique novamente: 1×. Arraste para mover. Esc/fora fecha.
      </div>
    </div>
  );
}

const SelectLeadModal = React.memo(function SelectLeadModal({ leads = [], onClose, onPick }) {
  const [q, setQ] = React.useState("");

  const filtrados = React.useMemo(() => {
    const normalizar = (v) =>
      String(v || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();

    const s = q.trim();
    const qTexto = normalizar(s);
    const qDigits = String(s || "").replace(/\D+/g, "");

    if (!qTexto && !qDigits) return leads.slice(0, 500);

    return leads.filter(l => {
      const nome = normalizar(l.nome);
      const tel = String(l.telefone || "").replace(/\D+/g, "");

      return (
        (qTexto && nome.includes(qTexto)) ||
        (qDigits && tel.includes(qDigits))
      );
    }).slice(0, 500);
  }, [q, leads]);

  return (
    <div className="pm-modal-overlay" onClick={onClose}>
      <div className="pm-modal-card" onClick={e => e.stopPropagation()}>
        <div className="pm-modal-header">
          <strong>Enviar contato</strong>
          <button className="pm-modal-close" onClick={onClose} aria-label="Fechar">×</button>
        </div>

        <div className="pm-modal-body">
          <input
            className="pm-modal-search"
            placeholder="Buscar por nome ou telefone…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />

          <div className="pm-lead-list" role="listbox" aria-label="Leads">
            {filtrados.length === 0 ? (
              <div className="pm-empty">Nenhum lead encontrado.</div>
            ) : (
              filtrados.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  className="pm-lead-row"
                  onClick={() => onPick?.(l)}
                  title="Enviar este contato"
                >
                  <div className="pm-lead-name">{l.nome || "(Sem nome)"}</div>
                  <div className="pm-lead-phone">{l.telefone}</div>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="pm-modal-footer">
          <button className="pm-btn-cancel" onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
});



export default function PainelMensagens() {
  
  const [leads, setLeads] = useState([]);
  const [telefone, setTelefone] = useState("");
  const [leadId, setLeadId] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [novaMensagem, setNovaMensagem] = useState("");
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);
  const [ultimasConversas, setUltimasConversas] = useState([]);
  const [carregandoConversas, setCarregandoConversas] = useState(true);
  const primeiroLoadConversasRef = useRef(true);
  const [uploading, setUploading] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const audioChunksRef = useRef([]);
  const gravacaoCanceladaRef = useRef(false);
  const [estagioAtual, setEstagioAtual] = useState("");
  const [tagsLead, setTagsLead] = useState([]);
  const [imagemModal, setImagemModal] = useState(null);
  const [tagsDisponiveis, setTagsDisponiveis] = useState([]);
  const [exibirModalAcao, setExibirModalAcao] = useState(false);
  const [descricaoAcao, setDescricaoAcao] = useState("");
  const [dataAcao, setDataAcao] = useState("");
  const [objecaoAcao, setObjecaoAcao] = useState("");
  const [salvandoAcao, setSalvandoAcao] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [mensagem, setMensagem] = useState("");
  const [iaBloqueado, setIaBloqueado] = useState(false);
  const [iaPermitida, setIaPermitida] = useState(false);
  const [mostrarModalArquivos, setMostrarModalArquivos] = useState(false);

//  const [rrVisivel, setRrVisivel] = useState(false);
//  const [rrLista, setRrLista] = useState([]);
//  const [rrFiltro, setRrFiltro] = useState('');
//  const [rrIndex, setRrIndex] = useState(0);
//  const rrRef = useRef(null);
  const navigate = useNavigate();
  const chatAreaRef = useRef(null);
  const precisaScrollarRef = useRef({ tel: "", done: true });
  const [replyDraft, setReplyDraft] = useState(null);
  const btnMenuRef = useRef(null);
  const menuRef = useRef(null);
  const stickToBottomRef = useRef(true); 
  const [mostrarModalAcoes, setMostrarModalAcoes] = useState(false);
  const [acoes, setAcoes] = useState([]);
  const [carregandoAcoes, setCarregandoAcoes] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [edicaoBuffer, setEdicaoBuffer] = useState({ descricao: "", data_acao: "", status: "Pendente" });
  const [isMarkingAll, setIsMarkingAll] = useState(false);
  const [chatName, setChatName] = useState("");
  const [togglingId, setTogglingId] = useState(null);
  const [emojiMenuFor, setEmojiMenuFor] = useState(null);
  const [showJumpBottom, setShowJumpBottom] = useState(false);
  
  const [hasMoreOld, setHasMoreOld] = useState(true);
  const [fetchingOlder, setFetchingOlder] = useState(false);
  const suppressNextAutoScrollRef = useRef(false);
  const autoScrollLockRef = useRef(false);
  const bloqueiaPollingRef = useRef(false);


  const [fwdOpen, setFwdOpen] = useState(false);
  const [fwdFilter, setFwdFilter] = useState('');
  const [fwdMessage, setFwdMessage] = useState(null);
  const [fwdLeadIds, setFwdLeadIds] = useState([]); 
  const [fwdSending, setFwdSending] = useState(false);
  const fwdSendingRef = useRef(false);

  const [startOpen, setStartOpen] = React.useState(false);
  const [startPhone, setStartPhone] = React.useState('');
  const [startLoading, setStartLoading] = React.useState(false);

  const [ephemeralConv, setEphemeralConv] = React.useState(null);
  const [isGroup, setIsGroup] = useState(false);

  const [lastId, setLastId] = useState(0);
  const [started, setStarted] = useState(false);

  const [tagsLoading, setTagsLoading] = useState(false);

  const [olderLinkDelayOk, setOlderLinkDelayOk] = useState(false);

  const [leadSearchOpen, setLeadSearchOpen] = useState(false);
  const [leadSearchQuery, setLeadSearchQuery] = useState('');

  const [rowMenuOpenKey, setRowMenuOpenKey] = useState(null);

const [showArchivedOnly, setShowArchivedOnly] = useState(false);

const [msgMenuOpenFor, setMsgMenuOpenFor] = useState(null);
const [editDraft, setEditDraft] = useState(null);
const [savingEdit, setSavingEdit] = useState(false); 

const [mostrarModalNovaFuncaoEditar, setMostrarModalNovaFuncaoEditar] = useState(false);

const [editandoOrcamento, setEditandoOrcamento] = useState(false);
const [valorOrcamentoEditando, setValorOrcamentoEditando] = useState("");
const [salvandoOrcamento, setSalvandoOrcamento] = useState(false);

const [atendentes, setAtendentes] = useState([]);
const [atendenteSelecionado, setAtendenteSelecionado] = useState(null);

const [modalAtendenteOpen, setModalAtendenteOpen] = useState(false);
const [modalNovoAtendenteOpen, setModalNovoAtendenteOpen] = useState(false);
const [novoAtendenteNome, setNovoAtendenteNome] = useState("");
const [salvandoAtendente, setSalvandoAtendente] = useState(false);

const [showTooltipAtendente, setShowTooltipAtendente] = useState(false);
const [carregandoInicial, setCarregandoInicial] = useState(true);

const [whatsappStatus, setWhatsappStatus] = useState(null);
const [carregandoStatusWhatsapp, setCarregandoStatusWhatsapp] = useState(false);

const whatsappConectado =
  Number(whatsappStatus?.whatsapp_ativo ?? 1) === 1 &&
  whatsappStatus?.connected === true &&
  whatsappStatus?.smartphoneConnected === true;

const whatsappStatusVerificado = whatsappStatus !== null;

const whatsappBloqueiaEnvio =
  whatsappStatusVerificado && !whatsappConectado;

const isArchived = useCallback((c) => {
  return Number(c?.is_archived || 0) === 1 || c?.is_archived === true;
}, []);


  const [leadSearchDebounced, setLeadSearchDebounced] = useState('');
  useEffect(() => {
  const t = setTimeout(() => setLeadSearchDebounced(leadSearchQuery.trim().toLowerCase()), 180);
  return () => clearTimeout(t);
}, [leadSearchQuery]);

useEffect(() => {
  let ativo = true;

  const buscarLeadsNoBackend = async () => {
    const q = leadSearchDebounced.trim();

    if (!q || q.length < 3) {
      setLeadSearchResults([]);
      setLeadSearchLoading(false);
      return;
    }

    try {
      setLeadSearchLoading(true);

      const res = await fetch(
        `/crm-wly/getLeads.php?q=${encodeURIComponent(q)}&limite=100`,
        {
          credentials: "include",
          cache: "no-store",
        }
      );

      const data = await res.json();

      if (!ativo) return;

      setLeadSearchResults(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erro ao buscar leads na lupa:", err);
      if (ativo) setLeadSearchResults([]);
    } finally {
      if (ativo) setLeadSearchLoading(false);
    }
  };

  buscarLeadsNoBackend();

  return () => {
    ativo = false;
  };
}, [leadSearchDebounced]);

const carregarLeadsCompletos = useCallback(async (force = false) => {
  if (!force && Array.isArray(leads) && leads.length > 0) {
    return leads;
  }

  try {
    const res = await fetch("/crm-wly/getLeads.php", {
      credentials: "include",
      cache: "no-store",
    });

    const data = await res.json().catch(() => []);

    if (Array.isArray(data)) {
      setLeads(data);
      return data;
    }

    return [];
  } catch (err) {
    console.error("Erro ao buscar lista completa de leads:", err);
    return [];
  }
}, [leads]);


const [tagFilterOpen, setTagFilterOpen] = useState(false);
const [tagFilterIds, setTagFilterIds] = useState([]);

// === CLIP MENU + CONTATO (vCard) ===
const [clipOpen, setClipOpen] = useState(false);
const clipRef = useRef(null);

const [contactOpen, setContactOpen] = useState(false);
const [contactNome, setContactNome] = useState("");
const [contactPhones, setContactPhones] = useState([""]);
const [selectedChat, setSelectedChat] = useState(null);

const [gravacaoSegundos, setGravacaoSegundos] = useState(0);

const [editandoNomeLead, setEditandoNomeLead] = useState(false);
const [nomeLeadEditando, setNomeLeadEditando] = useState("");
const [salvandoNomeLead, setSalvandoNomeLead] = useState(false);

const [leadSearchResults, setLeadSearchResults] = useState([]);
const [leadSearchLoading, setLeadSearchLoading] = useState(false);


const [mostrarModalAgendamento, setMostrarModalAgendamento] = useState(false);
const [mensagemFila, setMensagemFila] = useState("");
const [enviandoAgendamento, setEnviandoAgendamento] = useState(false);
const [delaySegundos, setDelaySegundos] = useState(5);
const [dataAgendamento, setDataAgendamento] = useState("");
const [horaAgendamento, setHoraAgendamento] = useState("");
const [arquivoUrl, setArquivoUrl] = useState("");
const [tipoMidia, setTipoMidia] = useState("");
const [uploadEmAndamento, setUploadEmAndamento] = useState(false);
const [mostrarEmojiPickerAgendamento, setMostrarEmojiPickerAgendamento] = useState(false);

const [smartviewAtiva, setSmartviewAtiva] = useState(null);
const [smartviewFiltroLeads, setSmartviewFiltroLeads] = useState([]);
const [smartviewFiltroTelKeys, setSmartviewFiltroTelKeys] = useState([]);
const [smartviewFiltroTelefones, setSmartviewFiltroTelefones] = useState([]);

const [smartviewRows, setSmartviewRows] = useState([]);

const [origemAtual, setOrigemAtual] = useState("");

const [prefsNotifOpen, setPrefsNotifOpen] = useState(false);

const [audioNotificacaoAtivo, setAudioNotificacaoAtivo] = useState(() => {
  return localStorage.getItem("norea_audio_notificacao") !== "0";
});

const tituloOriginalRef = useRef(document.title || "Nórea CRM");
const unreadAnteriorRef = useRef(0);
const notificacaoIniciadaRef = useRef(false);
const audioNotificacaoRef = useRef(null);

const msgAgendamentoRef = useRef(null);

// fecha o menu/ modal ao clicar fora
useEffect(() => {
  function onClickOutside(e) {
    if (clipRef.current && !clipRef.current.contains(e.target)) {
      setClipOpen(false);
    }
  }
  document.addEventListener("mousedown", onClickOutside);
  return () => document.removeEventListener("mousedown", onClickOutside);
}, []);


  const msgsCacheRef = useRef(new Map());

  const msgsReqRef = React.useRef(0);


  const tagsReqRef = React.useRef(0);

  const fetchingMsgsRef = useRef(false);

  const startInputRef = useRef(null);

  const pauseSinceUntilRef = useRef(0);


  const lastMarkReadRef = useRef(0);

  const myReactionsRef = useRef(new Map());
  
  const unreadOverrideRef = useRef(new Map()); 

  const pinnedOverrideRef = useRef(new Map());

  const archivedOverrideRef = useRef(new Map());

  
  const containerRef = useRef(null);

  const mensagensRef = useRef([]);

useEffect(() => {
  mensagensRef.current = mensagens;
}, [mensagens]);

const tagDropdownRef = useRef(null);

const conversasFetchIdRef = useRef(0);

const chatRef = useRef(null);
const shouldSnapBottomRef = useRef(false);

const forceSnapBottomRef  = useRef(false);

const telefoneChatAtualRef = useRef("");

const gravacaoIntervalRef = useRef(null);


  const unreadAtual = useMemo(() => {
  const key = getChatKey(telefone || "");
  const c = ultimasConversas.find((x) => getChatKey(x) === key);
  return Number(c?.unread_count || 0);
}, [ultimasConversas, telefone]);

  const tagDestaque = useMemo(() => {
  if (!Array.isArray(tagsLead) || tagsLead.length === 0) return null;
  // escolha simples: a última do array; mude a regra se quiser
  return tagsLead[tagsLead.length - 1];
}, [tagsLead]);

const archivedCount = useMemo(
  () => (Array.isArray(ultimasConversas) ? ultimasConversas.filter(isArchived).length : 0),
  [ultimasConversas, isArchived]
);

const copiarTexto = (txt) => {
  if (!txt) return;
  navigator.clipboard.writeText(txt).then(() => {
    // opcional: feedback visual
  });
};

const currentKey = useMemo(() => {
  if (selectedChat) return convStableKey(selectedChat);
  return convStableKey({ telefone: telefone || "" });
}, [selectedChat, telefone]);

const aplicarSmartview = (payload) => {
  setSmartviewAtiva({
    key: payload.key,
    title: payload.title,
    total: payload.total,
  });

  setSmartviewFiltroLeads(payload.leadIds || []);
  setSmartviewFiltroTelKeys(payload.telKeys || []);
  setSmartviewFiltroTelefones(payload.telefones || []);
  setSmartviewRows(payload.rows || []);

  setTimeout(() => {
  recarregarConversas(false, true);
}, 100);

};

const limparSmartview = () => {
  setSmartviewAtiva(null);
  setSmartviewFiltroLeads([]);
  setSmartviewFiltroTelKeys([]);
  setSmartviewFiltroTelefones([]);
  setSmartviewRows([]);
};

const conversasRender = useMemo(() => {
  const base = Array.isArray(ultimasConversas) ? ultimasConversas : [];
  const depoisArquivo = showArchivedOnly ? base.filter(isArchived) : base.filter(c => !isArchived(c));

  const ids = Array.isArray(tagFilterIds) ? tagFilterIds : [];
  return ids.length > 0
    ? depoisArquivo.filter(conv => ids.every(id => conversaPossuiTag(conv, id)))
    : depoisArquivo;
}, [ultimasConversas, showArchivedOnly, isArchived, tagFilterIds]);

  // Mensagens ordenadas e limitadas para aliviar renderização
  const mensagensVisiveis = useMemo(() => {
    if (!mensagens || mensagens.length === 0) return [];

    // ordem garantida por ts_ms (ou data_envio), depois id
    const ordenadas = [...mensagens].sort((a, b) => {
      const ta = a.ts_ms || (a.data_envio ? new Date(a.data_envio).getTime() : 0);
      const tb = b.ts_ms || (b.data_envio ? new Date(b.data_envio).getTime() : 0);

      if (ta === tb) {
        return (a.id || 0) - (b.id || 0);
      }
      return ta - tb;
    });

    // limita as últimas N (ex: 200) para não entupir o React
    const LIMITE = 200;
    if (ordenadas.length > LIMITE) {
      return ordenadas.slice(ordenadas.length - LIMITE);
    }

    return ordenadas;
  }, [mensagens]);

const conversasComSmartview = useMemo(() => {
  if (!smartviewAtiva) return conversasRender;

  const rows = Array.isArray(smartviewRows) ? smartviewRows : [];

  if (rows.length > 0) {
    return rows.map((r) => {
      const existente = conversasRender.find((conv) => {
        const leadIdConv = Number(conv.lead_id_resolvido ?? conv.lead_id ?? 0);
        const leadIdRow = Number(r.lead_id || r.smartview_lead_id || 0);

        const telKeyConv = String(conv.tel_key || "").trim();
        const telKeyRow = String(r.tel_key || "").trim();

        const telConv = String(conv.telefone || "").trim();
        const telRow = String(r.telefone || "").trim();

        return (
          (leadIdConv > 0 && leadIdConv === leadIdRow) ||
          (telKeyConv && telKeyConv === telKeyRow) ||
          (telConv && telConv === telRow)
        );
      });

      return {
        ...(existente || {}),
        ...r,

        lead_id:
          Number(
            r.lead_id ||
            r.smartview_lead_id ||
            existente?.lead_id ||
            0
          ) || null,

        lead_id_resolvido:
          Number(
            r.lead_id ||
            r.smartview_lead_id ||
            existente?.lead_id_resolvido ||
            0
          ) || null,

        lead_nome:
          r.nome ||
          existente?.lead_nome ||
          existente?.nome ||
          r.telefone,

        telefone:
          String(
            r.telefone ||
            existente?.telefone ||
            r.tel_key ||
            ""
          ),

        tel_key:
          r.tel_key ||
          existente?.tel_key ||
          "",

        tipo:
          smartviewAtiva?.key === "leads_quentes"
            ? "entrada"
            : smartviewAtiva?.key === "aguardando_resposta"
              ? "saida"
              : (
                  r.tipo ||
                  existente?.tipo ||
                  ""
                ),

        mensagem:
          r.ultima_mensagem ||
          r.mensagem ||
          r.descricao ||
          existente?.mensagem ||
          "",

        data_envio:
          r.smartview_last_msg_time ||
          r.last_in_msg_time ||
          r.data_acao ||
          existente?.data_envio ||
          null,

        unread_count:
          r.unread_count ||
          existente?.unread_count ||
          0,

        tags:
          existente?.tags || [],

        photo:
          existente?.photo ||
          existente?.foto ||
          null,
      };
    });
  }

  return [];
}, [conversasRender, smartviewAtiva, smartviewRows]);



const selectedTagNames = useMemo(() => {
  const ids = new Set((Array.isArray(tagFilterIds) ? tagFilterIds : []).map(Number));
  return tagsDisponiveis
    .filter(t => ids.has(Number(t.id)))
    .map(t => t.nome);
}, [tagFilterIds, tagsDisponiveis]);

const composerRef = React.useRef(null);

const fwdDestinos = useMemo(() => {
  const gruposMap = new Map();
  const contatosMap = new Map();

  const isGroupLike = (tel) =>
    Number(tel?.is_group) === 1 ||
    /@g\.us$|-group$|^\d+-\d+$/.test(String(tel || "").trim());

  const contatoKey = (tel, telKey = "") => {
    const tk = String(telKey || "").trim();
    const t = String(tel || "").trim();

    if (tk) return `tk:${tk}`;
    if (t) return `tel:${t.replace(/\D+/g, "")}`;
    return "";
  };

  // 1) Grupos vindos das conversas
  (ultimasConversas || []).forEach((c) => {
    const tel = String(c?.telefone || "").trim();

    const isGroup =
      Number(c?.is_group) === 1 ||
      /@g\.us$|-group$|^\d+-\d+$/.test(tel);

    if (!isGroup || !tel) return;

    const groupKey = tel;

    if (gruposMap.has(groupKey)) return;

    gruposMap.set(groupKey, {
      id: `group:${tel}`,
      nome:
        c?.chat_name ||
        c?.chatName ||
        c?.lead_nome ||
        c?.remetente_nome ||
        tel,
      telefone: tel,
      foto: c?.photo || c?.foto || "",
      is_group: 1,
      tel_key: String(c?.tel_key || "").trim(),
    });
  });

  // 2) Contatos normais vindos de leads
  (leads || []).forEach((l) => {
    const tel = String(l?.telefone || "").trim();
    const telKey = String(l?.tel_key || "").trim();
    const key = contatoKey(tel, telKey);

    if (!key) return;
    if (contatosMap.has(key)) return;

    contatosMap.set(key, {
      id: l.id,
      nome: l.nome || "(sem nome)",
      telefone: tel,
      foto: l.foto || "",
      is_group: 0,
      tel_key: telKey,
    });
  });

  // 3) Contatos normais vindos das conversas
  (ultimasConversas || []).forEach((c) => {
    const tel = String(c?.telefone || "").trim();
    const telKey = String(c?.tel_key || "").trim();

    const isGroup =
      Number(c?.is_group) === 1 ||
      /@g\.us$|-group$|^\d+-\d+$/.test(tel);

    if (isGroup || !tel) return;

    const key = contatoKey(tel, telKey);
    if (!key) return;

    if (contatosMap.has(key)) {
      const existente = contatosMap.get(key);

      // se já existe, mas está sem nome bom, melhora com dados da conversa
      contatosMap.set(key, {
        ...existente,
        nome:
          existente.nome && existente.nome !== "(sem nome)"
            ? existente.nome
            : c?.lead_nome || c?.remetente_nome || tel,
        foto: existente.foto || c?.photo || c?.foto || "",
      });

      return;
    }

    contatosMap.set(key, {
      id: c?.lead_id || c?.lead_id_resolvido || key,
      nome: c?.lead_nome || c?.remetente_nome || tel,
      telefone: tel,
      foto: c?.photo || c?.foto || "",
      is_group: 0,
      tel_key: telKey,
    });
  });

  return [...Array.from(contatosMap.values()), ...Array.from(gruposMap.values())];
}, [leads, ultimasConversas]);

const tocarSomNotificacao = useCallback(() => {
  if (!audioNotificacaoAtivo) return;

  try {
    if (!audioNotificacaoRef.current) {
      audioNotificacaoRef.current = new Audio("/crm-wly/sounds/norea-notificacao.mp3");
      audioNotificacaoRef.current.volume = 0.6;
    }

    audioNotificacaoRef.current.currentTime = 0;
    audioNotificacaoRef.current.play().catch(() => {
      console.warn("Som bloqueado pelo navegador até interação do usuário.");
    });
  } catch (e) {
    console.warn("Erro ao tocar notificação:", e);
  }
}, [audioNotificacaoAtivo]);

const atualizarTituloENotificacao = useCallback((total) => {
  const totalNum = Number(total || 0);

  document.title = totalNum > 0
    ? `(${totalNum}) Nórea CRM`
    : tituloOriginalRef.current;

  if (!notificacaoIniciadaRef.current) {
    unreadAnteriorRef.current = totalNum;
    notificacaoIniciadaRef.current = true;
    return;
  }

  if (totalNum > unreadAnteriorRef.current && !isMarkingAll) {
    tocarSomNotificacao();
  }

  unreadAnteriorRef.current = totalNum;
}, [isMarkingAll, tocarSomNotificacao]);

const checarTotalNaoLidasLeve = useCallback(async () => {
  try {
    const res = await fetch("/crm-wly/api/whatsapp/unread_total.php", {
      credentials: "include",
      cache: "no-store",
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data?.success) return;

    atualizarTituloENotificacao(Number(data.total || 0));
  } catch (e) {
    console.warn("Erro ao checar total de não lidas:", e);
  }
}, [atualizarTituloENotificacao]);

useEffect(() => {
  let stop = false;
  let t = null;

  const loop = async () => {
    if (stop) return;

    await checarTotalNaoLidasLeve();

    t = setTimeout(loop, CHECK_UNREAD_BG_MS);
  };

  loop();

  return () => {
    stop = true;
    if (t) clearTimeout(t);
  };
}, [checarTotalNaoLidasLeve]);

// Botão "X" → pede confirmação e chama a exclusão
const onDeleteMessageClick = (m) => {
  if (!m?.id_mensagem_zapi) {
    alert("Mensagem sem id válido.");
    return;
  }
  if (!window.confirm("Apagar esta mensagem para todos?")) return;
  deleteMessage(m);
};

const agendado_em = (dataAgendamento && horaAgendamento)
  ? `${dataAgendamento} ${horaAgendamento}:00`
  : null;

const handleEnviarAgendamentoLead = async () => {
  const telefoneAtual = String(selectedChat?.telefone || telefone || "").trim();

const ehGrupo =
  Number(selectedChat?.is_group) === 1 ||
  /@g\.us$|-group$|^\d+-\d+$|^\d{18,30}$/.test(telefoneAtual);

  if (!leadId && !ehGrupo) {
    alert("Nenhum lead ou grupo selecionado.");
    return;
  }

  if (!mensagemFila.trim() && !arquivoUrl) {
    alert("Digite uma mensagem ou anexe um arquivo para enviar.");
    return;
  }

  try {
    setEnviandoAgendamento(true);

    const payload = {
      mensagem: mensagemFila,
      tipo_midia: tipoMidia || null,
      midia_url: arquivoUrl || null,
      delay_envio: delaySegundos,
      agendado_em,
    };

    if (ehGrupo) {
      payload.grupos = [{
        telefone: telefoneAtual,
        nome: grupoTitulo || chatName || telefoneAtual
      }];
    } else {
      payload.lead_ids = [leadId];
    }

    const res = await fetch("/crm-wly/api/whatsapp/criar_fila_zapi.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    });

    const data = await res.json();

    if (data.success) {
      alert("Mensagem agendada com sucesso!");
      setMostrarModalAgendamento(false);
      setMensagemFila("");
      setArquivoUrl("");
      setTipoMidia("");
      setDataAgendamento("");
      setHoraAgendamento("");
      setDelaySegundos(5);
    } else {
      alert("Erro ao adicionar na fila: " + (data.message || ""));
    }
  } catch (err) {
    console.error(err);
    alert("Erro ao agendar mensagem.");
  } finally {
    setEnviandoAgendamento(false);
  }
};

const formatarTempoGravacao = (totalSegundos) => {
  const min = Math.floor(totalSegundos / 60);
  const seg = totalSegundos % 60;
  return `${String(min).padStart(2, "0")}:${String(seg).padStart(2, "0")}`;
};


function getContraste(hex = "#666666") {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "#666666");
  if (!m) return "#fff";
  const r = parseInt(m[1], 16), g = parseInt(m[2], 16), b = parseInt(m[3], 16);
  return (r*299 + g*587 + b*114) / 1000 >= 128 ? "#111" : "#fff";
}

// Normalização EXCLUSIVA para iniciar chat manual
// Aceita: com/sem 55, com/sem 9, celular e fixo
const normalizeBRStart = (raw) => {
  let n = String(raw || "").replace(/\D+/g, "");

  // remove prefixos de discagem internacional comuns
  if (n.startsWith("00")) n = n.slice(2);
  if (n.startsWith("0") && n.length >= 12) n = n.slice(1);

  // se vier sem DDI
  if (n.length === 10 || n.length === 11) {
    n = "55" + n;
  }

  if (!n.startsWith("55")) return null;
  if (n.length !== 12 && n.length !== 13) return null;

  const ddd = n.slice(2, 4);
  if (!/^\d{2}$/.test(ddd)) return null;

  const local = n.slice(4);

  // celular já correto
  if (local.length === 9) {
    return "55" + ddd + local;
  }

  // local com 8 dígitos → decidir fixo x celular sem 9
  if (local.length === 8) {
    const first = local[0];

    // fixo: começa normalmente com 2–5
    if (/^[2-5]$/.test(first)) {
      return "55" + ddd + local;
    }

    // celular sem 9
    return "55" + ddd + "9" + local;
  }

  return null;
};


// Normaliza BR: retorna '55' + DDD + 9? + últimos 8 (13 dígitos) ou null
const normalizeBR = (raw) => {
  let n = (raw || '').replace(/\D+/g, '');
  if (n.length === 10 || n.length === 11) n = '55' + n;
  if (!n.startsWith('55') || n.length < 12 || n.length > 13) return null;

  const ddd = n.slice(2, 4);
  const sub = n.slice(4); // 8 ou 9
  const last8 = sub.slice(-8);
  const can = sub.length === 9 ? `55${ddd}${sub}` : `55${ddd}9${sub}`;
  return can;
};

function buildVCardFromLead(lead) {
  const nome = (lead?.nome || "Contato").trim();
  const tel  = String(lead?.telefone || "").replace(/\D+/g, "");
  const telOut = tel.startsWith("55") ? `+${tel}` : tel;
  return [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `FN:${nome}`,
    `TEL;TYPE=CELL:${telOut}`,
    "END:VCARD"
  ].join("\n");
}


const handleStartChatFromRef = () => {
  flushConversaAtualComoLida();
  const raw = startInputRef.current?.value || '';
  if (!raw.trim()) return;
  handleStartChat(raw);  // vamos adaptar handleStartChat para aceitar parâmetro
  setStartOpen(false);
};


// Atualiza, imediatamente, o preview da conversa ativa na coluna da esquerda
function atualizarPreviewConversaLocal(texto) {
  if (!telefone) return;
  const msg = String(texto || "").trim();
  if (!msg) return;

  const nowIso = new Date().toISOString();
  const nomeLead = (leadSelecionado && leadSelecionado.nome) || chatName || telefone;

  setUltimasConversas((prev) => {
    const base = Array.isArray(prev) ? [...prev] : [];
    const key = selectedChat ? getChatKey(selectedChat) : getChatKey(telefone || "");
    let foundConv = null;
    let found = false;

    const arr = base.map((c) => {
      const ck = c._key || getChatKey(c);
      if (ck !== key) return c;

      found = true;
      foundConv = {
        ...c,
        mensagem: msg,
        data_envio: nowIso,
        tipo: "saida",
        unread_count: 0,
        leitura: 0,
        is_archived: 0,
        photo: c.photo ?? c.foto ?? null,
      };

      return foundConv;
    });

    if (!found) {
      foundConv = {
        telefone: selectedChat?.telefone || telefone,
        tel_key: selectedChat?.tel_key || undefined,
        _key: key,
        mensagem: msg,
        data_envio: nowIso,
        tipo: "saida",
        unread_count: 0,
        leitura: 0,
        is_archived: 0,
        is_group: isGroup ? 1 : 0,
        lead_id: leadId || null,
        lead_nome: nomeLead,
        tags: Array.isArray(tagsLead) ? tagsLead : [],
        photo:
          selectedChat?.photo ||
          selectedChat?.foto ||
          leads.find((l) => l.id === leadId)?.foto ||
          ultimasConversas.find((c) => getChatKey(c) === key)?.photo ||
          null,
      };

      arr.push(foundConv);
    }

    if (foundConv) {
      setSelectedChat((prevSel) =>
        prevSel ? { ...prevSel, ...foundConv } : prevSel
      );
    }

    return arr;
  });

  ordenarConversas();
}



// ---- helper: garante 1 mensagem por id/id_mensagem_zapi/midia_url ----
function dedupeMensagens(arr) {
  if (!Array.isArray(arr)) return [];

  const seenZ = new Set(); // id_mensagem_zapi
  const seenD = new Set(); // id
  const seenU = new Set(); // midia_url
  const outRev = [];

  // percorre do fim -> início, assim a versão mais nova vence
  for (let i = arr.length - 1; i >= 0; i--) {
    const m = arr[i];

    const z = m?.id_mensagem_zapi;
    if (z) {
      if (seenZ.has(z)) continue;
      seenZ.add(z);
    }

    const d = m?.id;
    if (d) {
      if (seenD.has(d)) continue;
      seenD.add(d);
    }

    // para arquivos, dedupe também por midia_url (independente de tipo),
    // porque a versão antiga pode estar sem tipo mas com a mesma URL.
    const u = m?.midia_url ? String(m.midia_url) : null;
    if (u) {
      if (seenU.has(u)) continue;
      seenU.add(u);
    }

    outRev.push(m);
  }

  // volta para ordem original
  return outRev.reverse();
}

const abrirConversaPorTelefone = async (rawPhone) => {
  const phone = normalizeBRStart(rawPhone);

  if (!phone) return;

  flushConversaAtualComoLida();

  const convDaLista = (ultimasConversas || []).find((c) => {
    const telC = String(
      c?.telefone_open ||
      resolveKeyToOpen(c) ||
      c?.telefone ||
      ""
    ).trim();

    return getChatKey(telC) === getChatKey(phone);
  });

  const leadEncontrado = (leads || []).find((l) => {
    return getChatKey(l?.telefone || "") === getChatKey(phone);
  });

  const conv = convDaLista || {
    telefone: phone,
    telefone_open: phone,
    lead_id: leadEncontrado?.id || null,
    lead_id_resolvido: leadEncontrado?.id || null,
    lead_nome: leadEncontrado?.nome || phone,
    photo: leadEncontrado?.foto || null,
    foto: leadEncontrado?.foto || null,
    is_group: 0,
    tags: leadEncontrado?.tags || [],
  };

  const phoneToOpen = String(
    conv?.telefone_open ||
    resolveKeyToOpen(conv) ||
    conv?.telefone ||
    phone
  ).trim();

  telefoneChatAtualRef.current = phoneToOpen;

  const phoneLower = phoneToOpen.toLowerCase();
  const isLid = phoneLower.startsWith("lid:") || phoneLower.endsWith("@lid");

  const isGroupPattern =
    !isLid &&
    (
      /@g\.us$/i.test(phoneToOpen) ||
      /-group$/i.test(phoneToOpen) ||
      /^\d+-\d+$/.test(phoneToOpen) ||
      /^\d{18,30}$/.test(phoneToOpen)
    );

  const isGroupClk = Number(conv?.is_group) === 1 || isGroupPattern;

  const newLeadId = isGroupClk
    ? null
    : Number(
        conv?.lead_id_resolvido ||
        conv?.lead_id ||
        leadEncontrado?.id ||
        0
      ) || null;

  setSelectedChat({
    ...conv,
    telefone: phoneToOpen,
    telefone_open: phoneToOpen,
  });

  setIsGroup(isGroupClk);
  setLeadId(newLeadId);
  setEstagioAtual(isGroupClk ? "" : (conv.estagio_funil || leadEncontrado?.estagio_funil || ""));
  setOrigemAtual(isGroupClk ? "" : (conv.origem_lead || leadEncontrado?.origem || ""));
  setChatName(
    conv.chat_name ||
    conv.chatName ||
    conv.lead_nome ||
    leadEncontrado?.nome ||
    conv.remetente_nome ||
    phoneToOpen
  );

  setTagsLead([]);
  setLastId(0);
  setMensagens([]);
  setStarted(false);

  setTelefone(phoneToOpen);

  const stableKey = convStableKey({
    ...conv,
    telefone_open: phoneToOpen,
    telefone: phoneToOpen,
    lead_id: newLeadId,
    lead_id_resolvido: newLeadId,
  });

  const cached = msgsCacheRef.current.get(stableKey);

  if (Array.isArray(cached) && cached.length) {
    const orderedCached = sortMsgsAsc(cached.map(normalizeMsg));
    setMensagens(orderedCached);
    setStarted(true);

    requestAnimationFrame(() => {
      autoScrollLockRef.current = false;
      const area = chatAreaRef.current;
      if (area) area.scrollTop = area.scrollHeight;
      chatEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
    });
  }

  markLocalReadByKey(stableKey);
  marcarComoLida(phoneToOpen);
};

const handleMessageTextClick = (e) => {
  const phoneLink = e.target.closest(".msg-phone-link");

  if (!phoneLink) return;

  e.preventDefault();
  e.stopPropagation();

  const phone = phoneLink.dataset.phone;

  if (!phone) return;

  abrirConversaPorTelefone(phone);
};

// === Handler comum para upload (reaproveita seu enviar_arquivo.php)
async function handleSendFile(file) {
  if (!file || !telefone) return;

  const formData = new FormData();
  formData.append("arquivo", file);
  formData.append("telefone", toBackendPhone(telefone));

  if (leadId) {
    formData.append("lead_id", leadId);
  }

  try {
    setUploading(true);
    setLoading(true);

    const res = await fetch("/crm-wly/api/whatsapp/enviar_arquivo.php", {
      method: "POST",
      body: formData,
      credentials: "include",
    });

    const text = await res.text();
    let data = {};

    try {
      data = JSON.parse(text);
    } catch {
      data = {
        success: false,
        message: text || `Erro HTTP ${res.status}`,
      };
    }

    if (!res.ok || data?.success === false) {
      console.error("Erro enviar_arquivo.php:", data);
      alert(data?.message || data?.detail || `Erro HTTP ${res.status}`);
      return;
    }

    const novo = {
      tipo: "saida",
      mensagem: data.mensagem,
      tipo_midia: data.tipo_midia || null,
      midia_url: data.midia_url || data.url || null,
      id_mensagem_zapi: data.id_mensagem_zapi || null,
      status_envio: data.status_envio || "sucesso",
      telefone,
      data_envio: new Date().toISOString(),
      is_group: /^\d+-\d+$/.test(String(telefone || "")) ? 1 : 0,
    };

    setMensagens((prev) => dedupeMensagens([...(prev || []), novo]));

  } catch (err) {
    console.error("Erro ao enviar arquivo:", err);
    alert(err?.message || "Erro ao enviar arquivo.");
  } finally {
    setUploading(false);
    setLoading(false);
  }
}

// === Envio de contato (vCard) ===
// back espera: tipo=contato, contato_nome, contato_telefones[] (repetido), opcional contato_vcard
async function handleSendContact({ nome, telefones, vcardText }) {
  if (!telefone) return;
  const formData = new FormData();
  formData.append("tipo", "contato");
  formData.append("telefone", toBackendPhone(telefone));
  if (leadId) formData.append("lead_id", leadId);
  if (nome) formData.append("contato_nome", nome);

  (telefones || []).forEach(t => {
    const v = String(t || "").trim();
    if (v) formData.append("contato_telefones[]", v);
  });

  if (vcardText && vcardText.trim()) {
    formData.append("contato_vcard", vcardText.trim());
  }

  try {
    setLoading(true);
    const res = await fetch("/crm-wly/api/whatsapp/enviar_arquivo.php", {
      method: "POST",
      body: formData,
      credentials: "include",
    });
    const data = await res.json();

    if (data && data.success) {
      // mensagem local "saida" do tipo contato
      const novo = {
        tipo: "saida",
        mensagem: data.mensagem || "[Contato enviado]",
        tipo_midia: "contato",
        contato_nome: nome || "",
        contato_telefones: JSON.stringify((telefones || []).map(s => s.replace(/\D+/g, ""))),
        contato_vcard: vcardText || null,
        id_mensagem_zapi: data.id_mensagem_zapi || null,
        status_envio: data.status_envio || "sucesso",
        telefone,
        data_envio: new Date().toISOString(),
        is_group: /^\d+-\d+$/.test(String(telefone || "")) ? 1 : 0,
      };
      setMensagens(prev => dedupeMensagens([...(prev || []), novo]));
    }
  } catch (err) {
    console.error("Erro ao enviar contato:", err);
  } finally {
    setLoading(false);
  }
}

const handleLoadOlderClick = async () => {
  if (fetchingOlder || !hasMoreOld) return;
  try {
    setFetchingOlder(true);
    // NÃO descer após o prepend
    suppressNextAutoScrollRef.current = true;
    stickToBottomRef.current = false;
    autoScrollLockRef.current = true;
    pauseSinceUntilRef.current = Date.now() + 1500;
    await carregarMensagens(false, { prependOlder: true, limit: PAGE_SIZE });
  } finally {
    setFetchingOlder(false);
  }
};


// (opcional) key de conversa baseada no tel (igual ao backend)
const convKey = (t) => {
  const v = (t || '').trim();
  if (!v) return '';
  if (/^\d+-\d+(@g\.us)?$/i.test(v)) return v.replace(/@g\.us$/i, '');
  const d = v.replace(/\D+/g, '');
  if (d.startsWith('55') && d.length >= 12) {
    const ddd = d.slice(2, 4);
    const last8 = d.slice(-8);
    return `55${ddd}${last8}`;
  }
  return v;
};


// chama o PHP, e atualiza a UI para "Mensagem apagada"
const deleteMessage = async (m) => {
  try {
    const fd = new FormData();
    fd.append('id_mensagem_zapi', m.id_mensagem_zapi);
    fd.append('mensagem_id', m.id);

    const res = await fetch('/crm-wly/api/whatsapp/deletar_mensagem.php', {
      method: 'POST',
      credentials: 'include',
      body: fd
    });
    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data.success) {
      throw new Error(data.error || 'Não foi possível apagar.');
    }

    // feedback imediato no estado local
setMensagens(prev =>
  prev.map(x =>
    (x.id_mensagem_zapi === m.id_mensagem_zapi || x.id === m.id)
      ? { ...x, mensagem: 'Mensagem apagada', tipo_midia: null, midia_url: null }
      : x
  )
);
  } catch (e) {
    console.error(e);
    alert(e.message || 'Erro ao apagar mensagem.');
  }
};

  // >>> NOVO: menu da lista e filtro "Não lidos"
const [menuAberto, setMenuAberto] = useState(false);
const [mostrarSomenteNaoLidos, setMostrarSomenteNaoLidos] = useState(false);

const carregarAcoes = async () => {
  if (!leadId) return;
  try {
    setCarregandoAcoes(true);
    const res = await fetch(`/crm-wly/criar_acao.php?acao=listar_acoes&lead_id=${leadId}`, { credentials: "include" });
    const data = await res.json();
    if (data.success) setAcoes(data.acoes || []);
    else setAcoes([]);
  } catch (e) {
    console.error("Erro ao listar ações:", e);
    setAcoes([]);
  } finally {
    setCarregandoAcoes(false);
  }
};

// Adapter para o Composer: retorna um array [{id?, titulo?, conteudo}, ...]
const fetchQuickReplies = async (q = "") => {
  try {
    const url = q
      ? `/crm-wly/api/whatsapp/respostas_rapidas.php?q=${encodeURIComponent(q)}`
      : `/crm-wly/api/whatsapp/respostas_rapidas.php`;

    const res = await fetch(url, { credentials: "include" });
    const data = await res.json();

    if (data.success && Array.isArray(data.data)) return data.data;
    return [];
  } catch {
    return [];
  }
};

const fetchQuickRepliesCb = useCallback(
  (q) => fetchQuickReplies(q),
  [] // estável, sem dependências
);

const fetchMentionParticipants = useCallback(async (q = "") => {
  if (!telefone || !isGroup) return [];

  try {
    const url = `/crm-wly/api/whatsapp/grupo_participantes.php?telefone=${encodeURIComponent(telefone)}&q=${encodeURIComponent(q)}`;
    const res = await fetch(url, { credentials: "include", cache: "no-store" });
    const data = await res.json();

    if (data?.success && Array.isArray(data.participants)) {
      return data.participants;
    }

    return [];
  } catch (err) {
    console.error("Erro ao buscar participantes do grupo:", err);
    return [];
  }
}, [telefone, isGroup]);

const enviarRespostaRapida = async (rr) => {
  if (!rr || !telefone) return;

  const texto = String(rr.conteudo || "").trim();
  const arquivoUrl = String(rr.arquivo_url || "").trim();
  const arquivoNome = String(rr.arquivo_nome || "arquivo").trim();

  try {
    // 1) envia primeiro o texto
    if (texto) {
      await enviarMensagem(texto);
    }

    // 2) depois envia o arquivo
    if (arquivoUrl) {
      setUploading(true);
      setLoading(true);

      const fileRes = await fetch(arquivoUrl, {
        credentials: "include",
        cache: "no-store",
      });

      if (!fileRes.ok) {
        throw new Error("Não foi possível carregar o arquivo da resposta rápida.");
      }

      const blob = await fileRes.blob();
      const file = new File([blob], arquivoNome || "arquivo", {
        type: blob.type || rr.arquivo_tipo || "application/octet-stream",
      });

      await handleSendFile(file);
    }

    requestAnimationFrame(() => composerRef.current?.focus?.());
  } catch (err) {
    console.error("Erro ao enviar resposta rápida com arquivo:", err);
    alert(err.message || "Erro ao enviar resposta rápida.");
  } finally {
    setUploading(false);
    setLoading(false);
  }
};

const formatPtBr = (yyyyMmDd) => {
  if (!yyyyMmDd) return "";
  // evita timezone bug do new Date(YYYY-MM-DD)
  const [y, m, d] = String(yyyyMmDd).split("-");
  if (!y || !m || !d) return yyyyMmDd;
  return `${d.padStart(2,"0")}/${m.padStart(2,"0")}/${y}`;
};


const abrirModalAcoes = async () => {
  setMostrarModalAcoes(true);
  await carregarAcoes();
};
const fecharModalAcoes = () => {
  setMostrarModalAcoes(false);
  setEditandoId(null);
  setEdicaoBuffer({ descricao: "", data_acao: "", status: "Pendente" });
};

function leadPossuiTag(lead, tagId) {
  if (!lead || !tagId) return false;
  // tenta vários formatos comuns: [id, ...] ou [{id}, ...]
  const arr = lead.tags || lead.tags_ids || lead.tagsIds || [];
  if (!Array.isArray(arr)) return false;
  return arr.some(t => Number(t?.id ?? t) === Number(tagId));
}


const iniciarEdicao = (acao) => {
  setEditandoId(acao.id);
  setEdicaoBuffer({
    descricao: acao.descricao || "",
    data_acao: acao.data_acao || "",
    status: acao.status || "Pendente",
  });
};

const carregarTagsLead = async (id) => {
  const reqId = ++tagsReqRef.current;   // gera um id único pra cada chamada
  setTagsLoading(true);                 // ativa o estado de carregando

  if (!id) {
    setTagsLead([]);
    setTagsLoading(false);
    return;
  }

  try {
    const res = await fetch(`/crm-wly/lead_tags.php?lead_id=${id}`, { credentials: "include" });
    const data = await res.json();

    // se outra chamada mais nova já foi feita, ignora esta resposta
    if (reqId !== tagsReqRef.current) return;

    setTagsLead(
      Array.isArray(data)
        ? data.map(d => ({
            id: Number(d.id),
            nome: d.nome,
            cor: d.cor || "#666666"
          }))
        : []
    );
  } catch {
    if (reqId !== tagsReqRef.current) return;
    setTagsLead([]);
  } finally {
    if (reqId === tagsReqRef.current) {
      setTagsLoading(false);            // desativa o loading só da chamada atual
    }
  }
};

const salvarOrcamentoLead = async () => {
  if (!leadId) return;

  const valorOtimista = Number(
  String(valorOrcamentoEditando || "0")
    .replace("R$", "")
    .replace(/\./g, "")
    .replace(",", ".")
    .trim()
) || 0;

setEditandoOrcamento(false);
bloqueiaPollingRef.current = true;

setLeads((prev) =>
  prev.map((l) =>
    Number(l.id) === Number(leadId)
      ? { ...l, smartview_valor_orcamento: valorOtimista }
      : l
  )
);

setUltimasConversas((prev) =>
  prev.map((c) =>
    Number(c.lead_id_resolvido ?? c.lead_id ?? 0) === Number(leadId)
      ? { ...c, smartview_valor_orcamento: valorOtimista }
      : c
  )
);

setSelectedChat((prev) =>
  prev
    ? { ...prev, smartview_valor_orcamento: valorOtimista }
    : prev
);

  try {
    setSalvandoOrcamento(true);

    const res = await fetch("/crm-wly/api/whatsapp/atualizar_orcamento_smartview.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        lead_id: leadId,
        valor_orcamento: valorOrcamentoEditando,
      }),
    });

    const data = await res.json();

    if (!data.success) {
      throw new Error(data.error || "Erro ao atualizar orçamento.");
    }

    setLeads((prev) =>
      prev.map((l) =>
        Number(l.id) === Number(leadId)
          ? { ...l, smartview_valor_orcamento: data.valor_orcamento }
          : l
      )
    );

    setUltimasConversas((prev) =>
      prev.map((c) =>
        Number(c.lead_id_resolvido ?? c.lead_id ?? 0) === Number(leadId)
          ? { ...c, smartview_valor_orcamento: data.valor_orcamento }
          : c
      )
    );

    setSelectedChat((prev) =>
  prev
    ? { ...prev, smartview_valor_orcamento: data.valor_orcamento }
    : prev
);

    setEditandoOrcamento(false);
  } catch (err) {
    console.error(err);
    alert(err.message || "Erro ao atualizar orçamento.");
  } finally {
    setSalvandoOrcamento(false);

      setTimeout(() => {
        bloqueiaPollingRef.current = false;
        recarregarConversas(false, true);
      }, 500);
  }
};

const salvarEdicao = async (id) => {
  try {
    const res = await fetch("/crm-wly/criar_acao.php", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ acao: "atualizar", id, ...edicaoBuffer }),
    });
    const data = await res.json();
    if (data.success) {
      await carregarAcoes();
      setEditandoId(null);
    } else {
      alert(data.error || "Não foi possível atualizar.");
    }
  } catch (e) {
    console.error(e);
    alert("Erro ao atualizar ação.");
  }
};

const toggleStatus = async (acao) => {
  if (!acao?.id) return;
  if (togglingId) return; // evita duplo clique

  const novo = acao.status === "Concluída" ? "Pendente" : "Concluída";

  try {
    setTogglingId(acao.id);

    // usa FormData porque seu PHP lê $_POST
    const fd = new FormData();
    fd.append("acao_id", String(acao.id));
    fd.append("novo_status", novo);

    const res = await fetch("/crm-wly/listagem_acoes.php", {
      method: "POST",
      credentials: "include",
      body: fd,
    });

    const data = await res.json().catch(() => ({}));

    // o seu PHP retorna { "success": "..." } (string) em sucesso e http 500 em erro,
    // então, se não veio "error", consideramos ok.
    if (res.ok && !data.error) {
      // Atualiza só a linha no estado local (mais rápido que recarregar tudo)
      setAcoes((prev) =>
        prev.map((x) => (x.id === acao.id ? { ...x, status: novo } : x))
      );
    } else {
      alert(data.error || "Não foi possível atualizar o status.");
    }
  } catch (e) {
    console.error("Erro ao alternar status:", e);
    alert("Erro ao alternar status.");
  } finally {
    setTogglingId(null);
  }
};

const excluirAcao = async (id) => {
  if (!window.confirm("Tem certeza que deseja excluir esta ação?")) return;
  try {
    const res = await fetch("/crm-wly/criar_acao.php", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ acao: "excluir", id }),
    });
    const data = await res.json();
    if (data.success) {
      setAcoes((prev) => prev.filter((a) => a.id !== id));
    } else {
      alert(data.error || "Não foi possível excluir.");
    }
  } catch (e) {
    console.error(e);
    alert("Erro ao excluir ação.");
  }
};

const ordenarConversas = useCallback(() => {
  setUltimasConversas(prev => {
    const arr = Array.isArray(prev) ? [...prev] : [];
    return arr.sort((a, b) => {
      const ap = Number(a?.is_pinned || 0), bp = Number(b?.is_pinned || 0);
      if (ap !== bp) return bp - ap;                 // fixadas em cima
      const at = dateFromServer(a.data_envio || 0).getTime();
      const bt = dateFromServer(b.data_envio || 0).getTime();
      return bt - at;                                // resto por data
    });
  });
}, []);

async function recarregarConversasSemCache() {
  const url = `/crm-wly/api/whatsapp/conversas_resumo.php?no_cache=1&ts=${Date.now()}`;
  const r = await fetch(url, { credentials: "include" });
  const j = await r.json().catch(() => null);

  if (!r.ok || !j?.success) {
    throw new Error(j?.error || `Erro HTTP ${r.status}`);
  }

  // ✅ atualiza a LISTA (sem mexer em carregandoConversas)
  setUltimasConversas(j.conversas || []);

  // ✅ mantém ordenação
  ordenarConversas();
}

// Recarrega a coluna esquerda (conversas + unread_count)
const recarregarConversas = useCallback(
  async (withLoader = false, forceNoCache = false) => {
    // id incremental para evitar race condition
    const fetchId = ++conversasFetchIdRef.current;

    try {
//      if (withLoader || primeiroLoadConversasRef.current) {
 //       setCarregandoConversas(true);
 //     }

      // ===== CHAVE DA CONVERSA ATUAL (ativa) — calculada FORA do map =====
      // (evita closure velha + garante consistência)
      const currentKey = selectedChat ? getChatKey(selectedChat) : getChatKey(telefone || "");

      const qs = new URLSearchParams();

      if (Array.isArray(tagFilterIds) && tagFilterIds.length > 0) {
        qs.set("tag_ids", tagFilterIds.join(","));
        qs.set("tag_mode", "all");
        qs.set("days", "365");
      }

      qs.set("limit", smartviewAtiva ? "500" : "120");

      if (forceNoCache) qs.set("no_cache", "1");

      const url = `/crm-wly/api/whatsapp/conversas_resumo.php${
        qs.toString() ? "?" + qs.toString() : ""
      }`;

      const res = await fetch(url, {
        credentials: "include",
        cache: "no-store",
      });

      const data = await res.json().catch(() => ({}));
      const now = Date.now();

      

      // ===== normaliza lista do servidor já aplicando OVERRIDES de forma estável =====
const lista = (Array.isArray(data?.conversas) ? data.conversas : []).map((c) => {
  const k = getChatKey(c);

  // base do servidor
  let leitura = Number(c.leitura || 0);
  let unread = Number(c.unread_count || 0);

  // regra: se leitura=1 (não lida), garante pelo menos 1
  if (leitura === 1) unread = Math.max(unread, 1);

  // conversa aberta não mostra badge
  if (k && k === currentKey) {
    unread = 0;
    leitura = 0;
  }

  // ---- OVERRIDE temporário leitura/unread (LIDA / NÃO LIDA) ----
  const uo = unreadOverrideRef.current.get(k);
  if (uo && uo.until > now) {
    // ✅ NOVO: respeita ação manual do usuário
    // mode: "force_unread" => sempre mostra badge
    // mode: "force_read"   => sempre esconde badge
    if (uo.mode === "force_unread") {
      leitura = 1;
      unread = Math.max(1, Number(uo.unread_count ?? unread ?? 1));
    } else if (uo.mode === "force_read") {
      leitura = 0;
      unread = 0;
    } else {
      // compat com overrides antigos
      if (typeof uo.leitura === "number") leitura = uo.leitura;
      if (typeof uo.unread_count === "number") unread = uo.unread_count;
    }

    // coerência
    if (leitura === 0) unread = 0;
    if (leitura === 1) unread = Math.max(1, unread);

    // conversa aberta sempre zera
    if (k && k === currentKey) {
      unread = 0;
      leitura = 0;
    }
  } else if (uo) {
    unreadOverrideRef.current.delete(k);
  }

  
  // ---- REGRA: se a última mensagem foi SAÍDA, não mostrar badge automaticamente
  // (exceto se usuário marcou manualmente como "não lida")
  const tipoUlt = String(c.tipo || "").toLowerCase();
  const forcedUnread = uo && uo.until > now && uo.mode === "force_unread";
  if (tipoUlt === "saida" && !forcedUnread) {
   leitura = 0;
   unread = 0;
  }

// ---- OVERRIDE temporário PIN ----
  let isPinned = Number(c.is_pinned || 0);
  const po = pinnedOverrideRef.current.get(k);
  if (po && po.until > now) {
    isPinned = po.value ? 1 : 0;
  } else if (po) {
    pinnedOverrideRef.current.delete(k);
  }

  // ---- OVERRIDE temporário ARCHIVE ----
  let isArchived = Number(c.is_archived || 0);
  const ao = archivedOverrideRef.current.get(k);
  if (ao && ao.until > now) {
    isArchived = ao.value ? 1 : 0;
  } else if (ao) {
    archivedOverrideRef.current.delete(k);
  }

  const tagsArr = Array.isArray(c.tags)
    ? c.tags.map((t) => ({
        id: Number(t.id),
        nome: String(t.nome || "").trim(),
        cor: t.cor || "#666666",
      }))
    : [];


        return {
          ...c,
          _key: k, // cache interno pra não recalcular convStableKey 30x
          _selected: k && k === currentKey,
          unread_count: unread,
          leitura,
          is_pinned: isPinned,
          is_archived: isArchived,
          lead_id: Number(c.lead_id_resolvido ?? c.lead_id_msg ?? c.lead_id ?? 0) || null,
          lead_nome: c.lead_nome || null,
          tags: tagsArr,
        };
      });

      // ⚠️ Se já houve outra chamada mais recente, ignora este resultado
      if (fetchId !== conversasFetchIdRef.current) return;

      // ===== merge / dedup sem misturar chaves diferentes =====
      setUltimasConversas((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];

        // mapa do estado anterior por key estável
        const prevMap = new Map(prevArr.map((c) => [getChatKey(c), c]));

        // keys do servidor
        const serverKeys = new Set(lista.map((c) => c._key).filter(Boolean));

        // merge: mantém preview do prev se for “mais novo” (seu comportamento atual)
        const merged = lista.map((srv) => {
          const key = srv._key || getChatKey(srv);
          const prevConv = prevMap.get(key);

          if (!prevConv) {
            return {
              ...srv,
              _selected: key && key === currentKey,
            };
          }

          const prevTime = dateFromServer(prevConv.data_envio || 0).getTime();
          const srvTime = dateFromServer(srv.data_envio || 0).getTime();

          const base =
            prevTime > srvTime
              ? {
                  ...srv,
                  mensagem: prevConv.mensagem,
                  data_envio: prevConv.data_envio,
                }
              : srv;

          // IMPORTANTE: re-aplica “não mostrar badge” se é o chat atual
          // (seguro caso algum dado venha diferente do servidor)
          let unread = Number(base.unread_count || 0);
          if (key && key === currentKey) unread = 0;

          return {
            ...base,
            _selected: key && key === currentKey,
            unread_count: unread,
          };
        });

        // traz conversas antigas que não vieram do servidor (mantém seu comportamento)
        prevArr.forEach((c) => {
          const key = getChatKey(c);
          if (key && !serverKeys.has(key)) merged.push(c);
        });

        // dedup final por convStableKey: escolhe maior last_msg_id
        const dedup = new Map();
        for (const c of merged) {
          const k = c._key || getChatKey(c);
          if (!k) continue;

          const prevItem = dedup.get(k);
          const cId = Number(c?.last_msg_id || 0);
          const pId = Number(prevItem?.last_msg_id || 0);

          if (!prevItem || cId > pId) {
            dedup.set(k, { ...c, _key: k });
          }
        }

        return Array.from(dedup.values());
      });

      ordenarConversas();
    } catch (e) {
      console.error("Erro ao recarregar conversas:", e);
    } finally {
      if (withLoader || primeiroLoadConversasRef.current) {
// setCarregandoConversas(false);
// primeiroLoadConversasRef.current = false;
      }
    }
  },
  // ✅ IMPORTANTÍSSIMO: selectedChat precisa estar aqui (senão currentKey fica “velho” no polling)
  [tagFilterIds, telefone, selectedChat, ordenarConversas]
);


// =====================
// Pref: manter arquivadas mesmo com não lidas
// (coloque ESTE BLOCO logo abaixo do const recarregarConversas = useCallback(...))
// =====================
const [manterArquivadas, setManterArquivadas] = useState(null);
const [prefsLoading, setPrefsLoading] = useState(false);
const [prefsError, setPrefsError] = useState("");
const [prefsOpen, setPrefsOpen] = useState(false);

const carregarPrefsWhatsApp = useCallback(async () => {
  try {
    setPrefsError("");
    setPrefsLoading(true);

    const r = await fetch("/crm-wly/api/whatsapp/prefs.php", {
      method: "GET",
      credentials: "include",
      cache: "no-store",
    });

    const j = await r.json().catch(() => ({}));
    if (!r.ok || !j?.success) throw new Error(j?.error || `Erro HTTP ${r.status}`);

    // ✅ vem 0 ou 1 do backend (sem fallback que força 0)
    setManterArquivadas(Number(j?.manter_arquivadas));
  } catch (e) {
    setPrefsError(e?.message || "Falha ao carregar preferência.");
    // ✅ default do sistema = 1 (mantém arquivadas), sem piscar
    setManterArquivadas(1);
  } finally {
    setPrefsLoading(false);
  }
}, []);

const salvarManterArquivadas = useCallback(
  async (novoValor) => {
    const v = Number(novoValor) ? 1 : 0;

    // ✅ otimista (não pisca)
    setManterArquivadas(v);

    try {
      setPrefsError("");
      setPrefsLoading(true);

      const r = await fetch("/crm-wly/api/whatsapp/prefs.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ manter_arquivadas: v }),
      });

      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.success) throw new Error(j?.error || `Erro HTTP ${r.status}`);

      // ✅ confirma valor retornado (ou mantém o v)
      setManterArquivadas(Number(j?.manter_arquivadas ?? v));

      // recarrega lista (pq o backend pode desarquivar ao desligar)
      await recarregarConversas(false, true);
    } catch (e) {
      setPrefsError(e?.message || "Falha ao salvar preferência.");
      // (opcional) você pode reverter aqui se quiser
      // setManterArquivadas((prev) => prev);
    } finally {
      setPrefsLoading(false);
    }
  },
  [recarregarConversas]
);

// ✅ carrega quando entra no filtro de arquivadas
useEffect(() => {
  if (showArchivedOnly) {
    carregarPrefsWhatsApp();
  }
}, [showArchivedOnly, carregarPrefsWhatsApp]);

// ✅ carrega quando abre o modal (garante estado correto sempre)
useEffect(() => {
  if (prefsOpen && showArchivedOnly) {
    carregarPrefsWhatsApp();
  }
}, [prefsOpen, showArchivedOnly, carregarPrefsWhatsApp]);

function markLocalReadByKey(key) {
  unreadOverrideRef.current.set(key, {
    leitura: 0,
    unread_count: 0,
    until: Date.now() + 10000,
  });

  setUltimasConversas(prev =>
    (prev || []).map(c =>
      convStableKey(c) === key ? { ...c, leitura: 0, unread_count: 0 } : c
    )
  );
}

// Marca UMA conversa como lida no servidor + zera o badge localmente
const marcarComoLida = async (phone) => {
  if (!phone) return;

  // chave estável para evitar mismatch (telefone_open, lid, grupo etc.)
  const stableKey = convStableKey({ telefone: phone, telefone_open: phone });

  // ✅ feedback imediato + proteção anti-polling
  unreadOverrideRef.current.set(stableKey, {
    unread_count: 0,
    leitura: 0,
    until: Date.now() + 10000,
  });

  setUltimasConversas((prev) =>
    (prev || []).map((cItem) =>
      convStableKey(cItem) === stableKey
        ? { ...cItem, unread_count: 0, leitura: 0 }
        : cItem
    )
  );

  try {
    await fetch("/crm-wly/api/whatsapp/marcar_lida.php", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ telefone: phone }),
      cache: "no-store",
      keepalive: true,
    });

    // revalidar sem pressa (evita voltar o badge enquanto backend consolida)
    setTimeout(() => recarregarConversas(), 600);
  } catch (e) {
    console.error("Erro ao marcar como lida:", e);
    // se der erro, revalidar assim mesmo
    setTimeout(() => recarregarConversas(), 600);
  }
};

const flushConversaAtualComoLida = () => {
  const telAtual = String(telefoneChatAtualRef.current || telefone || "").trim();
  if (!telAtual) return;
  marcarComoLida(telAtual);
};


// Ação: marcar todas como lidas
const marcarTodasComoLidas = async () => {
  setIsMarkingAll(true);

  // feedback instantâneo
  setUltimasConversas(prev => prev.map(c => ({ ...c, unread_count: 0, leitura: 0 })));

  try {
    await fetch("/crm-wly/api/whatsapp/marcar_todos_lidos.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      keepalive: true,
      cache: "no-store"
    });
  } catch (_) {
    // ok, vamos revalidar de qualquer forma
  } finally {
    // dá um respiro pro upsert do backend e revalida
    setTimeout(async () => {
      await recarregarConversas();
      setIsMarkingAll(false);   // <-- só aqui desliga o spinner/label
      setMenuAberto(false);
    }, 1200);
  }
};

const marcarComoNaoLidaOuLida = async (conv, action) => {
  const tel = conv?.telefone;
  if (!tel) return;

  const key = convStableKey(conv);

  const atual = ultimasConversas.find((c) => convStableKey(c) === key);
  const unreadAtualConv = Number(atual?.unread_count || 0);

  try {
    if (action === "read") {
      await fetch("/crm-wly/api/whatsapp/marcar_lida.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ telefone: tel }),
      });

      unreadOverrideRef.current.set(key, {
        mode: "force_read",
        unread_count: 0,
        leitura: 0,
        until: Date.now() + 15000,
      });

      setUltimasConversas((prev) =>
        prev.map((c) =>
          convStableKey(c) === key ? { ...c, unread_count: 0, leitura: 0 } : c
        )
      );
    } else {
      await fetch("/crm-wly/api/whatsapp/marcar_nao_lida.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ telefone: tel }),
      });

      const novoUnread = Math.max(1, unreadAtualConv);

      unreadOverrideRef.current.set(key, {
        mode: "force_unread",
        unread_count: novoUnread,
        leitura: 1,
        until: Date.now() + 15000,
      });

      setUltimasConversas((prev) =>
        prev.map((c) =>
          convStableKey(c) === key
            ? { ...c, leitura: 1, unread_count: novoUnread }
            : c
        )
      );
    }

    setTimeout(() => recarregarConversas(false, true), 250);
  } catch (e) {
    console.error("Erro ao alternar leitura:", e);
  } finally {
    setRowMenuOpenKey(null);
  }
};


 const handleStartChat = async (rawPhone) => {
  const phone = normalizeBRStart(rawPhone);
  if (!phone) {
    alert('Telefone inválido. Use DDD + número.');
    return;
  }
  setStartLoading(true);
  try {
    // zera o contexto atual e abre o chat desse número
    setStartOpen(false);
    setStartPhone('');

    // ajuste estes setters para os que você já usa:
    setLeadId(null);
    setIsGroup(false);
    setTagsLead([]); 
    setTelefone(phone);

    telefoneChatAtualRef.current = phone;
    setSelectedChat({ telefone: phone }); // evita o guard do carregarMensagens usar o chat antigo


    setChatName(phone);     // fallback até termos lead
    setMensagens([]);       // limpa o histórico na UI

    // (opcional) injeta um item efêmero na lista de conversas
    setEphemeralConv({
      telefone: phone,
      mensagem: '',
      data_envio: new Date().toISOString(),
      is_group: 0,
      unread_count: 0,
      tags: [],
      photo: null,
      _ephemeral: true,
    });
  
// carrega mensagens (virá [] até existir algo no BD)
// pega unread da conversa correspondente (se já existir na lista)
const conversa = ultimasConversas.find(
  (c) => getChatKey(c) === getChatKey(phone)
);
const unread = Number(conversa?.unread_count || 0);

await carregarMensagens(true, { limit: PAGE_SIZE, 
  openMode: unread > 0 ? 'anchor-unread' : 'bottom',
  unreadCount: unread,
});

  } finally {
    setStartLoading(false);
  }
};

const onStartKeyDown = (e) => {
  if (e.key === 'Enter') handleStartChat();
  if (e.key === 'Escape') setStartOpen(false);
};


function conversaPossuiTag(conv, tagId) {
  if (!tagId) return true;

  // 1) backend novo: array de objetos {id, nome, cor}
  if (Array.isArray(conv?.tags) && conv.tags.length) {
    return conv.tags.some(t => Number(t?.id) === Number(tagId));
  }

  // 2) compat: string "1|2|3"
  const ids = String(conv?.tags_ids || '')
    .split('|')
    .map(n => parseInt(n, 10))
    .filter(Boolean);
  if (ids.length) return ids.includes(Number(tagId));

  // 3) sem fallback em leads para evitar varrer lista grande no filtro
  return false;
}

const startEditMessage = useCallback((msg) => {
  if (!msg?.id_mensagem_zapi) return;
  if (msg?.tipo !== "saida") {
    alert("Só é possível editar mensagens enviadas por você.");
    return;
  }
  if (msg?.tipo_midia && msg.tipo_midia !== "texto") {
    alert("Só é possível editar mensagens de texto.");
    return;
  }

  setEditDraft({
    id: msg.id_mensagem_zapi,
    dbId: msg.id || null,
    original: msg.mensagem || "",
  });

  setMsgMenuOpenFor(null);

  requestAnimationFrame(() => {
    composerRef.current?.setValue?.(msg.mensagem || "");
    composerRef.current?.focus?.();
  });
}, []);

const clearEditDraft = useCallback(() => {
  setEditDraft(null);
  requestAnimationFrame(() => {
    composerRef.current?.setValue?.("");
    composerRef.current?.focus?.();
  });
}, []);

const abrirReplyDaMensagem = useCallback((msg) => {
  if (!msg?.id_mensagem_zapi) return;

  setReplyDraft({
    id: msg.id_mensagem_zapi,
    preview: previewDaMensagem(msg),
  });

  requestAnimationFrame(() => composerRef.current?.focus?.());
}, [setReplyDraft]);

const clearReply = useCallback(() => {
  setReplyDraft(null);
  requestAnimationFrame(() => composerRef.current?.focus());
}, [setReplyDraft]);

// abaixo dos useStates, junto dos outros effects
React.useEffect(() => {
  if (!ephemeralConv) return;
  setUltimasConversas((prev) => {
    const exists = prev?.some(
      (c) => convKey(c.telefone) === convKey(ephemeralConv.telefone)
    );
    if (exists) return prev;
    return [ephemeralConv, ...(prev || [])];
  });
}, [ephemeralConv]);

useEffect(() => {
  if (!telefone) return;

  if (loading || uploading) return;

  requestAnimationFrame(() => {
    composerRef.current?.focus?.();
    setTimeout(() => composerRef.current?.focus?.(), 0);
  });
}, [telefone, loading, uploading]);

useEffect(() => {
  const onKeyDown = (e) => {
    if (e.key !== "Escape") return;

    // Se estiver respondendo mensagem, primeiro cancela a resposta
    if (replyDraft) {
      clearReply();
      return;
    }

    // Se algum menu/modal simples estiver aberto, fecha primeiro
    if (emojiMenuFor) {
      setEmojiMenuFor(null);
      return;
    }

    if (msgMenuOpenFor) {
      setMsgMenuOpenFor(null);
      return;
    }

    if (rowMenuOpenKey) {
      setRowMenuOpenKey(null);
      return;
    }

    if (leadSearchOpen) {
      setLeadSearchOpen(false);
      setLeadSearchQuery("");
      return;
    }

    if (startOpen) {
      setStartOpen(false);
      return;
    }

    // Se tiver conversa aberta, fecha igual ao WhatsApp Web
   if (telefone || selectedChat || leadId) {
      flushConversaAtualComoLida();

      setSelectedChat(null);
      setTelefone("");
      setLeadId(null);
      setIsGroup(false);
      setMensagens([]);
      setTagsLead([]);
      setChatName("");
      setLastId(0);
      setStarted(false);
      setReplyDraft(null);
      setEditDraft(null);
      setMsgMenuOpenFor(null);
      setEmojiMenuFor(null);
      setShowJumpBottom(false);

      telefoneChatAtualRef.current = "";
      autoScrollLockRef.current = false;
      forceSnapBottomRef.current = false;
      shouldSnapBottomRef.current = false;
    }
  };

  window.addEventListener("keydown", onKeyDown);
  return () => window.removeEventListener("keydown", onKeyDown);
}, [
  telefone,
  selectedChat,
  replyDraft,
  emojiMenuFor,
  msgMenuOpenFor,
  rowMenuOpenKey,
  leadSearchOpen,
  startOpen,
  clearReply,
]);

useEffect(() => {
  if (!emojiMenuFor) return;

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setEmojiMenuFor(null);
    }
  };

  const onClickOutside = (e) => {
    if (e.target.closest(".emoji-reaction-pop")) return;
    if (e.target.closest(".reply-actions")) return;
    if (e.target.closest(".msg-actions-dropdown")) return;
    setEmojiMenuFor(null);
  };

  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("click", onClickOutside);
  window.addEventListener("touchend", onClickOutside);

  return () => {
    window.removeEventListener("keydown", onKeyDown);
    window.removeEventListener("click", onClickOutside);
    window.removeEventListener("touchend", onClickOutside);
  };
}, [emojiMenuFor]);

useEffect(() => { carregarTagsLead(leadId); }, [leadId]);


useEffect(() => {
  if (!msgMenuOpenFor) return;

  const onClickOutside = (e) => {
    if (e.target.closest(".msg-actions-wrap")) return;
    if (e.target.closest(".msg-actions-dropdown")) return;
    setMsgMenuOpenFor(null);
  };

  window.addEventListener("click", onClickOutside);
  window.addEventListener("touchend", onClickOutside);

  return () => {
    window.removeEventListener("click", onClickOutside);
    window.removeEventListener("touchend", onClickOutside);
  };
}, [msgMenuOpenFor]);


const sincronizarLeituraConversas = useCallback(async () => {
  try {
    const res = await fetch("/crm-wly/api/whatsapp/unread_conversas.php", {
      credentials: "include",
      cache: "no-store",
    });

    const data = await res.json().catch(() => null);

    if (!res.ok || !data?.success || !Array.isArray(data.conversas)) {
      return;
    }

    const mapa = new Map();

    data.conversas.forEach((item) => {
      const telKey = String(item?.tel_key || "").trim();
      if (!telKey) return;

      mapa.set(getChatKey(telKey), Number(item?.unread_count || 0));
    });

    const currentKey = selectedChat
      ? getChatKey(selectedChat)
      : getChatKey(telefoneChatAtualRef.current || telefone || "");

    setUltimasConversas((prev) =>
      (Array.isArray(prev) ? prev : []).map((conv) => {
        const convKey = getChatKey(conv);

        let unread = null;

        const keys = [
          convKey,
          getChatKey(conv?.tel_key),
          getChatKey(conv?.telefone),
          getChatKey(conv?.telefone_open),
        ].filter(Boolean);

        for (const k of keys) {
          if (mapa.has(k)) {
            unread = mapa.get(k);
            break;
          }
        }

        if (convKey && currentKey && convKey === currentKey) {
          return {
            ...conv,
            unread_count: 0,
            leitura: 0,
          };
        }

        if (unread === null) unread = 0;

        return {
          ...conv,
          unread_count: unread,
          leitura: unread > 0 ? 1 : 0,
        };
      })
    );
  } catch (e) {
    console.warn("Erro ao sincronizar leitura das conversas:", e);
  }
}, [selectedChat, telefone]);

useEffect(() => {
  let stop = false;
  let timer = null;

  const loop = async () => {
    if (stop) return;

    await sincronizarLeituraConversas();

    timer = setTimeout(loop, 2000);
  };

  loop();

  return () => {
    stop = true;
    if (timer) clearTimeout(timer);
  };
}, [sincronizarLeituraConversas]);


useEffect(() => {
  const handlePasteImage = async (e) => {
    const file = e.detail;
    if (!file || !telefone) return;

    const formData = new FormData();
    formData.append("arquivo", file);
    formData.append("telefone", toBackendPhone(telefone));
    formData.append("lead_id", leadId);

    try {
      setUploading(true);
      const res = await fetch("/crm-wly/api/whatsapp/enviar_arquivo.php", {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      const data = await res.json();
      if (data.success) {
        const nova = {
          tipo: "saida",
          mensagem: data.mensagem,
          tipo_midia: data.tipo_midia || null,
          midia_url: data.midia_url || data.url || null,
          id_mensagem_zapi: data.id_mensagem_zapi || null,
          data_envio: new Date().toISOString(),
          status_envio: "sucesso",
        };
        setMensagens((prev) => [...(prev || []), nova]);
      }
    } catch (err) {
      console.error("Erro ao colar imagem:", err);
      alert("Erro ao enviar print colado.");
    } finally {
      setUploading(false);
    }
  };

  window.addEventListener("composer-paste-image", handlePasteImage);
  return () => window.removeEventListener("composer-paste-image", handlePasteImage);
}, [telefone, leadId]);


useEffect(() => {
  // troca de conversa: zera cursor, zera tags e liga o loading das tags
  setLastId(0);
  setTagsLead([]);
  setTagsLoading(true);
}, [telefone]);

useEffect(() => {
  if (!menuAberto) return;
  const onClick = (e) => {
    // Se clicou dentro do menu ou no botão, não fecha
    if (menuRef.current?.contains(e.target)) return;
    if (btnMenuRef.current?.contains(e.target)) return;
    setMenuAberto(false);
  };
  window.addEventListener("click", onClick);
  window.addEventListener("touchend", onClick);
  return () => {
    window.removeEventListener("click", onClick);
    window.removeEventListener("touchend", onClick);
  };
}, [menuAberto]);

const carregarAtendentes = useCallback(async () => {
  try {
    const res = await fetch("/crm-wly/api/whatsapp/atendentes.php", {
      credentials: "include",
      cache: "no-store",
    });

    const data = await res.json();

    if (data?.success && Array.isArray(data.data)) {
      setAtendentes(data.data);
    }
  } catch (e) {
    console.error("Erro ao carregar atendentes:", e);
  }
}, []);

useEffect(() => {
  carregarAtendentes();
}, [carregarAtendentes]);

const carregouAtendenteSalvoRef = useRef(false);

useEffect(() => {
  if (!atendentes.length) return;

  const idSalvo = localStorage.getItem("norea_atendente_selecionado_id");

  if (idSalvo) {
    const encontrado = atendentes.find(
      (a) => Number(a.id) === Number(idSalvo)
    );

    if (encontrado) {
      setAtendenteSelecionado(encontrado);
    }
  }

  carregouAtendenteSalvoRef.current = true;
}, [atendentes]);

useEffect(() => {
  if (!carregouAtendenteSalvoRef.current) return;

  if (atendenteSelecionado?.id) {
    localStorage.setItem(
      "norea_atendente_selecionado_id",
      String(atendenteSelecionado.id)
    );
  } else {
    localStorage.removeItem("norea_atendente_selecionado_id");
  }
}, [atendenteSelecionado]);

const excluirAtendente = async (atendente) => {
  if (!atendente?.id) return;

  if (!window.confirm(`Excluir o atendente "${atendente.nome}"?`)) return;

  try {
    const res = await fetch("/crm-wly/api/whatsapp/atendentes.php", {
      method: "DELETE",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: atendente.id }),
    });

    const data = await res.json();

    if (!data?.success) {
      throw new Error(data?.error || "Erro ao excluir atendente.");
    }

    if (Number(atendenteSelecionado?.id) === Number(atendente.id)) {
      setAtendenteSelecionado(null);
      localStorage.removeItem("norea_atendente_selecionado_id");
    }

    await carregarAtendentes();
  } catch (e) {
    console.error(e);
    alert(e.message || "Erro ao excluir atendente.");
  }
};

const salvarNovoAtendente = async () => {
  const nome = novoAtendenteNome.trim();

  if (!nome) {
    alert("Informe o nome do atendente.");
    return;
  }

  try {
    setSalvandoAtendente(true);

    const res = await fetch("/crm-wly/api/whatsapp/atendentes.php", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ nome }),
    });

    const data = await res.json();

    if (!data?.success) {
      throw new Error(data?.error || "Erro ao cadastrar atendente.");
    }

    await carregarAtendentes();

    setNovoAtendenteNome("");
    setModalNovoAtendenteOpen(false);
    setModalAtendenteOpen(true);
  } catch (e) {
    console.error(e);
    alert(e.message || "Erro ao cadastrar atendente.");
  } finally {
    setSalvandoAtendente(false);
  }
};

useEffect(() => {
  function handleClickOutside(e) {
    if (tagDropdownRef.current && !tagDropdownRef.current.contains(e.target)) {
      setTagFilterOpen(false);
    }
  }
  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);

// useEffect(() => {
 // const jaViu = localStorage.getItem("pm_nova_funcao_editar_v1");
 // if (!jaViu) {
 //   setMostrarModalNovaFuncaoEditar(true);
 // }
// }, []);

useEffect(() => {
  const telAtual = String(selectedChat?.telefone || telefone || "").trim();

  const group =
    Number(selectedChat?.is_group) === 1 ||
    (
      !isLidLike(telAtual) &&
      (
        /@g\.us$/i.test(telAtual) ||
        /-group$/i.test(telAtual) ||
        /^\d+-\d+$/.test(telAtual) ||
        /^\d{18,30}$/.test(telAtual)
      )
    );

  setIsGroup(group);

  if (group) {
    setLeadId(null);
    setTagsLead([]);
    setTagsLoading(false);
  }
}, [telefone, selectedChat]);


useEffect(() => {
  if (!gravando) {
    if (gravacaoIntervalRef.current) {
      clearInterval(gravacaoIntervalRef.current);
      gravacaoIntervalRef.current = null;
    }
    return;
  }

  gravacaoIntervalRef.current = setInterval(() => {
    setGravacaoSegundos((prev) => prev + 1);
  }, 1000);

  return () => {
    if (gravacaoIntervalRef.current) {
      clearInterval(gravacaoIntervalRef.current);
      gravacaoIntervalRef.current = null;
    }
  };
}, [gravando]);

useEffect(() => {
  let ativo = true;
  let timer = null;

  const verificarStatusWhatsapp = async () => {
    try {
      setCarregandoStatusWhatsapp(true);

      const res = await fetch("/crm-wly/api/whatsapp/status_instancia.php", {
        credentials: "include",
        cache: "no-store",
      });

      const data = await res.json().catch(() => null);

      if (!ativo) return;

      if (res.ok && data?.success) {
        setWhatsappStatus(data);
      } else {
        setWhatsappStatus({
          whatsapp_ativo: 1,
          connected: false,
          smartphoneConnected: false,
          error: data?.error || "status_indisponivel",
        });
      }
    } catch (err) {
      if (!ativo) return;

      setWhatsappStatus({
        whatsapp_ativo: 1,
        connected: false,
        smartphoneConnected: false,
        error: "erro_rede",
      });
    } finally {
      if (ativo) {
        setCarregandoStatusWhatsapp(false);
      }
    }
  };

  verificarStatusWhatsapp();

  timer = setInterval(() => {
    if (!document.hidden) {
      verificarStatusWhatsapp();
    }
  }, 30000);

  return () => {
    ativo = false;
    if (timer) clearInterval(timer);
  };
}, []);

useEffect(() => {
  const jaViu = localStorage.getItem("norea_tooltip_atendente_v1");

  if (!jaViu) {
    setShowTooltipAtendente(true);
  }
}, []);

useEffect(() => {
  if (!telefone) return;
  if (loading || uploading) return;

  requestAnimationFrame(() => {
    composerRef.current?.focus?.();
    setTimeout(() => composerRef.current?.focus?.(), 0);
  });
}, [telefone, loading, uploading]);

useLayoutEffect(() => {
  const el = chatRef.current;
  if (!el) return;

  // 1) Ao abrir conversa: cola SEM checar atBottom
  if (forceSnapBottomRef.current) {
    el.scrollTop = el.scrollHeight;
    forceSnapBottomRef.current = false;
    shouldSnapBottomRef.current = false;
    return;
  }

  // 2) Em updates (polling): só cola se estiver permitido + usuário no fim
  if (!shouldSnapBottomRef.current) return;
  if (autoScrollLockRef.current) { shouldSnapBottomRef.current = false; return; }

  const atBottom = (el.scrollHeight - el.scrollTop - el.clientHeight) <= 80; // 80 é melhor que 2
  if (!atBottom) { shouldSnapBottomRef.current = false; return; }

  el.scrollTop = el.scrollHeight;
  shouldSnapBottomRef.current = false;
}, [mensagens]);



function previewDaMensagem(msg) {
  if (!msg) return "";
  if (msg.tipo_midia === "imagem")   return "🖼️ imagem";
  if (msg.tipo_midia === "audio")    return "🎤 áudio";
  if (msg.tipo_midia === "video")    return "🎞️ vídeo";
  if (msg.tipo_midia === "pdf")      return "📄 PDF";
  if (msg.tipo_midia === "documento")return "📎 documento";
  if (msg.tipo_midia === "sticker")  return "🌟 figurinha";
  const t = (msg.mensagem || "").trim();
  return t.length > 80 ? t.slice(0, 80) + "…" : t;
}

  const toBackendPhone = (t) => {
    const v = String(t || "").trim();
    if (!v) return "";

    // conversa aberta por LID vem como "lid:123..."
    if (v.startsWith("lid:")) {
      const digits = v.slice(4).replace(/\D+/g, "");
      return digits ? `${digits}@lid` : v;
    }

    return v;
  };

function truthy(v) {
  if (v === true) return true;
  if (v === 1 || v === "1") return true;
  if (typeof v === "string" && v.toLowerCase() === "true") return true;
  return false;
}

function normalizeMsg(m) {
  const tipoVal = m?.tipo;

  // tipo pode vir: "saida", "entrada", "1", 1, "saída" (com acento)...
  const tipoRaw = String(tipoVal ?? "").trim().toLowerCase();
  const dirRaw  = String(m?.direction ?? m?.direcao ?? "").trim().toLowerCase();

  const tipoNum = Number(tipoVal);

  const fromMe =
    // string
    tipoRaw === "saida" ||
    tipoRaw === "saída" ||
    tipoRaw === "out" ||
    tipoRaw === "sent" ||
    tipoRaw === "outgoing" ||

    // numérico (muito comum em backend)
    tipoRaw === "1" ||
    tipoNum === 1 ||

    // direction/direcao
    dirRaw === "outgoing" ||
    dirRaw === "sent" ||
    dirRaw === "out" ||

    // flags
    truthy(m?.fromMe) ||
    truthy(m?.from_me) ||
    truthy(m?.fromme);

  return { ...m, tipo: fromMe ? "saida" : "entrada" };
}




function msgTs(m) {
  // preferir momment (ms) se existir
  if (m?.momment) return Number(m.momment);
  // depois datas string
  const s = m?.data_envio || m?.data_hora || m?.created_at || m?.criado_em;
  const t = s ? Date.parse(s) : NaN;
  return Number.isFinite(t) ? t : 0;
}

function sortMsgsAsc(arr) {
  return [...arr].sort((a, b) => {
    const ta = msgTs(a);
    const tb = msgTs(b);
    if (ta !== tb) return ta - tb;
    // desempate estável
    const ia = Number(a?.id || 0);
    const ib = Number(b?.id || 0);
    return ia - ib;
  });
}

function normalizarTelefoneComparacao(tel = "") {
  return String(tel || "").replace(/\D/g, "").slice(-9);
}

  function convKeyJS(t) {
  const v = String(t || "").trim();

  // grupos
  if (/^\d+-\d+$/.test(v)) return v; // usa numero-numero como chave
  if (/@g\.us$/i.test(v) || /-group$/i.test(v)) {
    const base = v.replace(/@g\.us$/i, "").replace(/-group$/i, "");
    return /^\d+-\d+$/.test(base) ? base : base;
  }

  // 1:1 BR: 55 + DDD + últimos 8
  const d = v.replace(/\D+/g, "");
  if (d.startsWith("55") && d.length >= 12) {
    const ddd = d.slice(2, 4);
    const last8 = d.slice(-8);
    return "55" + ddd + last8;
  }
  return v;
}
// Chave estável do chat (funciona para telefone simples, telefone_open, grupo, lid etc.)
function getChatKey(convOrPhone) {
  if (!convOrPhone) return "";

  // se vier string (telefone)
  if (typeof convOrPhone === "string") {
    const phone = String(convOrPhone || "").trim();
    if (!phone) return "";
    return convStableKey({ telefone: phone, telefone_open: phone });
  }

  // se vier objeto conversa
  return convStableKey(convOrPhone);
}


function convStableKey(c) {
  if (!c) return "";

  const tel = String(c?.telefone || c?.telefone_open || "").trim();
  const tk = String(c?.tel_key || "").trim();

  const baseGrupo = tk || tel;

  const isGrupo =
    Number(c?.is_group) === 1 ||
    (
      !isLidLike(baseGrupo) &&
      (
        /@g\.us$/i.test(baseGrupo) ||
        /-group$/i.test(baseGrupo) ||
        /^\d+-\d+$/.test(baseGrupo) ||
        /^\d{18,30}$/.test(baseGrupo)
      )
    );

  // GRUPO TEM PRIORIDADE SOBRE lead_id
  if (isGrupo) {
    return `group:${convKeyJS(baseGrupo)}`;
  }

  const leadId = Number(c?.lead_id_resolvido ?? c?.lead_id ?? c?.lead_id_msg ?? 0);
  if (leadId > 0) return `lead:${leadId}`;

  if (tk.startsWith("lid:")) return tk;

  if (/@lid$/i.test(tel)) {
    const digits = tel.replace(/\D+/g, "");
    return digits ? `lid:${digits}` : tel;
  }

  return convKeyJS(tel || tk);
}

const onEmojiClick = (emojiObject) => {
  if (composerRef.current) {
    const atual = composerRef.current.getValue();
    composerRef.current.setValue(atual + emojiObject.emoji);
    composerRef.current.focus();
  }
};


  
  const leadSelecionado = leads.find((l) => l.id === leadId);


const carregarTagsDisponiveis = useCallback(async (force = false) => {
  if (!force && tagsDisponiveis.length > 0) return;

  try {
    const res = await fetch(`/crm-wly/tags.php?resumo=1&ts=${Date.now()}`, {
      credentials: "include",
      cache: "no-store",
      headers: {
        Accept: "application/json",
      },
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || data?.error) {
      throw new Error(data?.error || `Erro HTTP ${res.status}`);
    }

    const lista = Array.isArray(data?.resumo)
      ? data.resumo
      : Array.isArray(data?.tags)
        ? data.tags
        : Array.isArray(data)
          ? data
          : [];

    setTagsDisponiveis(
      lista.map((t) => ({
        id: Number(t.id),
        nome: t.nome || t.nome_tag || t.label || `Tag ${t.id}`,
        cor: t.cor || "#666666",
      }))
    );
  } catch (err) {
    console.error("Erro ao buscar tags disponíveis:", err);
    // Não limpa se já tinha tags carregadas
    setTagsDisponiveis((prev) => prev || []);
  }
}, [tagsDisponiveis.length]);

useEffect(() => {
  carregarTagsDisponiveis(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

const primeiroFiltroTagRef = useRef(true);

useEffect(() => {
  if (primeiroFiltroTagRef.current) {
    primeiroFiltroTagRef.current = false;
    return;
  }

  const t = setTimeout(() => {
    recarregarConversas(false, true);
  }, 150);

  return () => clearTimeout(t);
}, [tagFilterIds, recarregarConversas]);

useEffect(() => {
  if (!tagFilterOpen) return;

  if (tagsDisponiveis.length === 0) {
    carregarTagsDisponiveis(true);
  }
}, [tagFilterOpen, tagsDisponiveis.length, carregarTagsDisponiveis]);

useEffect(() => {
  // ao trocar de conversa, esconde por 5s
  setOlderLinkDelayOk(false);
  const t = setTimeout(() => setOlderLinkDelayOk(true), 3000);
  return () => clearTimeout(t);
}, [telefone]);


useEffect(() => {
  precisaScrollarRef.current = { tel: telefone, done: false };
}, [telefone, leadId]);

useEffect(() => {
  setEditandoNomeLead(false);
  setNomeLeadEditando("");
  setSalvandoNomeLead(false);
}, [telefone, leadId]);

useEffect(() => {
  fetch("/crm-wly/getUser.php", { credentials: "include" })
    .then(res => res.json())
    .then(user => {
      setIaPermitida(Number(user?.ia_ativa) === 1);
    })
    .catch(err => {
      console.error("Erro ao verificar permissão IA:", err);
      setIaPermitida(false);
    });
}, []);

useEffect(() => {
  return () => {
    document.title = tituloOriginalRef.current;
  };
}, []);

  // Performance: não carrega todos os leads ao iniciar.
  // A lista completa é buscada sob demanda em encaminhamento/envio de contato.
const query = useQuery();

useEffect(() => {
  const tel = query.get("telefone");
  const id = query.get("leadId");

  if (tel) 
    setTelefone(tel);
  if (id) setLeadId(parseInt(id));
}, []);

useEffect(() => {
if (!leadId) {
  setEstagioAtual("");
  setOrigemAtual("");
  setIaBloqueado(false);
  return;
}

  const lead = leads.find(l => Number(l.id) === Number(leadId));

  setEstagioAtual(
    selectedChat?.estagio_funil ||
    lead?.estagio_funil ||
    ""
  );

  const identList = [
    lead?.telefone,
    lead?.whatsapp_lid,
    telefone
  ].filter(Boolean);

  if (identList.length === 0) {
    setIaBloqueado(false);
    return;
  }

  fetch(`/crm-wly/api/openai/numeros_bloqueados.php?telefone=${identList.join(',')}`, {
    credentials: "include"
  })
    .then(res => res.json())
    .then(data => {
      setIaBloqueado(!!data?.bloqueado);
    })
    .catch(err => {
      console.error("Erro ao verificar bloqueio IA:", err);
      setIaBloqueado(false);
    });

}, [leadId, leads, telefone, selectedChat]);


// Rola para o fim do chat (âncora)
const scrollToBottom = (behavior = "auto") => {
  if (chatEndRef.current) {
    chatEndRef.current.scrollIntoView({ behavior, block: "end" });
  }
};

useEffect(() => {
  const el = chatAreaRef.current;
  if (!el) return;

  const onScroll = () => {
    const nearBottom = (el.scrollHeight - el.scrollTop - el.clientHeight) <= 0;

    // “grudado no fim”?
    stickToBottomRef.current = nearBottom;
    setShowJumpBottom(!nearBottom);

    // se NÃO está no fim, travamos o autoscroll
    autoScrollLockRef.current = !nearBottom;
  };

  el.addEventListener("scroll", onScroll);
  onScroll();
  return () => el.removeEventListener("scroll", onScroll);
}, [telefone]);


useEffect(() => {
  // se estamos carregando antigas, suprime o autoscroll deste ciclo
  if (suppressNextAutoScrollRef.current) {
    suppressNextAutoScrollRef.current = false;
    return;
  }

  const area = chatAreaRef.current;
  if (!area) return;

  // **checa ao vivo** se está realmente colado no fim
  const atBottomNow = (area.scrollHeight - area.scrollTop - area.clientHeight) <= 0;
   if (!atBottomNow || autoScrollLockRef.current) return;

  area.scrollTo({ top: area.scrollHeight, behavior: "auto" });
  chatEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
}, [mensagensVisiveis.length]);




// Carrega conversas uma vez ao abrir a página.
// Primeiro usa cache para abrir mais rápido; depois revalida sem cache em segundo plano.
useEffect(() => {
  let alive = true;
  let revalidateTimer = null;

  const iniciar = async () => {
    setCarregandoInicial(true);

    try {
      await recarregarConversas(false, false);
    } finally {
      if (alive) setCarregandoInicial(false);
    }

    revalidateTimer = setTimeout(() => {
      if (alive) recarregarConversas(false, true);
    }, 800);
  };

  iniciar();

  return () => {
    alive = false;
    if (revalidateTimer) clearTimeout(revalidateTimer);
  };
}, []);


useEffect(() => {
  if (!telefone) return;

  let stop = false;
  let interval = null;

   (async () => {
    const unread = unreadAtual;

    forceSnapBottomRef.current = true; // ✅ AQUI (abriu conversa -> vai pro final)

    await carregarMensagens(true, {
      openMode: unread > 0 ? "anchor-unread" : "bottom",
      unreadCount: unread,
    });
    if (stop) return;

   interval = setInterval(() => {
     if (document.visibilityState !== "visible") return;
     carregarMensagens(false, { sinceMode: true });
   }, REFRESH_MS);
  })();

  return () => {
    stop = true;
    if (interval) clearInterval(interval);
  };
}, [telefone]);

useEffect(() => {
  if (replyDraft) {
    requestAnimationFrame(() => composerRef.current?.focus());
  }
}, [replyDraft]);


async function toggleArchiveChat(phone) {
  const key = getChatKey(phone);
  const atual = ultimasConversas.find(c => getChatKey(c) === key);
  const isArchived = Number(atual?.is_archived || 0) === 1;
  const nextArchived = !isArchived;

  // ✅ override por 15s
  archivedOverrideRef.current.set(key, { value: nextArchived, until: Date.now() + 15000 });

  // ✅ update otimista
  setUltimasConversas(prev =>
    prev.map(c => getChatKey(c) === key
      ? { ...c, is_archived: nextArchived ? 1 : 0 }
      : c
    )
  );

  try {
const r = await fetch("/crm-wly/api/whatsapp/archive_chat.php", {
  method: "POST",
  credentials: "include",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    telefone: phone,
    action: nextArchived ? "archive" : "unarchive",
  }),
});

const j = await r.json().catch(() => null);

if (!r.ok || !j?.success) {
  throw new Error(j?.error || `Erro HTTP ${r.status}`);
}

  } catch (e) {
    console.error("Erro ao archive/unarchive:", e);

    // rollback
    archivedOverrideRef.current.set(key, { value: isArchived, until: Date.now() + 15000 });
    setUltimasConversas(prev =>
      prev.map(c => getChatKey(c) === key
        ? { ...c, is_archived: isArchived ? 1 : 0 }
        : c
      )
    );
  } finally {
  // revalida sem cache (pra não "voltar" por causa do TTL)
  setTimeout(() => recarregarConversasSemCache(), 400);
}

}



async function togglePinChat(phone) {
  const key = getChatKey(phone);
  const atual = ultimasConversas.find(c => getChatKey(c) === key);
  const isPinned = Number(atual?.is_pinned || 0) === 1;
  const nextPinned = !isPinned;

  // Segura a UI por 15s contra cache/polling
  pinnedOverrideRef.current.set(key, { value: nextPinned, until: Date.now() + 15000 });

  // 1) Update otimista (NUNCA ativa loader)
  setUltimasConversas(prev =>
    (prev || []).map(c =>
      getChatKey(c) === key ? { ...c, is_pinned: nextPinned ? 1 : 0 } : c
    )
  );
  ordenarConversas();

  try {
    // 2) Backend pin/unpin
    const fd = new FormData();
    fd.append("telefone", phone);
    fd.append("action", nextPinned ? "pin" : "unpin");
    fd.append("acao",   nextPinned ? "pin" : "unpin");

    const res = await fetch("/crm-wly/api/whatsapp/pin_chat.php", {
      method: "POST",
      credentials: "include",
      body: fd,
    });

    const ct = res.headers.get("content-type") || "";
    const data = ct.includes("application/json")
      ? await res.json().catch(() => ({}))
      : null;

    if (!res.ok || (data && data.success === false)) {
      const errTxt = !ct.includes("application/json")
        ? await res.text().catch(() => "")
        : (data?.error || "");
      throw new Error(errTxt || "Falha ao fixar/desafixar.");
    }

    // 3) Revalidação silenciosa (SEM setCarregandoConversas)
    setTimeout(async () => {
      try {
        const url = `/crm-wly/api/whatsapp/conversas_resumo.php?no_cache=1&ts=${Date.now()}`;
        const r2 = await fetch(url, { credentials: "include" });
        const j2 = await r2.json().catch(() => null);
        if (!r2.ok || !j2?.success) return;

        // Atualiza SOMENTE a lista (sem loader)
        setUltimasConversas(prev => {
          const lista = j2.conversas || [];
          return lista;
        });

        ordenarConversas();
      } catch (e) {
        // silencioso: não atrapalha UX
        console.warn("Revalidação silenciosa falhou:", e);
      }
    }, 250);

  } catch (e) {
    console.error(e);

    // Rollback do otimista (também sem loader)
    pinnedOverrideRef.current.set(key, { value: isPinned, until: Date.now() + 15000 });
    setUltimasConversas(prev =>
      (prev || []).map(c =>
        getChatKey(c) === key ? { ...c, is_pinned: isPinned ? 1 : 0 } : c
      )
    );
    ordenarConversas();

    alert(e.message || "Erro ao fixar/desafixar.");
  } finally {
    setRowMenuOpenKey(null);
  }
}


const toggleArchive = async (conv, action /* 'archive' | 'unarchive' */) => {
  try {
    const tel = conv?.telefone || '';

    const fd = new FormData();
    fd.append('telefone', tel);
    fd.append('action', action);
    fd.append('acao',   action); // compat

    const res = await fetch('/crm-wly/api/whatsapp/archive_chat.php', {
      method: 'POST',
      credentials: 'include',
      body: fd
    });

    let data = null;
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) data = await res.json().catch(() => ({}));

    if (!res.ok || (data && data.success === false)) {
      const errTxt = !ct.includes('application/json') ? await res.text().catch(()=>'') : (data?.error || '');
      throw new Error(errTxt || 'Falha ao arquivar/desarquivar.');
    }

    setUltimasConversas(prev =>
      prev.map(c =>
        getChatKey(c) === getChatKey(tel)
          ? { ...c, is_archived: action === 'archive' ? 1 : 0 }
          : c
      )
    );
  } catch (e) {
    console.error(e);
    alert(e.message || 'Erro ao arquivar/desarquivar.');
  } finally {
    setRowMenuOpenKey(null);
  }
};



  const iniciarGravacao = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    // Chrome (macOS) muitas vezes só suporta webm; opus
    const MIME_OGG = "audio/ogg;codecs=opus";
    const MIME_WEBM = "audio/webm;codecs=opus";

    let mimeType = MIME_OGG;
    if (!MediaRecorder.isTypeSupported(MIME_OGG) && MediaRecorder.isTypeSupported(MIME_WEBM)) {
      mimeType = MIME_WEBM;
    }

    const recorder = new MediaRecorder(stream, { mimeType });

    audioChunksRef.current = [];
    gravacaoCanceladaRef.current = false;

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    recorder.onstop = async () => {
      if (gravacaoCanceladaRef.current) return;

      try {
        const usedMime = String(mimeType || recorder?.mimeType || "").toLowerCase();
        const isWebm = usedMime.includes("webm");

        const ext = isWebm ? "webm" : "ogg";
        const blobType = isWebm ? "audio/webm" : "audio/ogg";

        const blob = new Blob(audioChunksRef.current, { type: blobType });
        audioChunksRef.current = [];

        // evita mandar áudio “vazio”
        if (!blob || blob.size < 800) {
          throw new Error("Áudio muito curto ou vazio. Grave novamente.");
        }

        const file = new File([blob], `gravado_${Date.now()}.${ext}`, { type: blobType });

        const formData = new FormData();
        formData.append("arquivo", file);
        formData.append("telefone", toBackendPhone(telefone)); // ✅ importante
        formData.append("lead_id", String(leadId || 0));

        setUploading(true);

        const res = await fetch("/crm-wly/api/whatsapp/enviar_arquivo.php", {
          method: "POST",
          body: formData,
          credentials: "include",
        });

        const data = await res.json();

        if (!res.ok || data?.success === false) {
          throw new Error(data?.message || "Falha ao enviar áudio.");
        }
      } catch (err) {
        console.error(err);
        alert(err?.message || "Erro ao enviar o áudio.");
      } finally {
        setUploading(false);
      }
    };

    setGravacaoSegundos(0);

    recorder.start();
    setMediaRecorder(recorder);
    setGravando(true);
  } catch (error) {
    console.error("Erro ao iniciar gravação:", error);
    alert("Não foi possível acessar o microfone.");
  }
};



const pararGravacao = () => {
  setGravacaoSegundos(0);

  if (mediaRecorder) {
    mediaRecorder.stop();
    setGravando(false);

    // Encerra o stream para liberar o microfone
    const tracks = mediaRecorder.stream?.getTracks?.();
    if (tracks) {
      tracks.forEach(track => track.stop());
    }

    setMediaRecorder(null);
  }
};
  
const cancelarGravacao = () => {
  setGravacaoSegundos(0);
  if (mediaRecorder && mediaRecorder.state === "recording") {

    gravacaoCanceladaRef.current = true;

    mediaRecorder.stop();
  }

  const tracks = mediaRecorder?.stream?.getTracks?.();
  if (tracks) {
    tracks.forEach(track => track.stop());
  }

  // limpa
  audioChunksRef.current = [];
  setMediaRecorder(null);
  setGravando(false);
};

const enviarMensagemComAtendente = async (texto, meta = {}) => {
  let textoFinal = texto;

  if (atendenteSelecionado?.nome) {
    const nomeSeguro = String(atendenteSelecionado.nome).replace(/`/g, "");
    textoFinal = `\`${nomeSeguro}\`\n\n${texto}`;
  }

  return enviarMensagem(textoFinal, meta);
};

const failCountRef = useRef(0);  
// aceita um 2º parâmetro opcional: { scrollIfNoNew: boolean }
// aceita { openMode: 'bottom' | 'anchor-unread', unreadCount?: number }

const abrirConversa = (c) => {
  // guarda o chat inteiro (opcional, mas seguro)
  setSelectedChat(c);

  // lead
  setLeadId(c?.lead_id_resolvido || 0);

  // 🔥 PONTO CRÍTICO:
  // para grupo, c.telefone JÁ É "5512991888466-1489930077"
  // para 1:1, continua sendo telefone normal
  setTelefone(String(c?.telefone || ""));
};

const carregarMensagens = async (
  forceFull = false,
  {
    openMode = "bottom",
    unreadCount = 0,
    prependOlder = false,
    limit = PAGE_SIZE,
    sinceMode = false,
  } = {}
) => {
  const myReqId = ++msgsReqRef.current;

  // ✅ usa SEMPRE a chave correta do chat selecionado (grupo = jidKey com hífen)
  const telSnap = String(telefoneChatAtualRef.current || telefone || "");

  const leadSnap = leadId;

  try {
    // ignora esse ciclo: acabamos de fazer PREPEND e pausamos o since
    if (sinceMode && Date.now() < (pauseSinceUntilRef.current || 0)) return;

    const url = new URL("/crm-wly/api/whatsapp/get_mensagens.php", window.location.origin);
    url.searchParams.set("telefone", telSnap);

    const isGroupTel = /@g\.us$|-group$|\d+-\d+$/.test(String(telSnap || ""));
if (!isGroupTel && Number(leadSnap) > 0) {
  url.searchParams.set("lead_id", String(leadSnap));
}


    const areaNow = chatAreaRef.current;
    const atBottomNow = areaNow
      ? (areaNow.scrollHeight - areaNow.scrollTop - areaNow.clientHeight) <= 2
      : true;

    // só permite snap se o usuário estiver no fim (ou se for abertura forceFull)
    shouldSnapBottomRef.current = forceFull || (atBottomNow && !autoScrollLockRef.current);

    if (sinceMode) {
      // derive since_id do estado, caso lastId ainda não esteja pronto
      let sid = lastId || 0;

      if (!sid && Array.isArray(mensagens) && mensagens.length) {
        for (let i = 0; i < mensagens.length; i++) {
          const idNum = Number(mensagens[i]?.id || 0);
          if (idNum > sid) sid = idNum;
        }
      }

      if (sid > 0) {
        url.searchParams.set("since_id", String(sid));
      } else {
        url.searchParams.set("limit", String(limit));
      }
    } else {
      url.searchParams.set("limit", String(limit));

      if (prependOlder && Array.isArray(mensagens) && mensagens.length) {
let firstId = 0;

// pega o MENOR id numérico > 0 disponível (não depende da ordem do array)
for (const m of mensagens) {
  const idNum = Number(m?.id || 0);
  if (idNum > 0 && (firstId === 0 || idNum < firstId)) firstId = idNum;
}

if (firstId > 0) url.searchParams.set("before_id", String(firstId));

      }
    }

    const res = await fetch(url.toString(), { credentials: "include", cache: "no-store" });
    const data = await res.json();

    // chave única do chat ATUAL (grupo com hífen / lid / etc)
const chatKey =
  (typeof selectedChat === "object" && selectedChat?.telefone)
    ? String(selectedChat.telefone)
    : (telefone != null ? String(telefone) : "");

// se mudou de chat no meio do fetch, descarta
if (myReqId !== msgsReqRef.current || chatKey !== String(
  (typeof selectedChat === "object" && selectedChat?.telefone)
    ? selectedChat.telefone
    : (telefone != null ? telefone : "")
)) return;

const incoming = Array.isArray(data?.mensagens) ? data.mensagens : [];

// has_more_old
setHasMoreOld(Boolean(data?.has_more_old ?? (incoming.length >= limit)));

// 🔥 se chegou mensagem, garante que a UI pode renderizar
if (incoming.length > 0) setStarted(true);

// fluxo normal (não prependOlder)
if (!prependOlder) {
  setMensagens((prev) => {
    const prevArr = Array.isArray(prev) ? prev : [];

    // "incoming" = mensagens que vieram do backend nesse request
    // sinceMode => append incremental (senão substitui)
    const rawNext = sinceMode ? [...prevArr, ...incoming] : incoming;

    let ordered = rawNext;

    try {
      // 1) dedupe
      const deduped = dedupeMensagens(rawNext);

      // 2) normaliza (garante tipo = entrada/saida antes de renderizar)
      const normalized = deduped.map(normalizeMsg);

      // 3) ordena com desempate estável (timestamp + id)
      ordered = sortMsgsAsc(normalized);
    } catch (e) {
      console.error("dedupe/normalize/sort falhou:", e);
      ordered = rawNext;
    }

    // ✅ cache SEMPRE na MESMA chave do chat atual (estável)
    // use selectedChat se existir, senão o telefone atual
    const stableKey = selectedChat
      ? convStableKey(selectedChat)
      : convStableKey({ telefone: chatKey || telefone });

    msgsCacheRef.current.set(stableKey, ordered);

    return ordered;
  });

    return;
}




    // (1) anexar mensagens
    if (prependOlder) {
      // suprime qualquer autoscroll do ciclo
      suppressNextAutoScrollRef.current = true;

      // âncora: primeiro balão atualmente visível
      const area = chatAreaRef.current;
      const anchorEl = area?.querySelector(".mensagem");
      const anchorTop = anchorEl ? anchorEl.getBoundingClientRect().top : null;

      // PREPEND sem duplicar
      let inserted = 0;

      setMensagens((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const seen = new Set(prevArr.map((m) => m.id ?? m.id_mensagem_zapi));

        const novos = incoming.filter((m) => !(seen.has(m.id ?? m.id_mensagem_zapi)));
        inserted = novos.length;

        const next = inserted ? [...novos, ...prevArr] : prevArr;

        const deduped = dedupeMensagens(next);
        const normalized = deduped.map(normalizeMsg);
        const ordered = sortMsgsAsc(normalized);


        // cache do histórico expandido (evita sumir no próximo refresh)
        msgsCacheRef.current.set(getChatKey(telSnap), ordered);

        return ordered;
      });

      // Ajuste em 3 passes para lidar com imagens carregando
      const adjust = () => {
        const a = chatAreaRef.current;
        if (!a || !anchorEl || anchorTop == null) return;
        const nowTop = anchorEl.getBoundingClientRect().top;
        const delta = nowTop - anchorTop;
        if (Math.abs(delta) > 1) a.scrollTop += delta;
      };

      requestAnimationFrame(() => {
        adjust();                 // 1º ajuste (logo após render)
        setTimeout(adjust, 120);  // 2º ajuste (imagens menores)
        setTimeout(adjust, 400);  // 3º ajuste (imagens maiores)
      });

      // Se o backend não mandar has_more_old, mantenha o link enquanto vier algo
      setHasMoreOld((prev) => {
        if (typeof data?.has_more_old === "boolean") return !!data.has_more_old;

        // fallback seguro:
        // se veio pelo menos 1 msg, mantém o botão (porque pode ter mais no histórico)
        return incoming.length > 0;
      });


      return; // encerra aqui; não reposiciona como "bottom"/"anchor-unread"
    }

   
   // APPEND / FULL
let adicionou = 0;
setMensagens(prev => {
  const prevArr = Array.isArray(prev) ? prev : [];

  // 2.1) carga inicial completa
  if (forceFull || (!started && prevArr.length === 0)) {
    adicionou = incoming.length;
    setStarted(true);
    const deduped = dedupeMensagens(incoming);
      const normalized = deduped.map(normalizeMsg);
      return sortMsgsAsc(normalized);

  }

  // 2.2) índices auxiliares
  const seenZapi = new Set(prevArr.map(m => m.id_mensagem_zapi).filter(Boolean));
  const seenDb   = new Set(prevArr.map(m => m.id).filter(Boolean));

  // 2.3) vamos montar o array final fazendo reconciliação com otimistas
  const out = prevArr.slice();
  let appended = 0;

  for (const s of incoming) {
    const hasByDb   = s.id && seenDb.has(s.id);
    const hasByZapi = s.id_mensagem_zapi && seenZapi.has(s.id_mensagem_zapi);

    if (hasByDb || hasByZapi) {
      // já temos — opcionalmente atualiza os campos
      const idx = out.findIndex(m =>
        (s.id && m.id === s.id) ||
        (s.id_mensagem_zapi && m.id_mensagem_zapi === s.id_mensagem_zapi)
      );
      if (idx >= 0) out[idx] = { ...out[idx], ...s, status_envio: "sucesso", temp: false };
      continue;
    }

    // 2.4) tentar CASAR com um otimiz. pendente (sem id_zapi) – evita duplicar
    const idxTemp = out.findIndex(m => {
      if (m.tipo !== "saida") return false;
      if (m.status_envio && m.status_envio !== "pendente") return false;
      if (m.id_mensagem_zapi) return false;

      const sameText = String(m.mensagem || "") === String(s.mensagem || "");
      const sameReply = String(m.reply_to_zapi_id || "") === String(s.reply_to_zapi_id || "");
// aceita data_envio OU created_at; se algum faltar, não travar pelo tempo
const t1 = new Date(m.data_envio || m.created_at || 0).getTime();
const t2 = new Date(s.data_envio || s.created_at || 0).getTime();
const hasTimes = Number.isFinite(t1) && Number.isFinite(t2);
const closeTime = !hasTimes || Math.abs(t1 - t2) < 30000; // 30s

return sameText && sameReply && closeTime;

    });

// mantém lastId alinhado com a lista já ordenada
setTimeout(() => {
  const key = getChatKey(telSnap);
  const arr = msgsCacheRef.current.get(key) || mensagensRef.current || [];
  if (Array.isArray(arr) && arr.length) {
    const maxId = arr.reduce((mx, m) => Math.max(mx, Number(m?.id || 0)), 0);
    if (maxId > 0) setLastId(maxId);
  }
}, 0);


    if (idxTemp !== -1) {
      // substitui a otimista pelo item real do servidor
      out[idxTemp] = { ...out[idxTemp], ...s, status_envio: "sucesso", temp: false };
      if (s.id) seenDb.add(s.id);
      if (s.id_mensagem_zapi) seenZapi.add(s.id_mensagem_zapi);
      continue;
    }

    // 2.5) não casou com nada: adiciona normalmente
    out.push(s);
    appended++;
    if (s.id) seenDb.add(s.id);
    if (s.id_mensagem_zapi) seenZapi.add(s.id_mensagem_zapi);
  }

adicionou = appended;
const deduped = dedupeMensagens(out);
const normalized = deduped.map(normalizeMsg);
return sortMsgsAsc(normalized);


});


 // scroll automático só se estamos REALMENTE no rodapé agora
 if (adicionou > 0) {
   const area = chatAreaRef.current;
   const atBottom = area
     ? (area.scrollHeight - area.scrollTop - area.clientHeight) <= 0
     : true;
   if (atBottom && !autoScrollLockRef.current) {
     requestAnimationFrame(() => {
       const area2 = chatAreaRef.current;
       if (area2) {
         area2.scrollTo({ top: area2.scrollHeight, behavior: 'auto' });
         chatEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
       }
     });
   }
 }

    // (2) Atualizar lastId e metadados
    if (typeof data?.last_id === 'number') {
      setLastId(prev => Math.max(prev || 0, data.last_id));
    }

    setChatName(data?.chatName || '');
    if (!leadId && Number(data?.lead_id_resolvido) > 0) {
      setLeadId(Number(data.lead_id_resolvido));
    }

    // (2.1) Atualizar tags somente se a API enviou "tags"
    if (Object.prototype.hasOwnProperty.call(data, 'tags') && Array.isArray(data.tags)) {
      setTagsLead(prev => {
        const byName = new Map((tagsDisponiveis || []).map(t => [String(t.nome || '').toLowerCase(), t]));
        const byId   = new Map((tagsDisponiveis || []).map(t => [Number(t.id), t]));
        return data.tags.map(raw => {
          if (raw && typeof raw === 'object') {
            const id   = Number(raw.id) || byName.get(String(raw.nome || '').toLowerCase())?.id || null;
            const nome = raw.nome || byId.get(id)?.nome || String(raw).trim();
            const cor  = raw.cor || byId.get(id)?.cor ||
              prev.find(p => (p.id && p.id === id) || p.nome.toLowerCase() === String(nome).toLowerCase())?.cor ||
              '#666666';
            return { id, nome, cor };
          }
          const nome = String(raw || '').trim();
          const cat  = byName.get(nome.toLowerCase());
          const old  = prev.find(p => p.nome.toLowerCase() === nome.toLowerCase());
          return { id: cat?.id ?? old?.id ?? null, nome, cor: cat?.cor ?? old?.cor ?? '#666666' };
        });
      });
    }

    // (3) Posicionamento inicial ao abrir (sem animação)
    if (forceFull) {
      requestAnimationFrame(() => {
        const area = chatAreaRef.current;
        if (!area) return;
        if (openMode === 'bottom') {
          stickToBottomRef.current = true;
          area.scrollTo({ top: area.scrollHeight, behavior: 'auto' });
          chatEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
        } else if (openMode === 'anchor-unread') {
          const nodeList = area.querySelectorAll('[data-msg-idx]');
          const total = nodeList ? nodeList.length : 0;
          const rawIdx = total - Number(unreadCount || 0);
          const buffer = 2;
          const targetIdx = Math.max(0, (isFinite(rawIdx) ? rawIdx : total) - buffer);
          const targetEl = area.querySelector(`[data-msg-idx="${targetIdx}"]`);
          if (targetEl) {
            stickToBottomRef.current = false;
            targetEl.scrollIntoView({ behavior: 'auto', block: 'start' });
            area.scrollBy({ top: -8, behavior: 'auto' });
          } else {
            stickToBottomRef.current = true;
            area.scrollTo({ top: area.scrollHeight, behavior: 'auto' });
            chatEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
          }
        }
      });
    }
  } catch (e) {
    console.error('Erro ao buscar mensagens:', e);
        failCountRef.current = Math.min(5, failCountRef.current + 1);
    // pausa o since por 2^n * 1000ms (até 32s)
    const backoffMs = Math.pow(2, failCountRef.current) * 1000;
    pauseSinceUntilRef.current = Date.now() + backoffMs;
  }
};

// teste novo

const sseDebounceRef = useRef(null);
const carregarMensagensRef = useRef(null);
const telefoneAbertoRef = useRef("");
const sseUltimaMsgProcessadaRef = useRef(new Map());
const ssePendingUpdatesRef = useRef(new Map());

useEffect(() => {
  carregarMensagensRef.current = carregarMensagens;
}, [carregarMensagens]);

useEffect(() => {
  telefoneAbertoRef.current = String(
    telefoneChatAtualRef.current || telefone || ""
  ).trim();
}, [telefone]);

const aplicarConversaUpdate = useCallback((conversaNova) => {
  if (!conversaNova) return;

  setUltimasConversas((prev) => {
    const lista = Array.isArray(prev) ? [...prev] : [];

    const rawKey = String(
      conversaNova?.open_key ||
      conversaNova?.telefone_open ||
      conversaNova?.telefone ||
      conversaNova?.tel_key ||
      ""
    ).trim();

    const isGrupoNova =
      Number(conversaNova?.is_group) === 1 ||
      (
        !isLidLike(rawKey) &&
        /@g\.us$|-group$|^\d+-\d+$|^\d{18,30}$/.test(rawKey)
      );

    const telefoneCanonical = isGrupoNova
      ? rawKey.replace(/@g\.us$/i, "").replace(/-group$/i, "")
      : rawKey;

    const conversaNormalizada = {
      ...conversaNova,
      telefone: telefoneCanonical,
      telefone_open: telefoneCanonical,
      tel_key: String(conversaNova?.tel_key || telefoneCanonical),
      is_group: isGrupoNova ? 1 : Number(conversaNova?.is_group || 0),
      lead_id: isGrupoNova ? null : conversaNova?.lead_id,
      lead_id_resolvido: isGrupoNova ? null : conversaNova?.lead_id_resolvido,
      lead_nome: isGrupoNova ? null : conversaNova?.lead_nome,
    };

    const keyNova = getChatKey(conversaNormalizada);
    let achou = false;

    const atualizada = lista.map((c) => {
      const keyAtual = getChatKey(c);

      if (keyAtual !== keyNova) return c;

      achou = true;

      return {
        ...c,
        ...conversaNormalizada,
        _key: keyAtual,
      };
    });

    if (!achou) {
      atualizada.unshift({
        ...conversaNormalizada,
        _key: keyNova,
      });
    }

    return atualizada.sort((a, b) => {
      const ap = Number(a?.is_pinned || 0);
      const bp = Number(b?.is_pinned || 0);

      if (ap !== bp) return bp - ap;

      const at = dateFromServer(a?.data_envio || 0).getTime();
      const bt = dateFromServer(b?.data_envio || 0).getTime();

      return bt - at;
    });
  });
}, []);

const buscarConversaUpdate = useCallback(async (telKey) => {
  if (!telKey) return null;

  try {
    const res = await fetch(
      `/crm-wly/api/whatsapp/conversa_update.php?tel_key=${encodeURIComponent(telKey)}&ts=${Date.now()}`,
      {
        credentials: "include",
        cache: "no-store",
      }
    );

    const data = await res.json().catch(() => null);

    if (!res.ok || !data?.success || !data?.conversa) {
      return null;
    }

    return data.conversa;
  } catch {
    return null;
  }
}, []);

useEffect(() => {
  const stream = new EventSource(
    "/crm-wly/api/whatsapp/chat-stream.php",
    { withCredentials: true }
  );

  stream.addEventListener("chat_update", (event) => {
    let data = null;

    try {
      data = JSON.parse(event.data);
    } catch {
      return;
    }

    if (
      !data?.success ||
      !Array.isArray(data.updates) ||
      data.updates.length === 0
    ) {
      return;
    }

    data.updates.forEach((update) => {
      const telKey = String(update?.tel_key || "").trim();
      if (!telKey) return;

      ssePendingUpdatesRef.current.set(telKey, update);
    });

    clearTimeout(sseDebounceRef.current);

    sseDebounceRef.current = setTimeout(async () => {
      const updates = Array.from(ssePendingUpdatesRef.current.values());
      ssePendingUpdatesRef.current.clear();

      const telAberto = String(telefoneAbertoRef.current || "").trim();

      for (const update of updates) {
        const telKey = String(update?.tel_key || "").trim();
        if (!telKey) continue;

        const assinatura = [
          update?.update_source || "message",
          update?.msg_id || 0,
          update?.updated_at || "",
          update?.unread_count ?? "",
          update?.leitura ?? "",
          update?.smartview_last_msg_time || "",
          update?.smartview_last_msg_type || "",
        ].join("|");

        if (sseUltimaMsgProcessadaRef.current.get(telKey) === assinatura) {
          continue;
        }

        sseUltimaMsgProcessadaRef.current.set(telKey, assinatura);

        let conversa = await buscarConversaUpdate(telKey);

        if (!conversa) continue;

        const tipoConversa = String(conversa?.tipo || "").toLowerCase();
        const unreadConversa = Number(conversa?.unread_count || 0);

        conversa = {
          ...conversa,
          tipo: tipoConversa,
          unread_count: tipoConversa === "saida" ? 0 : unreadConversa,
          leitura: tipoConversa === "saida" ? 0 : unreadConversa > 0 ? 1 : 0,
        };

        aplicarConversaUpdate(conversa);

        const bateuNoChatAberto =
          telAberto &&
          (
            getChatKey(telAberto) === getChatKey(conversa) ||
            getChatKey(telAberto) === getChatKey(conversa.telefone) ||
            getChatKey(telAberto) === getChatKey(conversa.telefone_open) ||
            getChatKey(telAberto) === getChatKey(conversa.tel_key)
          );

if (bateuNoChatAberto) {
  markLocalReadByKey(getChatKey(conversa));

  marcarComoLida(
    conversa.telefone_open ||
    conversa.telefone ||
    conversa.tel_key ||
    telKey
  );

  carregarMensagensRef.current?.(false, { sinceMode: true });
}
      }
    }, 250);
  });

  stream.onerror = () => {};

  return () => {
    clearTimeout(sseDebounceRef.current);
    stream.close();
  };
}, [buscarConversaUpdate, aplicarConversaUpdate]);

//fim teste


 const enviarMensagem = async (texto, meta = {}) => {
  if (!texto?.trim()) return;

    if (whatsappBloqueiaEnvio) {
    alert("WhatsApp desconectado do Nórea. Conecte novamente para enviar mensagens.");
    return;
  }

  const mentioned = Array.isArray(meta?.mentioned) ? meta.mentioned : [];

  // ===== MODO EDIÇÃO =====
  if (editDraft?.id) {
    try {
      setSavingEdit(true);

      const res = await fetch("/crm-wly/api/whatsapp/enviar_mensagem.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          telefone,
          mensagem: texto,
          lead_id: leadId,
          edit_message_id: editDraft.id,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!data?.success) {
        throw new Error(data?.message || "Erro ao editar mensagem.");
      }

      setMensagens((prev) =>
        (Array.isArray(prev) ? prev : []).map((m) =>
          String(m?.id_mensagem_zapi || "") === String(editDraft.id)
            ? { ...m, mensagem: texto, editada: 1 }
            : m
        )
      );

      atualizarPreviewConversaLocal(texto);
      clearEditDraft();
      return;
    } catch (err) {
      console.error("Erro ao editar mensagem:", err);
      alert(err.message || "Erro ao editar mensagem.");
      return;
    } finally {
      setSavingEdit(false);
    }
  }

  // ===== MODO ENVIO NORMAL =====
  setLoading(true);

  const tempId = "tmp-" + Date.now();

  pauseSinceUntilRef.current = Date.now() + 1500;

  setMensagens((prev) => [
    ...prev,
    {
      tempId,
      tipo: "saida",
      mensagem: texto,
      data_envio: new Date().toISOString(),
      status_envio: "pendente",
      reply_to_zapi_id: replyDraft?.id || null,
    },
  ]);

  atualizarPreviewConversaLocal(texto);

  try {
    const res = await fetch("/crm-wly/api/whatsapp/enviar_mensagem.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        telefone,
        mensagem: texto,
        lead_id: leadId,
        reply_to_message_id: replyDraft?.id || undefined,
        mentioned,
      }),
    });

    const data = await res.json().catch(() => ({}));
    const srvId = data?.id;
    const srvZapiId = data?.id_mensagem_zapi;
    const success = !!data?.success;

    setMensagens((prev) => {
      const idxServidor = prev.findIndex(
        (m) =>
          (srvId && m.id === srvId) ||
          (srvZapiId && m.id_mensagem_zapi === srvZapiId)
      );
      const idxTemp = prev.findIndex((m) => m.tempId === tempId);

      if (idxServidor !== -1) {
        const clone = [...prev];
        if (idxTemp !== -1) clone.splice(idxTemp, 1);
        clone[idxServidor] = {
          ...clone[idxServidor],
          status_envio: success ? "sucesso" : "erro",
        };
        return dedupeMensagens(clone);
      }

      const promoted = prev.map((m) =>
        m.tempId === tempId
          ? {
              ...m,
              status_envio: success ? "sucesso" : "erro",
              id: srvId ?? m.id,
              id_mensagem_zapi: srvZapiId ?? m.id_mensagem_zapi,
            }
          : m
      );

      return dedupeMensagens(promoted);
    });

    setReplyDraft(null);
  } catch (error) {
    setMensagens((prev) =>
      prev.map((m) =>
        m.tempId === tempId ? { ...m, status_envio: "erro" } : m
      )
    );
    console.error("Erro ao enviar mensagem:", error);
  } finally {
    setLoading(false);
  }
};




const encaminharParaSelecionados = async () => {
  if (fwdSendingRef.current) return;

  if (!fwdMessage?.id_mensagem_zapi) {
    alert("Escolha uma mensagem válida.");
    return;
  }

  const selecionados = fwdDestinos.filter(
    (d) => fwdLeadIds.includes(String(d.id)) && d.telefone
  );

  const destinosMap = new Map();

  selecionados.forEach((d) => {
    const isGrupo = Number(d.is_group) === 1;

    const key = isGrupo
      ? `grupo:${String(d.telefone || "").replace(/@g\.us$/i, "")}`
      : Number(d.id) > 0
        ? `lead:${Number(d.id)}`
        : `tel:${String(d.telefone || "").replace(/\D+/g, "")}`;

    if (!destinosMap.has(key)) {
      destinosMap.set(key, d);
    }
  });

  const destinos = Array.from(destinosMap.values());

  if (!destinos.length) {
    alert("Selecione ao menos um destino.");
    return;
  }

  try {
    fwdSendingRef.current = true;
    setFwdSending(true);

    let ok = 0;
    let fail = 0;
    const msgsErro = [];

    for (const dest of destinos) {
      try {
        const isGrupo = Number(dest.is_group) === 1;

        const payload = {
          dest_phone: String(dest.telefone || "").trim(),
          message_id: String(fwdMessage.id_mensagem_zapi || "").trim(),
        };

        if (!isGrupo && Number(dest.id) > 0) {
          payload.lead_id = Number(dest.id);
        }

        if (telefone) {
          payload.source_phone = String(telefone).trim();
        }

        const res = await fetch("/crm-wly/api/whatsapp/encaminhar_mensagem.php", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data?.success) {
          throw new Error(
            data?.error ||
            data?.response?.message ||
            "Falha ao encaminhar"
          );
        }

        ok++;
      } catch (e) {
        fail++;
        msgsErro.push(`${dest.nome || dest.telefone}: ${e.message || "Erro"}`);
      }
    }

    if (fail === 0) {
      alert(`Encaminhado com sucesso para ${ok} destino(s).`);
      setFwdOpen(false);
      setFwdLeadIds([]);
      setFwdFilter("");
    } else {
      alert(`Enviado: ${ok} | Falhas: ${fail}\n\n${msgsErro.join("\n")}`);
    }
  } finally {
    fwdSendingRef.current = false;
    setFwdSending(false);
  }
};

async function enviarOuRemoverReacao(messageId, emojiOrNull) {
  if (!telefone) return;

  const msgIdStr = String(messageId);
  let snapshotPrev = null; // para reverter em caso de erro
  const prevMine = myReactionsRef.current.get(msgIdStr); // meu último emoji conhecido (heurístico local)

  // Helper: aplica mudança local no array `reactions` da mensagem
  const mutateReactions = (updater) => {
    setMensagens((prev) =>
      (Array.isArray(prev) ? prev : []).map((m) => {
        const same =
          String(m?.id_mensagem_zapi || "") === msgIdStr ||
          String(m?.id || "") === msgIdStr;
        if (!same) return m;

        const cur = Array.isArray(m.reactions) ? m.reactions.slice() : [];
        if (snapshotPrev === null) snapshotPrev = cur.slice(); // salva 1x
        const next = updater(cur);
        return { ...m, reactions: next };
      })
    );
  };

  // 1) UPDATE OTIMISTA
  if (emojiOrNull) {
    // adicionar/alterar minha reação
    mutateReactions((arr) => {
      // se eu já tinha um emoji (nesta sessão), remove 1 ocorrência dele
      if (prevMine) {
        const ix = arr.findIndex((r) => r && r.reaction === prevMine);
        if (ix >= 0) arr.splice(ix, 1);
      }
      arr.push({ reaction: emojiOrNull, _local: true });
      return arr;
    });
    myReactionsRef.current.set(msgIdStr, emojiOrNull);
  } else {
    // remover minha reação
    mutateReactions((arr) => {
      if (prevMine) {
        const ix = arr.findIndex((r) => r && r.reaction === prevMine);
        if (ix >= 0) arr.splice(ix, 1);
      } else if (arr.length) {
        // fallback: remove 1 (vamos conciliar com o servidor em seguida)
        arr.pop();
      }
      return arr;
    });
    myReactionsRef.current.delete(msgIdStr);
  }

  // 2) POST para o backend
const body = emojiOrNull
  ? { acao: "add", messageId, reaction: emojiOrNull }
  : { acao: "remover", messageId };


  try {
    const res = await fetch("/crm-wly/api/whatsapp/whatsapp_reacoes.php", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error("Falha ao registrar reação");
  } catch (e) {
    console.error("Erro ao reagir/remover reação:", e);
    // 3) Reverte em caso de erro
    if (snapshotPrev) {
      setMensagens((prev) =>
        (Array.isArray(prev) ? prev : []).map((m) => {
          const same =
            String(m?.id_mensagem_zapi || "") === msgIdStr ||
            String(m?.id || "") === msgIdStr;
          return same ? { ...m, reactions: snapshotPrev } : m;
        })
      );
    }
    // restaura meu último emoji local
    if (emojiOrNull) {
      if (prevMine) myReactionsRef.current.set(msgIdStr, prevMine);
      else myReactionsRef.current.delete(msgIdStr);
    }
  } finally {
    setEmojiMenuFor(null);
    // 4) Conciliar com o servidor (puxa delta rápido)
    setTimeout(() => {
      carregarMensagens(false, { sinceMode: true });
    }, 250);
  }
}

// largura da coluna esquerda (salva/restaura do localStorage)
const [leftWidth, setLeftWidth] = useState(() => {
  const saved = Number(localStorage.getItem("pm:leftWidth"));
  return Number.isFinite(saved) && saved >= 240 ? saved : 500; // 500 = seu padrão atual
});
const draggingRef = useRef(false);

const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

const startDrag = (e) => {
  if (!containerRef.current) return;
  draggingRef.current = true;
  document.body.classList.add("is-resizing");

  const onMove = (ev) => {
    if (!draggingRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const total = rect.width;
    const x = ev.clientX - rect.left;

    // limites: min 260px (lista), min 360px (chat), max 70% da área
    const minLeft = 260;
    const minRight = 360;
    const maxLeft = Math.min(Math.floor(total * 0.7), total - minRight);

    setLeftWidth(clamp(x, minLeft, maxLeft));
  };

  const stop = () => {
    draggingRef.current = false;
    document.body.classList.remove("is-resizing");
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", stop);
    localStorage.setItem("pm:leftWidth", String(leftWidth));
  };

  window.addEventListener("mousemove", onMove);
  window.addEventListener("mouseup", stop);
};

// Acessibilidade: setinhas ajustam em 10px e duplo clique reseta
const nudge = (delta) => setLeftWidth((w) => clamp(w + delta, 260, 900));
const handleKeyResizer = (e) => {
  if (e.key === "ArrowLeft") { e.preventDefault(); nudge(-10); }
  if (e.key === "ArrowRight"){ e.preventDefault(); nudge(+10); }
};
const resetWidth = () => setLeftWidth(500); // volta pro padrão

function isLidLike(s) {
  const v = String(s || "").trim().toLowerCase();
  return v.startsWith("lid:") || v.endsWith("@lid");
}

const fecharModalNovaFuncaoEditar = () => {
  localStorage.setItem("pm_nova_funcao_editar_v1", "1");
  setMostrarModalNovaFuncaoEditar(false);
};

const isGroupLike = (s) => {
  const v = String(s || "").trim().toLowerCase();
  if (!v) return false;
  if (isLidLike(v)) return false; // <-- REGRA PRINCIPAL
  return (
    v.endsWith("@g.us") ||
    v.endsWith("-group") ||
    /^\d+-\d+$/.test(v)
  );
};

// ===== HEADER (TOPO) - regras para grupo =====
const telHeader = String(selectedChat?.telefone || telefone || "").trim();

const isGrupoHeader =
  Number(selectedChat?.is_group) === 1 ||
  isGroupLike(telefone || "");

// conversa atual do topo usando chave estável
const conversaAtualTopo = useMemo(() => {
  const currentKey = selectedChat
    ? getChatKey(selectedChat)
    : getChatKey(telefone || "");

  return (ultimasConversas || []).find((c) => getChatKey(c) === currentKey) || null;
}, [selectedChat, telefone, ultimasConversas]);

const leadTopo =
  leads.find((l) => Number(l.id) === Number(leadId)) ||
  leads.find(
    (l) =>
      String(l.whatsapp_lid || "") ===
      String(telefone || "").replace(/^lid:/, "").replace(/@lid$/i, "")
  ) ||
  null;

const grupoFoto =
  selectedChat?.photo ||
  selectedChat?.foto ||
  conversaAtualTopo?.photo ||
  conversaAtualTopo?.foto ||
  "";

const grupoTitulo =
  selectedChat?.chatName ||
  selectedChat?.chat_name ||
  conversaAtualTopo?.chatName ||
  conversaAtualTopo?.chat_name ||
  chatName ||
  telefone;

const headerFoto = isGrupoHeader
  ? (grupoFoto || "https://cdn-icons-png.flaticon.com/512/149/149071.png")
  : (
      leadTopo?.foto ||
      selectedChat?.photo ||
      selectedChat?.foto ||
      conversaAtualTopo?.photo ||
      conversaAtualTopo?.foto ||
      "https://cdn-icons-png.flaticon.com/512/149/149071.png"
    );

const nomeLeadTopo =
  leadTopo?.nome ||
  chatName ||
  telefone;

const telefoneLeadTopo =
  leadTopo?.telefone ||
  telefone;

const valorLeadTopo = Number(leadTopo?.smartview_valor_orcamento || 0);
const valorConversaTopo = Number(conversaAtualTopo?.smartview_valor_orcamento || 0);
const valorSelectedTopo = Number(selectedChat?.smartview_valor_orcamento || 0);

const valorOrcamentoTopo =
  valorLeadTopo > 0
    ? valorLeadTopo
    : valorConversaTopo > 0
      ? valorConversaTopo
      : valorSelectedTopo > 0
        ? valorSelectedTopo
        : 0;

return (
  <>

{prefsNotifOpen && (
  <div className="pm-modal-backdrop" onClick={() => setPrefsNotifOpen(false)}>
    <div
      className="pm-modal"
      onClick={(e) => e.stopPropagation()}
      style={{ width: 420, maxWidth: "92vw" }}
    >
      <div className="pm-modal-header">
        <h3 style={{ margin: 0 }}>Notificações</h3>
        <button
          className="pm-modal-close"
          onClick={() => setPrefsNotifOpen(false)}
          title="Fechar"
        >
          ×
        </button>
      </div>

      <div className="pm-modal-body">
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          padding: "14px 0",
        }}>
          <div>
            <strong>Som de nova mensagem</strong>
            <div style={{ fontSize: 13, color: "#666", marginTop: 4, maxWidth: 260 }}>
              Ativa ou desativa o áudio de notificação.
            </div>
          </div>

          <label className="switch-toggle2">
            <input
              type="checkbox"
              checked={audioNotificacaoAtivo}
              onChange={(e) => {
                const ativo = e.target.checked;
                setAudioNotificacaoAtivo(ativo);
                localStorage.setItem("norea_audio_notificacao", ativo ? "1" : "0");
              }}
            />
            <span className="slider" />
          </label>
        </div>
      </div>
    </div>
  </div>
)}

    <div className="pm-smartviews-shell">
      <SmartViews
        activeKey={smartviewAtiva?.key || null}
        onApplyFilter={aplicarSmartview}
        onClearFilter={limparSmartview}
      />
    </div>

    <div
      ref={containerRef}
      className={`painel-mensagens ${(selectedChat || telefone) ? "pm-chat-open" : ""}`}
      style={{ display: "flex" }}
    >

          {carregandoInicial && (
        <div className="splash-loader">
          <div className="splash-logo">Nórea CRM</div>
          <div className="splash-hint" style={{marginBottom: "5px"}}>Carregando mensagens</div>
          <div className="splash-bar" />
          <div className="splash-hint">Protegida com a criptografia de ponta a ponta</div>
        </div>
      )}

    <div
      className="lista-conversas"
      style={{
      width: leftWidth,
      borderRight: "1px solid #ccc",
      padding: "10px",
      overflowY: "auto",
      position: "relative",
    }}
    >

      {smartviewAtiva && (
  <div className="pm-smartview-list-header">
    <div className="pm-smartview-list-info">
      <strong>{smartviewAtiva.title}</strong>
      <span>{conversasComSmartview.length} conversas</span>
    </div>

    <button
      type="button"
      onClick={limparSmartview}
      className="pm-smartview-list-clear"
    >
      Limpar
    </button>
  </div>
)}

      {/* Topo com título + menu hambúrguer */}
      <div
        className="lista-conversas-topo"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        
        <div className="conversa-menu">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      setPrefsNotifOpen(true);
    }}
    title="Configurações de notificação"
    style={{
      background: "transparent",
      border: "none",
      cursor: "pointer",
      padding: 0,
      color: "#075e54",
      display: "flex",
      alignItems: "center",
    }}
  >
    <Settings size={18} />
  </button>

  <h4 style={{ margin: 0 }}>Conversas</h4>
</div>

      

        {/* Botão do menu */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setMenuAberto((v) => !v);
          }}
          disabled={isMarkingAll}
          title="Opções"
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            fontSize: 20,
            lineHeight: "20px",
            padding: 4,
            color: "#075e54",
            marginTop: "-5px",
          }}
          aria-haspopup="true"
          aria-expanded={menuAberto}
          ref={btnMenuRef}
        >
          ☰
        </button>

        {/* Dropdown de opções */}
        {menuAberto && (
          <div
            ref={menuRef}
            style={{
              position: "absolute",
              top: 42,
              left: 10,
              zIndex: 4000,
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: 8,
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              overflow: "hidden",
              minWidth: 190,
            }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                marcarTodasComoLidas();
                setMenuAberto(false);
              }}
              disabled={isMarkingAll}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "10px 12px",
                background: "white",
                border: "none",
                cursor: "pointer",
                color: "#075e54",
              }}
            >
              {isMarkingAll ? "Marcando..." : "Marcar todos como lido"}
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setMostrarSomenteNaoLidos((v) => !v);
                setMenuAberto(false);
                recarregarConversas();
              }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "10px 12px",
                background: "white",
                borderTop: "1px solid #eee",
                borderBottom: "none",
                borderLeft: "none",
                borderRight: "none",
                cursor: "pointer",
                color: "#075e54",
              }}
            >
              {mostrarSomenteNaoLidos ? "Mostrar todos" : "Não lidos"}
            </button>

 </div>
)}            
      </div>
        <div className="top-icons">

                             {/* Lupa para abrir busca de lead */}
        <button
  type="button"
  onClick={(e) => { e.stopPropagation(); setLeadSearchOpen(v => !v); }}
  title="Buscar lead"
  style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4, color: "#075e54" }}
>
  <Search size={20} />
</button>

{/* Iniciar chat por telefone (ícone) */}
<button
  type="button"
  onClick={(e) => { e.stopPropagation(); setStartOpen(v => !v); }}
  title="Iniciar chat por telefone"
  style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4, color: "#075e54" }}
>
  <MessageSquarePlus size={20} />
</button>
{/* Botão de filtro por TAG */}
<div style={{ position: "relative" }}>
  <button
    type="button"
    className="btn-tag-filter"
    onClick={(e) => { e.stopPropagation(); setTagFilterOpen(v => !v); }}
    title="Filtrar por tag"
  >
    <Tag size={18} />
  </button>
</div>

{/* Dropdown de tags */}
{tagFilterOpen && (
  <div
    ref={tagDropdownRef}
    className="tag-dropdown"
    onClick={(e) => e.stopPropagation()}
  >
    <button
      className="tag-item"
      onClick={() => {
        setTagFilterIds([]);
        setTagFilterOpen(false);
      }}
    >
      Todas as tags
    </button>

    <div className="tag-list">
      {tagsDisponiveis.map((t) => (
        <button
          key={t.id}
          className={`tag-item ${
            tagFilterIds.includes(Number(t.id)) ? "active" : ""
          }`}
          onClick={() => {
            setTagFilterIds((prev) =>
              prev.includes(Number(t.id))
                ? prev.filter((id) => id !== Number(t.id))
                : [...prev, Number(t.id)]
            );
          }}
          style={{
            borderLeft: `6px solid ${t.cor || "#666"}`,
            background: tagFilterIds.includes(Number(t.id))
              ? t.cor || "#075E54"
              : "#fff",
            color: tagFilterIds.includes(Number(t.id)) ? "#fff" : "#333",
          }}
        >
          {t.nome}
        </button>
      ))}
    </div>
  </div>
)}

</div>

      </div>

  {/* Chip da tag selecionada */}
{Array.isArray(tagFilterIds) && tagFilterIds.length > 0 && (
  <span
    className="tag-chip"
    onClick={() => setTagFilterIds([])}
    title="Limpar filtros"
  >
    {selectedTagNames.length === 1
      ? selectedTagNames[0]
      : `${selectedTagNames.length} tags`}
    <XIcon size={14} />
  </span>
)}

{leadSearchOpen && (
  <div
    style={{
      position: "absolute",
      top: 58,                // antes era 42
      right: 48,
      transform: "translateY(6px)",
      zIndex: 20000,          // bem acima do topo do chat
      background: "#fff",
      border: "1px solid #ddd",
      borderRadius: 8,
      boxShadow: "0 16px 40px rgba(0,0,0,0.2)",
      width: 340,
      maxHeight: 480,
      overflow: "hidden",
    }}
    onClick={(e) => e.stopPropagation()}
  >
    <div style={{ padding: 8, borderBottom: "1px solid #eee" }}>
      <input
        autoFocus
        value={leadSearchQuery}
        onChange={(e) => setLeadSearchQuery(e.target.value)}
        placeholder="(Digite nome/telefone)"
        style={{ width: "100%", outline: "none", padding: "8px 10px", borderRadius: 6, borderWidth: "0.5px", borderStyle: "solid", borderColor: "#075e54" }}
      />
    </div>

    <div style={{ maxHeight: 380, overflowY: "auto" }}>
{(!leadSearchDebounced || leadSearchDebounced.length < 3) ? (
  <div style={{ padding: 12, color: "#888" }}>Digite pelo menos 3 caracteres</div>
) : leadSearchLoading ? (
  <div style={{ padding: 12, color: "#888" }}>Buscando...</div>
) : leadSearchResults.length === 0 ? (
  <div style={{ padding: 12, color: "#888" }}>Nada encontrado</div>
) : (
        leadSearchResults.map((ld) => (
          <button
            key={ld.id}
onClick={() => {
  flushConversaAtualComoLida();

  const tel = String(ld?.telefone || "").trim();

  const convDaLista = (ultimasConversas || []).find((c) => {
    const telC = String(c?.telefone_open || resolveKeyToOpen(c) || c?.telefone || "").trim();
    return (
      getChatKey(telC) === getChatKey(tel) ||
      Number(c?.lead_id_resolvido ?? c?.lead_id ?? 0) === Number(ld?.id)
    );
  });

  const conv = convDaLista || {
    ...ld,
    telefone: tel,
    telefone_open: tel,
    lead_id: ld?.id || null,
    lead_id_resolvido: ld?.id || null,
    lead_nome: ld?.nome || tel,
    photo: ld?.foto || null,
    foto: ld?.foto || null,
    is_group: 0,
    tags: ld?.tags || [],
  };

  const phoneToOpen = String(
    conv?.telefone_open || resolveKeyToOpen(conv) || conv?.telefone || tel
  ).trim();

  telefoneChatAtualRef.current = phoneToOpen;

  setSelectedChat({
    ...conv,
    telefone: phoneToOpen,
    telefone_open: phoneToOpen,
  });

  setIsGroup(false);
  setLeadId(Number(ld?.id || conv?.lead_id_resolvido || conv?.lead_id || 0) || null);
  setChatName(ld?.nome || conv?.lead_nome || phoneToOpen);
  setTagsLead([]);
  setMensagens([]);
  setStarted(false);
  setLastId(0);

  setTelefone(phoneToOpen);

  const stableKey = convStableKey({
    ...conv,
    telefone: phoneToOpen,
    telefone_open: phoneToOpen,
    lead_id: ld?.id || conv?.lead_id || null,
  });

  const cached = msgsCacheRef.current.get(stableKey);

  if (Array.isArray(cached) && cached.length) {
    setMensagens(sortMsgsAsc(cached.map(normalizeMsg)));
    setStarted(true);
  }

  markLocalReadByKey(stableKey);
  marcarComoLida(phoneToOpen);

  setLeadSearchOpen(false);
  setLeadSearchQuery("");
}}

            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              width: "100%",
              padding: "10px 12px",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontWeight: 400,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: "#075e54",
                fontSize: "12",
              }}
            >
              {ld.nome ? `${ld.nome} — ${ld.telefone}` : ld.telefone}
            </div>
          </div>


          </button>
        ))
      )}
    </div>
  </div>
)}


{startOpen && (
  <div
    style={{
      position: "absolute",
      top: 58,
      right: 10,
      transform: "translateY(6px)",
      zIndex: 20000,
      background: "#fff",
      border: "1px solid #ddd",
      borderRadius: 8,
      boxShadow: "0 16px 40px rgba(0,0,0,0.2)",
      width: 250,
      overflow: "hidden",
    }}
    onClick={(e) => e.stopPropagation()}
  >
    <div
  style={{
    position: "relative",           
    padding: 10,
    borderBottom: "1px solid #eee",
    display: "flex",
    alignItems: "center",
    gap: 8
  }}
>
  <button
    type="button"
    onClick={() => setStartOpen(false)}
    title="Fechar"
    className="start-chat-close2"
  >
    <XIcon size={18} />
  </button>

      <input
        type="text"
        inputMode="tel"
        placeholder="(99) 9 9999-9999"
        ref={startInputRef}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleStartChatFromRef();
          if (e.key === "Escape") setStartOpen(false);
        }}
        style={{
          flexGrow: 1,
          width: 150,
          padding: "10px 12px",
          border: "1px solid #ddd",
          borderRadius: 8,
          fontSize: 14,
        }}
        autoFocus
      />
      <button
        type="button"
        onClick={handleStartChatFromRef}
        title="Iniciar"
        style={{
          background: "transparent",
          border: "none",
          padding: 0,   // <<< reduzido
          cursor: "pointer",
          lineHeight: 0,
        }}
      >
        <SendHorizontal size={40} className="start-chat-send2" color="#075e54" />


      </button>
      
    </div>  
      </div>
      
)}



{/* Link/controle de arquivadas */}
<div style={{ padding: "6px 2px", display: "flex", alignItems: "center", gap: 10 }}>
  {!showArchivedOnly ? (
    archivedCount > 0 && (
      <button
        type="button"
        className="link-arquivadas"
        onClick={() => setShowArchivedOnly(true)}
        title="Ver apenas arquivadas"
      >
        <FolderDown size={18} />
        <span>Arquivadas</span>
        <span className="pill">{archivedCount}</span>
      </button>
    )
  ) : (
    <button
      type="button"
      className="link-arquivadas voltar"
      onClick={() => setShowArchivedOnly(false)}
      title="Voltar"
    >
      <ArrowLeft size={18} />
      {/* sem texto quando filtrado */}
    </button>    
  )}
  {showArchivedOnly && (
  <button
  type="button"
  className="pm-prefs-btn"
  onClick={() => setPrefsOpen(true)}
  title="Configurações"
>
  <Settings size={22} />
</button>

)}
</div>




      {/* Lista de conversas */}
{(mostrarSomenteNaoLidos
  ? conversasComSmartview.filter((c) => (c.unread_count || 0) > 0)
  : conversasComSmartview
).map((conv, idx) => {
        const key = conv._key || getChatKey(conv);
        const nome =
          conv.chatName ||                          // 1º: sempre prioriza o nome do chat (grupo)
          conv.lead_nome ||                         // 2º: nome do lead (quando for contato normal)
          conv.remetente_nome ||                    // 3º: se for grupo sem chatName, usa remetente
          conv.senderName ||                        // 4º: fallback
          conv.telefone;                            // 5º: último recurso, mostra o número


// Detecta grupo
const isGroupList =
  Number(conv.is_group) === 1 ||
  /@g\.us$|-group$|^\d+-\d+$/.test(String(conv.telefone || ""));

// Tipo normalizado da última mensagem
const tipoMsg = String(conv.tipo || "").toLowerCase();
const isSaida = tipoMsg === "saida";

// Autor (quando for entrada em grupo)
let autor = "";
if (isGroupList && tipoMsg === "entrada") {
  autor =
    (conv.remetente_nome && String(conv.remetente_nome).trim()) ||
    conv.chatName ||
    conv.remetente_numero;
}

// ================= PREVIEW COMPLETO =================

let previewFull;

// 1) Se último evento for REAÇÃO → prioriza isso
if (conv.last_event_type === "reaction" && conv.last_reaction_emoji) {
  const tipoMidia = String(conv.last_reaction_msg_tipo_midia || "").toLowerCase();
  let alvo;

  if (["image", "photo", "imagem", "picture"].includes(tipoMidia)) {
    alvo = "Foto";
  } else if (["audio", "ptt", "voice", "audio_ptt"].includes(tipoMidia)) {
    alvo = "áudio";
  } else if (conv.last_reaction_msg_texto) {
    alvo = conv.last_reaction_msg_texto;
  } else {
    alvo = "mensagem";
  }

  // Direção da MENSAGEM que recebeu a reação
  // - se a mensagem era ENTRADA → você recebeu a msg e reagiu a ela → "Você reagiu..."
  // - se a mensagem era SAÍDA → você enviou a msg e a outra pessoa reagiu → "Reagiu com..."
  const direcao = String(conv.last_reaction_msg_direcao || "").toLowerCase();
  const voceReagiu = direcao === "entrada";
  const prefixo = voceReagiu ? "Você reagiu com" : "Reagiu com";

  previewFull = `${prefixo} ${conv.last_reaction_emoji} a: "${alvo}"`;
} else {
  // 2) Caso contrário, preview normal baseado no texto da última mensagem
  const msgBase = conv.mensagem ?? "";
  previewFull = msgBase;

  if (isGroupList) {
    if (tipoMsg === "entrada" && autor) {
      previewFull = `~ ${autor}: ${msgBase}`;
    } else if (tipoMsg === "saida") {
      previewFull = `Você: ${msgBase}`;
    }
  }
}



        const preview =
          previewFull.length > 60 ? previewFull.slice(0, 60) + "..." : previewFull;

        const horario = formatarHorarioLista(conv.data_envio);

        const valorOrcamentoLista = Number(conv.smartview_valor_orcamento || 0);


       const telItem = String(conv?.telefone_open || resolveKeyToOpen(conv) || conv?.telefone || "").trim();

        // chave estável do item da lista
        const itemKey = convStableKey(conv);

        // chave estável da conversa atual
        const currentKey = selectedChat ? getChatKey(selectedChat) : getChatKey(telefone || "");

        // ativo de verdade (não depende de telefone “variando”)
        const isAtiva = itemKey === currentKey;



        const foto =
          conv.photo ||
          "https://cdn-icons-png.flaticon.com/512/149/149071.png";

        const unread = conv.unread_count || 0;

        

        // helper p/ renderizar pílulas (todas as tags)
        const renderPills = () => {
          // 1) Já veio do backend a lista de cores?
          const coresAG = String(conv.tags_cores || "")
            .split("|")
            .map((s) => s.trim())
            .filter(Boolean);
          if (coresAG.length) {
            return coresAG.map((cor, i) => (
              <span
                key={`core-${i}`}
                className="conv-tag-pill"
                style={{ "--tag": cor }}
                title={cor}
              />
            ));
          }

          // 2) Veio só id? resolve via catálogo
          const idsAG = String(conv.tags_ids || "")
            .split("|")
            .map((s) => parseInt(s, 10))
            .filter((n) => !isNaN(n));
          if (idsAG.length) {
            return idsAG.map((id, i) => {
              const cor =
                (tagsDisponiveis || []).find((td) => Number(td.id) === id)
                  ?.cor || "#666666";
              return (
                <span
                  key={`id-${id}-${i}`}
                  className="conv-tag-pill"
                  style={{ "--tag": cor }}
                  title={cor}
                />
              );
            });
          }

          // 3) Veio só nome? resolve via catálogo
          const nomesAG = String(conv.tags || conv.tags_nomes || "")
            .split("|")
            .map((s) => s.trim())
            .filter(Boolean);
          if (nomesAG.length) {
            return nomesAG.map((nomeT, i) => {
              const cor =
                (tagsDisponiveis || []).find(
                  (td) =>
                    String(td.nome || "").toLowerCase() ===
                    String(nomeT).toLowerCase()
                )?.cor || "#666666";
              return (
                <span
                  key={`nm-${nomeT}-${i}`}
                  className="conv-tag-pill"
                  style={{ "--tag": cor }}
                  title={nomeT}
                />
              );
            });
          }

          // 4) Fallback: se é a conversa ativa, usa as tags carregadas nela
     //     if (isAtiva && Array.isArray(tagsLead) && tagsLead.length) {
       //     return tagsLead.map((t, i) => (
 //             <span
   //             key={`act-${t.id ?? i}`}
     //           className="conv-tag-pill"
       //         style={{ "--tag": t.cor || "#666666" }}
         //       title={t.nome}
       //       />
        //    ));
        //  }

          return null;
        };

        

        return (
          <div
            key={key}
            className={`conversa-item ${isAtiva ? "active" : ""}`}
           onClick={() => {
              flushConversaAtualComoLida();
              setEditandoNomeLead(false);
              setNomeLeadEditando("");
              setSalvandoNomeLead(false);
// 1) salva conversa atual no cache (SEMPRE com a MESMA chave usada para ler)
if (telefone) {
  const keyAtual = convKeyJS(telefone);
  msgsCacheRef.current.set(
    keyAtual,
    sortMsgsAsc((Array.isArray(mensagens) ? mensagens : []).map(normalizeMsg))
  );
}


 // 2) define o telefone REAL que será usado no get_mensagens
const phoneToOpen = String(conv?.telefone_open || resolveKeyToOpen(conv) || conv?.telefone || "").trim();
telefoneChatAtualRef.current = phoneToOpen;
setTelefone(phoneToOpen);

// 3) detecta grupo pelo padrão (hífen / @g.us etc)
// ✅ REGRA: LID NÃO É GRUPO (lid:... ou ...@lid)
const phoneLower = phoneToOpen.toLowerCase();
const isLid = phoneLower.startsWith("lid:") || phoneLower.endsWith("@lid");

// ✅ Grupo só se não for LID
const isGroupPattern =
  !isLid &&
  (
    /@g\.us$/i.test(phoneToOpen) ||
    /-group$/i.test(phoneToOpen) ||
    /^\d+-\d+$/.test(phoneToOpen) ||
    /^\d{18,30}$/.test(phoneToOpen) // concat numérico do grupo (mantenha só se você realmente usa)
  );

const isGroupClk = Number(conv?.is_group) === 1 || isGroupPattern;

setIsGroup(isGroupClk);


// 4) IMPORTANTÍSSIMO:
// grupo -> NÃO manda lead_id (senão o backend sobrescreve o telefone)
const newLeadId = isGroupClk
  ? null
  : (Number(conv?.lead_id_resolvido ?? conv?.lead_id ?? 0) || null);

setSelectedChat(conv);                 // ✅ para o carregarMensagens usar selectedChat.telefone
setLeadId(newLeadId);                  // ✅ grupo = null
setEstagioAtual(isGroupClk ? "" : (conv.estagio_funil || ""));
setOrigemAtual(isGroupClk ? "" : (conv.origem_lead || ""));
setChatName(
  conv.chat_name ||      // ✅ vem do backend
  conv.chatName  ||      // (mantém compat)
  conv.lead_nome ||
  conv.remetente_nome ||
  phoneToOpen
);

setTagsLead([]);
setLastId(0);

// cache instantâneo (sem quebrar override do badge)
const stableKey = convStableKey({
  ...conv,
  telefone_open: phoneToOpen,
  telefone: phoneToOpen,
});

const cached = msgsCacheRef.current.get(stableKey);

if (Array.isArray(cached) && cached.length) {
  const orderedCached = sortMsgsAsc(cached.map(normalizeMsg));
  setMensagens(orderedCached);
  setStarted(true);

  requestAnimationFrame(() => {
    autoScrollLockRef.current = false;
    const area = chatAreaRef.current;
    if (area) area.scrollTop = area.scrollHeight;
    chatEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
  });
} else {
  setMensagens([]);
  setStarted(false);
}

// 5) abre a conversa (monta URL/estado)
setTelefone(phoneToOpen);

// 6) feedback local do badge (anti “volta” por polling/cache)
markLocalReadByKey(stableKey);

// 7) marca como lida no servidor (sem tentar zerar badge aqui de novo)
marcarComoLida(phoneToOpen);

}}

            onMouseLeave={() => { if (rowMenuOpenKey === (conv._key || getChatKey(conv))) setRowMenuOpenKey(null); }}
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              padding: "8px 6px",
              cursor: "pointer",
              border: "none",
              boxShadow: "none",
              outline: "none",
            }}
          >
            <img
              key={foto}
              src={foto}
              alt="Foto"
              className="foto-contato"
              onError={(e) => {
                e.target.src =
                  "https://cdn-icons-png.flaticon.com/512/149/149071.png";
              }}
            />

            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Linha 1: Nome + Pílulas + Hora */}
              <div
                className="conv-line-1"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                }}
              >
                <div
                  className="conv-namewrap"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    minWidth: 0,
                  }}
                >
                  <strong
                    className="conv-name"
                    style={{
                      display: "block",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {nome}
                  </strong>

                  <span
                    className="conv-tagwrap"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      flexShrink: 0,
                    }}
                  >
                    {renderPills()}
                  </span>
                </div>

                <div
                  style={{
                    fontSize: "0.75em",
                    color: "#999",
                    flexShrink: 0,
                  }}
                >              

<div className="conv-right">
  {valorOrcamentoLista > 0 && (
    <span className="conv-list-orcamento">
      {formatarMoedaBR(valorOrcamentoLista)}
    </span>
  )}

  <span className="conv-hour">{horario}</span>

  {/*Estava aqui o chevron*/}
</div>
                </div>
              </div>

{/* Linha 2: preview + badge não-lido */}
<div
  className="conv-line-2"
  style={{ display: "flex", alignItems: "center", gap: 4 }}
>
  {isSaida && (
    <CheckCheck
      size={14}
      className="conv-check-preview"
      style={{ flexShrink: 0 }}
    />
  )}

  <span
  className="conv-preview"
  style={{ flex: 1, minWidth: 0 }}
  dangerouslySetInnerHTML={{
    __html: parseWhatsAppPreview(preview)
  }}
/>


  {Number(conv.is_pinned) === 1 && <Pin size={12} className="pin-mini" />}
  
  {unread > 0 && <span className="badge-unread">{unread}</span>}

  <button
    type="button"
    className="row-chevron"
    onClick={(e) => {
      e.stopPropagation();
      setRowMenuOpenKey((prev) => (prev === key ? null : key));
    }}
    title="Mais opções"
  >
    <ChevronDown size={18} />
  </button>

  {rowMenuOpenKey === key && (
    <div
      className="conversa-dropdown"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Arquivar / Desarquivar */}
      {conv.is_archived ? (
        <button onClick={() => toggleArchive(conv, 'unarchive')} style={btnDrop}>
          <ArchiveRestore /> <span>Desarquivar conversa</span>
        </button>
      ) : (
        <button onClick={() => toggleArchive(conv, 'archive')} style={btnDrop}>
          <ArchiveIcon /> <span>Arquivar conversa</span>
        </button>
      )}

            {/* Fixar / Desafixar */}
      {!conv.is_pinned ? (
        <button onClick={() => togglePinChat(conv.telefone)} style={btnDrop}>
          <Pin /> <span>Fixar conversa</span>
        </button>
      ) : (
        <button onClick={() => togglePinChat(conv.telefone)} style={btnDrop}>
          <PinOff /> <span>Desafixar conversa</span>
        </button>
      )}

      {/* Marcar lida / não lida */}
      {Number(unread) > 0 ? (
        <button onClick={() => marcarComoNaoLidaOuLida(conv, 'read')} style={btnDrop}>
          <MessageSquare /> <span>Marcar como lida</span>
        </button>
      ) : (
        <button onClick={() => marcarComoNaoLidaOuLida(conv, 'unread')} style={btnDrop}>
          <MessageSquareDot /> <span>Marcar como não lida</span>
        </button>
      )}
      
    </div>
  )}
              </div>


            </div>
          </div>
        );
      })}
    </div>

<div
  className="split-resizer"
  role="separator"
  aria-orientation="vertical"
  aria-label="Ajustar largura da lista"
  tabIndex={0}
  onMouseDown={startDrag}
  onDoubleClick={resetWidth}
  onKeyDown={handleKeyResizer}
/>




  <div className="conteudo-chat">
  <div className="topo-conversa">

<button
  type="button"
  className="btn-voltar-mobile"
  onClick={() => {
    flushConversaAtualComoLida();
    setSelectedChat(null);
    setTelefone("");     // <<< ESSENCIAL (fecha o chat)
    setLeadId(null);     // opcional
    setIsGroup(false);   // opcional
    setMensagens([]);    // opcional
  }}
  aria-label="Voltar para conversas"
>
  <ArrowLeft size={22} />
</button>
    
{leadId && !isGrupoHeader ? (
  <a
    href={`https://welaunchyou.com.br/crm-wly/lead-detalhes/${leadId}`}
    target="_blank"
    rel="noopener noreferrer"
  >
    <img
      src={headerFoto}
      alt="Foto do contato"
      className="foto-contato-topo"
      onError={(e) => {
        e.target.src = "https://cdn-icons-png.flaticon.com/512/149/149071.png";
      }}
      style={{ cursor: "pointer" }}
    />
  </a>
) : (
  <img
    src={headerFoto}
    alt="Foto do grupo"
    className="foto-contato-topo"
    onError={(e) => {
      e.target.src = "https://cdn-icons-png.flaticon.com/512/149/149071.png";
    }}
  />
)}




     <div className="dados-contato-topo">
  {leadId ? (
    <>
      {/* Nome + botão copiar */}
      <div className="nome-com-copy">
  {editandoNomeLead ? (
    <>
      <input
        type="text"
        value={nomeLeadEditando}
        onChange={(e) => setNomeLeadEditando(e.target.value)}
        onKeyDown={async (e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (!nomeLeadEditando.trim() || !leadId) return;

            try {
              setSalvandoNomeLead(true);

              const res = await fetch("/crm-wly/api/whatsapp/atualizar_lead.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                  lead_id: leadId,
                  nome: nomeLeadEditando.trim(),
                }),
              });

              const data = await res.json();

              if (data.success) {
                setLeads((prev) =>
                  prev.map((l) =>
                    l.id === leadId ? { ...l, nome: nomeLeadEditando.trim() } : l
                  )
                );
                setChatName(nomeLeadEditando.trim());
                setEditandoNomeLead(false);
              } else {
                alert(data.error || "Erro ao atualizar nome.");
              }
            } catch (err) {
              console.error(err);
              alert("Erro ao atualizar nome.");
            } finally {
              setSalvandoNomeLead(false);
            }
          }

          if (e.key === "Escape") {
            setEditandoNomeLead(false);
            setNomeLeadEditando(nomeLeadTopo || "");
          }
        }}
        autoFocus
        className="input-editar-nome"
      />

     <button
  onClick={async () => {
    const nome = nomeLeadEditando.trim();
    if (!nome || !leadId) return;

    try {
      setSalvandoNomeLead(true);

      const res = await fetch("/crm-wly/api/whatsapp/atualizar_lead.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          lead_id: leadId,
          nome: nome,
        }),
      });

      const data = await res.json();

      if (!data.success) {
        throw new Error(data.error || "Erro ao atualizar nome.");
      }

      setLeads((prev) =>
        prev.map((l) =>
          l.id === leadId ? { ...l, nome } : l
        )
      );

      setChatName(nome);
      setEditandoNomeLead(false);

    } catch (err) {
      console.error(err);
      alert(err.message || "Erro ao atualizar nome.");
    } finally {
      setSalvandoNomeLead(false);
    }
  }}
  className="copy-btn"
  title="Salvar nome"
  disabled={salvandoNomeLead}
>
  <Save className="icon-save-nome" />
</button>
    </>
  ) : (
    <>
<a
  href={`https://welaunchyou.com.br/crm-wly/lead-detalhes/${leadId}`}
  target="_blank"
  rel="noopener noreferrer"
  title="Ver detalhes do lead"
>
  {nomeLeadTopo}
</a>

      <button
        onClick={() =>
          copiarTexto(leads.find((l) => l.id === leadId)?.nome)
        }
        className="copy-btn"
        title="Copiar nome"
      >
        <Copy className="icon-copy-nome" />
      </button>

<div className="editar-nome-wrapper">
<button
  onClick={() => {
    setNomeLeadEditando(nomeLeadTopo || "");
    setEditandoNomeLead(true);
  }}
  className="copy-btn"
  title="Editar nome"
  type="button"
>
  <Pencil className="icon-pencil-nome" />
</button>

</div>
    </>
  )}
</div>

      {/* Telefone + botão copiar */}
<div className="telefone-com-copy">
  <span>{telefoneLeadTopo}</span>
  <button
    onClick={() => copiarTexto(telefoneLeadTopo)}
    className="copy-btn"
    title="Copiar telefone"
  >
    <Copy />
  </button>
</div>
    </>
  ) : (
     <strong>{isGrupoHeader ? grupoTitulo : (chatName || telefone)}</strong>

  )}




  {/* botão flutuante para pular pro final */}
{showJumpBottom && (
<button
  type="button"
  className="btn-scroll-bottom"
  onClick={() => { autoScrollLockRef.current = false; scrollToBottom("smooth"); }}
  title="Ir para a última mensagem"
>
  <img src="/crm-wly/uploads/svg/setinha.png" alt="Ir para o fim" style={{ width: 20, height: 20 }} />
</button>
)}

    
  {leadId && !isGrupoHeader && (
  <>
    <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
      {/* Estágio do funil */}
      <select
        value={estagioAtual}
        onChange={async (e) => {
          const novoEstagio = e.target.value;
          setEstagioAtual(novoEstagio);
          const res = await fetch("/crm-wly/api/whatsapp/atualizar_estagio.php", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              lead_id: leadId,
              estagio_funil: novoEstagio
            })
          });
          const data = await res.json();
         if (data.success) {
  setEstagioAtual(novoEstagio);

  setLeads(prev =>
    prev.map(l =>
      l.id === leadId ? { ...l, estagio_funil: novoEstagio } : l
    )
  );

  setUltimasConversas(prev =>
    prev.map(conv =>
      Number(conv.lead_id_resolvido || conv.lead_id) === Number(leadId)
        ? { ...conv, estagio_funil: novoEstagio }
        : conv
    )
  );

  setSelectedChat(prev =>
    prev
      ? { ...prev, estagio_funil: novoEstagio }
      : prev
  );

  setTimeout(() => recarregarConversas(false, true), 300);
} else {
            alert("Erro ao atualizar estágio.");
              setEstagioAtual(selectedChat?.estagio_funil || "");

          }
        }}
        style={{
          fontSize: "0.85em",
          padding: "2px 4px",
          borderRadius: "4px",
          background: `transparent url('/crm-wly/uploads/svg/setinha.svg') no-repeat right 5px center`,
          backgroundSize: "8px 8px",
          appearance: "none",
          paddingRight: "18px",
          color: "#555",
          border: "none"
        }}
      >
        <option value="Contato Inicial">Contato Inicial</option>
        <option value="Qualificação">Qualificação</option>
        <option value="Agendamento">Agendamento</option>
        <option value="Acompanhamento">Acompanhamento</option>
        <option value="Agendado">Agendado</option>
        <option value="Cliente">Cliente</option>
      </select>


<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
  <span style={{ marginTop: "10px", fontSize: "0.8em", color: "#444" }}><b>Origem:</b></span>
  <select
    value={origemAtual}
    onChange={async (e) => {
      const novaOrigem = e.target.value;
      setOrigemAtual(novaOrigem);

      const res = await fetch("/crm-wly/api/whatsapp/atualizar_estagio.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          lead_id: leadId,
          origem_lead: novaOrigem
        }),
      });

      const data = await res.json();
      if (data.success) {
        setLeads(prev =>
          prev.map(l =>
            l.id === leadId ? { ...l, origem_lead: novaOrigem } : l
          )
        );
      } else {
        alert("Erro ao atualizar origem.");
      }
    }}
    style={{
      fontSize: "0.85em",
      padding: "2px 4px",
      borderRadius: "4px",
      background: `transparent url('/crm-wly/uploads/svg/setinha.svg') no-repeat right 5px center`,
      backgroundSize: "8px 8px",
      appearance: "none",
      paddingRight: "18px",
      color: "#555",
      border: "none",
      width: "180px"
    }}
  >
    <option value="Facebook">Facebook</option>
    <option value="Instagram">Instagram</option>
    <option value="Google Ads">Google Ads</option>
    <option value="Indicação">Indicação</option>
    <option value="Nórea CRM">Nórea CRM</option>
  </select>
</div>


 {/* ATIVAR/DESATIVAR IA */}
{iaPermitida && (
  <div
    title="Clique para ativar ou desativar a IA para este lead"
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      marginTop: "8px",
      padding: "4px 8px",
      borderRadius: "6px",
      
    }}
  >
    {iaBloqueado ? (
      <ShieldX size={24} color="#cc0000" />
    ) : (
      <Brain size={24} color="#2e7d32" />
    )}

    <label className="switch-toggle2" title="Ativar/Desativar IA"  style={{ marginTop: "1px" }}>
      <input
  type="checkbox"
  checked={!iaBloqueado} // ligado = IA ativa
  onChange={async (e) => {
    const iaAtiva = e.target.checked;   // true quando ligou
    const novoBloqueio = !iaAtiva;      // bloqueio é o inverso

    // atualiza UI na hora
    setIaBloqueado(novoBloqueio);

    // sem telefone, não dá pra salvar
    if (!telefone) return;

    try {
      const res = await fetch("/crm-wly/api/openai/numeros_bloqueados.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          telefone,
          acao: novoBloqueio ? "bloquear" : "desbloquear",
        }),
      });
      const data = await res.json();
      if (!data?.success) {
        // se falhar, volta estado
        setIaBloqueado(!novoBloqueio);
        alert("Erro ao atualizar o agente de IA.");
      }
    } catch (err) {
      setIaBloqueado(!novoBloqueio);
      console.error(err);
    }
  }}
/>
      <span className="slider" />
    </label>
  </div>
)}


{leadId && !isGrupoHeader && (
  <div className="orcamento-topo-box">
    <span className="orcamento-topo-label">Orçamento Potencial</span>

    {editandoOrcamento ? (
      <>
        <input
          type="text"
          value={valorOrcamentoEditando}
          onChange={(e) => setValorOrcamentoEditando(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") salvarOrcamentoLead();
            if (e.key === "Escape") setEditandoOrcamento(false);
          }}
          autoFocus
          className="orcamento-topo-input"
          placeholder="R$ 0,00"
        />

        <button
          type="button"
          className="copy-btn"
          onClick={salvarOrcamentoLead}
          disabled={salvandoOrcamento}
          title="Salvar orçamento"
        >
          <Save className="icon-save-nome" />
        </button>
      </>
    ) : (
      <>
        <strong className="orcamento-topo-valor">
          {formatarMoedaBR(valorOrcamentoTopo)}
        </strong>

        <button
          type="button"
          className="copy-btn"
          title="Editar orçamento"
          onClick={() => {
            setValorOrcamentoEditando(
             String(valorOrcamentoTopo || "")
            );
            setEditandoOrcamento(true);
          }}
        >
          <Pencil className="icon-pencil-nome" />
        </button>
      </>
    )}
  </div>
)}
    </div>

   
  </>
)}


</div>

</div>
 {/* NOVO BLOCO DE TAGS ABAIXO */}
 {leadId && !isGrupoHeader && (
<div
  style={{
    background: "#f2f3f5",
    padding: "8px 10px",
    marginTop: "8px",
    borderRadius: "6px",
    display: "flex",
   // justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  }}
>
  {/* Tags associadas */}
    <div className="chat-tags-row" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
    {tagsLead.length > 0 ? (
      tagsLead.map((t) => (
        <span
          key={t.id}
          title="Clique para remover"
          className="chat-tag-badge"
          style={{
            backgroundColor: t.cor || "#666666",
            color: getContraste(t.cor || "#666666"),
            cursor: "pointer",
          }}
          onClick={async () => {
          if (tagsLoading) return;
  try {
    const res = await fetch("/crm-wly/lead_tags.php", {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
      body: JSON.stringify({ lead_id: leadId, tag_id: t.id }),
    });

    // 1) se a resposta não for 2xx, loga o texto e aborta
    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      console.error("DELETE /lead_tags.php ->", res.status, txt);
      alert("Erro ao remover tag.");
      return;
    }

    // 2) tenta ler como JSON; se não tiver corpo/JSON, considera sucesso
    let data = null;
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      try { data = await res.json(); } catch { data = null; }
    }

    // 3) sucesso “padrão” (data.success) OU sucesso sem corpo (204/empty)
        if (!data || data.success) {
      setTagsLead((prev) => prev.filter((x) => x.id !== t.id));
      await carregarTagsLead(leadId);
      bloqueiaPollingRef.current = true;
      await recarregarConversas(false, true);
      setTimeout(() => {
        bloqueiaPollingRef.current = false;
      }, 500); // meio segundo é suficiente


    } else {
      alert("Erro ao remover tag.");
    }

  } catch (err) {
    console.error("Erro ao remover tag:", err);
  }
}}
        >
          {t.nome} ✕
        </span>
      ))
) : tagsLoading ? (
  <div style={{ display: "flex", gap: 6 }}>
    <span className="chat-tag-skel" />
    <span className="chat-tag-skel" />
    <span className="chat-tag-skel" />
  </div>
) : (
  <span style={{ fontSize: 12, color: "#999" }}>Nenhuma tag</span>
)}
  </div>

  {/* Select para adicionar tag */}
    <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>

  <select
    onChange={async (e) => {
      const tagId = parseInt(e.target.value, 10);
      if (!tagId || !leadId) return;

      try {
        const res = await fetch("/crm-wly/lead_tags.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ lead_id: leadId, tag_id: tagId }),
        });

        const data = await res.json();
        // usa o que o PHP devolver (data.tag) ou cai no catálogo local
        const tagResp = data?.tag || tagsDisponiveis.find((t) => t.id === tagId);

        if (data?.success && tagResp) {
        setTagsLead(prev => {
          const jaExiste = prev.some(x => x.id === tagResp.id);
          return jaExiste ? prev : [...prev, { id: tagResp.id, nome: tagResp.nome, cor: tagResp.cor || "#666666" }];
        });
        await carregarTagsLead(leadId);
        bloqueiaPollingRef.current = true;
        await recarregarConversas(false, true);
        setTimeout(() => {
          bloqueiaPollingRef.current = false;
        }, 500); // meio segundo é suficiente


      } else {
        alert("Erro ao associar tag.");
      }

      } catch (err) {
        console.error("Erro ao associar tag:", err);
      }

      e.target.value = "";
    }}
    style={{ minWidth: "150px", maxWidth: "250px", padding: "6px 10px", borderRadius: "4px", fontSize: "0.85em" }}
  >
    <option value="">+ Adicionar tag</option>
    {tagsDisponiveis
      .filter((td) => !tagsLead.some((t) => t.id === td.id))
      .map((td) => (
        <option key={td.id} value={td.id}>
          {td.nome}
        </option>
      ))}
  </select>


<button
      onClick={abrirModalAcoes}
      style={{ ...btnPrimarySm, whiteSpace: "nowrap" }}  // não quebra "Ver Ações"
    >
      Ver Ações
    </button>

<button
  type="button"
  onClick={() => setMostrarModalArquivos(true)}
  title="Arquivos do lead (últimos 120 dias)"
  aria-label="Arquivos do lead"
  style={{
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: "transparent",
    color: "#000",
    border: "none",
    borderRadius: 8,
    padding: "8px 12px",
    cursor: "pointer"
  }}
>
  <FolderSearch size={18} />
  <span style={{ fontSize: 12, fontWeight: 600 }}>Arquivos</span>
</button>


  </div>


{exibirModalAcao && (
  <div
    className="popup-backdrop"
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 11001 }}
  >
    <div className="popup-content">
      <h3>Nova Ação</h3>

<p style={{
  background: "#fff3cd",
  color: "#856404",
  padding: "10px",
  borderRadius: "6px",
  fontSize: "14px",
  marginBottom: "15px",
  border: "1px solid #ffeeba"
}}>
  ⚠️ As ações criadas aqui servem apenas como lembrete no Dashboard ou pela 
  Listagem de Ações no menu <strong>Ações</strong>.  
  Elas <strong>não enviam mensagens automáticas</strong>.  
  Para envio automático, é necessário agendar a mensagem pela <strong>Listagem de Ações</strong> ou pela <strong>Listagem de Leads</strong>.
</p>


      <label>Descrição*</label>
      <textarea
        value={descricaoAcao}
        onChange={(e) => setDescricaoAcao(e.target.value)}
        rows={3}
        placeholder="Digite a descrição da ação"
      />

      <label>Data da Ação*</label>
      <input
        type="date"
        value={dataAcao}
        onChange={(e) => setDataAcao(e.target.value)}
      />
      <div className="botoes-envio">
        <button onClick={() => setExibirModalAcao(false)} className="cancelar">Cancelar</button>
        <button
          className="confirmar"
          disabled={salvandoAcao}
          onClick={async () => {
            if (!descricaoAcao || !dataAcao) {
              alert("Preencha todos os campos obrigatórios.");
              return;
            }

            setSalvandoAcao(true);
            const res = await fetch("/crm-wly/criar_acao.php", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                lead_id: leadId,
                descricao: descricaoAcao,
                data_acao: dataAcao,
                objecao: objecaoAcao
              })
            });

            const data = await res.json();
            if (data.success) {
              alert("Ação criada com sucesso!");
              setExibirModalAcao(false);
              setDescricaoAcao("");
              setDataAcao("");
              setObjecaoAcao("");
              if (mostrarModalAcoes) {
                carregarAcoes();
              }
            } else {
              alert(data.error || "Erro ao criar ação.");
            }


            setSalvandoAcao(false);
          }}
        >
          {salvandoAcao ? "Salvando..." : "Salvar"}
        </button>
      </div>
    </div>
  </div>
)}   
</div>


    )}
   

    <div
  className="chat-area"
  ref={(el) => {
    chatRef.current = el;
    chatAreaRef.current = el;
  }}
  key={telefone}
>


{/* Link para carregar mais antigas (mesmo estilo do balão da data) */}
{hasMoreOld && olderLinkDelayOk && mensagens.length > 0 && (
  <div className="older-holder">
    <div
      role="button"
      tabIndex={0}
      className={`data-divisor older-btn${fetchingOlder ? ' is-loading' : ''}`}
      aria-disabled={fetchingOlder}
      onClick={() => !fetchingOlder && handleLoadOlderClick()}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !fetchingOlder) {
          handleLoadOlderClick();
        }
      }}
    >
      {fetchingOlder ? 'Carregando…' : 'Ver mensagens mais antigas'}
    </div>
  </div>
)}



{(() => {
  let ultimaData = "";

  // calcula uma vez só
  const hoje = new Date();
  const hojeKey = dayKey(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()));
  const ontemDate = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 1);
  const ontemKey = dayKey(ontemDate);

  return mensagensVisiveis.map((msg, index) => {
    const dataMsg = dateFromServer(msg.data_envio);
    const msgKey = dayKey(new Date(dataMsg.getFullYear(), dataMsg.getMonth(), dataMsg.getDate()));


let rotuloData = "";
if (msgKey === hojeKey) {
  rotuloData = "Hoje";
} else if (msgKey === ontemKey) {
  rotuloData = "Ontem";
} else {
  rotuloData = dataMsg.toLocaleDateString("pt-BR");
}


  const mostrarDivisor = rotuloData !== ultimaData;
  ultimaData = rotuloData;

  let conteudo = (() => {

    // === LIGAÇÃO (CALL) ===
if (msg.tipo_midia === "call") {
  const texto = String(msg.mensagem || "").toLowerCase();

  const isMissed = texto.includes("perdida");
  const isVideo  = texto.includes("vídeo");

  const titulo = isVideo
    ? "Ligação de vídeo"
    : (isMissed ? "Ligação de voz perdida" : "Ligação de voz");

  const subtitulo = "Retorne as ligações no seu celular.";

  const Icone = isVideo
    ? Video
    : (isMissed ? PhoneMissed : Phone);

  return (
    <div className="call-card">
      <div
        className="call-icon"
        style={{ color: isMissed ? "#e53935" : "#4a4a4a" }}
      >
        <Icone size={18} />
      </div>

      <div className="call-texts">
        <div className="call-title">{titulo}</div>
        <div className="call-subtitle">{subtitulo}</div>
      </div>
    </div>
  );
}

    if (msg.tipo_midia === "imagem" && msg.midia_url) {
      return (
        <div style={{ display: "flex", flexDirection: "column", maxWidth: "300px" }}>
          <img
            src={msg.midia_url}
            alt="imagem"
            style={{ maxWidth: "100%", borderRadius: "8px", cursor: "zoom-in" }}
            onClick={() => setImagemModal(msg.midia_url)}
          />
          {msg.mensagem &&
            msg.mensagem !== `[Imagem recebida] ${msg.midia_url}` && (
              <span style={{ marginTop: "4px", fontSize: "14px", color: "#333" }}>
                <span
  dangerouslySetInnerHTML={{
    __html: parseWhatsAppText(msg.mensagem)
  }}
/>

              </span>
            )}
        </div>
      );
    }

// === CONTATO (vCard) ===
if (msg.tipo_midia === "contato") {
  // 1) tenta ler lista completa em midia_url (novo)
  let lista = null;
  try {
    if (typeof msg.midia_url === "string" && msg.midia_url.trim()) {
      const parsed = JSON.parse(msg.midia_url);
      if (Array.isArray(parsed)) lista = parsed;
    }
  } catch { /* noop */ }

  // 2) fallback: usa campos antigos (1 contato)
  const fallbackContato = (() => {
    let tels = [];
    try {
      if (typeof msg.contato_telefones === "string") {
        tels = JSON.parse(msg.contato_telefones || "[]");
      } else if (Array.isArray(msg.contato_telefones)) {
        tels = msg.contato_telefones;
      }
    } catch { /* noop */ }

    return [{
      displayName: msg.contato_nome || "(Sem nome)",
      phones: Array.isArray(tels) ? tels : [],
      vCard: (msg.contato_vcard && typeof msg.contato_vcard === "string") ? msg.contato_vcard : null
    }];
  })();

  const contatos = (lista && lista.length) ? lista : fallbackContato;

const getChatKey = (convOrPhone) => {
  if (!convOrPhone) return "";
  if (typeof convOrPhone === "string") {
    const phone = convOrPhone;
    return convStableKey({ telefone: phone, telefone_open: phone });
  }
  return convStableKey(convOrPhone);
};

const normalizeConversas = (convs, openedKey) => {
  const now = Date.now();

  return (Array.isArray(convs) ? convs : []).map((c) => {
    const k = getChatKey(c);

    // base do servidor
    let leitura = Number(c.leitura || 0);
    let unread = Number(c.unread_count || 0);

    // regra: leitura=1 significa "não lida" -> garante >=1
    if (leitura === 1) unread = Math.max(unread, 1);

    // conversa aberta não mostra badge
    if (k && openedKey && k === openedKey) {
      unread = 0;
      leitura = 0;
    }

    // ---- OVERRIDE temporário leitura/unread (LIDA / NÃO LIDA) ----
    const uo = unreadOverrideRef.current.get(k);
    if (uo && uo.until > now) {
      // ✅ NOVO: modo explícito (resolve seu item 1 da pergunta anterior)
      if (uo.mode === "force_unread") {
        leitura = 1;
        unread = Math.max(1, Number(uo.unread_count ?? unread ?? 1));
      } else if (uo.mode === "force_read") {
        leitura = 0;
        unread = 0;
      } else {
        // compat com overrides antigos
        if (typeof uo.leitura === "number") leitura = uo.leitura;
        if (typeof uo.unread_count === "number") unread = uo.unread_count;
      }

      // coerência final
      if (leitura === 0) unread = 0;
      if (leitura === 1) unread = Math.max(1, unread);

      // conversa aberta sempre zera
      if (k && openedKey && k === openedKey) {
        unread = 0;
        leitura = 0;
      }
    } else if (uo) {
      unreadOverrideRef.current.delete(k);
    }

    // ---- OVERRIDE temporário PIN ----
    let isPinned = Number(c.is_pinned || 0);
    const po = pinnedOverrideRef.current.get(k);
    if (po && po.until > now) {
      isPinned = po.value ? 1 : 0;
    } else if (po) {
      pinnedOverrideRef.current.delete(k);
    }

    // ---- OVERRIDE temporário ARCHIVE ----
    let isArchived = Number(c.is_archived || 0);
    const ao = archivedOverrideRef.current.get(k);
    if (ao && ao.until > now) {
      isArchived = ao.value ? 1 : 0;
    } else if (ao) {
      archivedOverrideRef.current.delete(k);
    }

    const tagsArr = Array.isArray(c.tags)
      ? c.tags.map((t) => ({
          id: Number(t.id),
          nome: String(t.nome || "").trim(),
          cor: t.cor || "#666666",
        }))
      : [];

    return {
      ...c,
      _key: k,
      _selected: !!(k && openedKey && k === openedKey),
      unread_count: unread,
      leitura,
      is_pinned: isPinned,
      is_archived: isArchived,
      lead_id: Number(c.lead_id_resolvido ?? c.lead_id_msg ?? c.lead_id ?? 0) || null,
      lead_nome: c.lead_nome || null,
      tags: tagsArr,
    };
  });
};

  // helper: gera data-uri p/ baixar vCard
  const makeVcardHref = (vcardText) =>
    vcardText ? ("data:text/vcard;charset=utf-8," + encodeURIComponent(vcardText)) : null;

  const formatTel = (t) => {
    const d = String(t || "").replace(/\D+/g, "");
    const display = d ? (d.startsWith("55") ? `+${d}` : `+${d}`) : String(t || "");
    const href = d ? `tel:+${d}` : `tel:${display}`;
    return { display, href };
  };

  return (
    <div className="contato-card">
      <div className="contato-card-header">
        <span className="contato-dot" /> Contato{contatos.length > 1 ? `s (${contatos.length})` : ""}
      </div>

      <div className="contato-card-body">
        {contatos.map((c, idx) => {
          const nomeC = (c?.displayName || c?.name || "(Sem nome)").trim() || "(Sem nome)";
          const tels = Array.isArray(c?.phones) ? c.phones : [];
          const vcardText = (c?.vCard && typeof c.vCard === "string") ? c.vCard : null;
          const vcardHref = makeVcardHref(vcardText);

          return (
            <div key={idx} className="contato-item">
              <div className="contato-nome">{nomeC}</div>

              {tels.length > 0 && (
                <ul className="contato-phones">
                  {tels.map((t, i) => {
                    const { display, href } = formatTel(t);
                    return (
                      <li key={i}>
                        <a href={href}>{display}</a>
                      </li>
                    );
                  })}
                </ul>
              )}

              <div className="contato-card-footer">
                {vcardHref ? (
                  <a
                    className="btn-mini"
                    download={(nomeC || "contato") + ".vcf"}
                    href={vcardHref}
                  >
                    Baixar vCard
                  </a>
                ) : (
                  <span className="contato-note">Sem vCard</span>
                )}
              </div>

              {idx < contatos.length - 1 && <div className="contato-sep" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}




    if (msg.tipo_midia === "sticker" && msg.midia_url) {
      return (
        <img
          src={msg.midia_url}
          alt="figurinha"
          style={{ width: "130px", height: "auto", borderRadius: "12px", marginTop: "5px" }}
        />
      );
    }

    if (msg.tipo_midia === "audio" && msg.midia_url) {
      let tipo = "audio/ogg";
      if (msg.midia_url.includes(".webm")) tipo = "audio/webm";
      else if (msg.midia_url.includes(".mp3")) tipo = "audio/mpeg";

      return (
        <audio controls style={{ width: "280px", borderRadius: "6px", backgroundColor: "#f1f1f1" }}>
          <source src={msg.midia_url} type={tipo} />
          Seu navegador não suporta áudio.
        </audio>
      );
    }

    if (msg.tipo_midia === "video" && msg.midia_url) {
  const texto = String(msg.mensagem || "").trim();

  // evita repetir "placeholder" do tipo: "[Vídeo recebido] URL"
  const isPlaceholder =
    !!texto &&
    !!msg.midia_url &&
    texto.includes(msg.midia_url) &&
    /^\s*\[.*(vídeo|video).*\]/i.test(texto);

  return (
    <div style={{ display: "flex", flexDirection: "column", maxWidth: "300px" }}>
      <video controls style={{ width: "100%", borderRadius: "8px" }}>
        <source src={msg.midia_url} type="video/mp4" />
        Seu navegador não suporta vídeo.
      </video>

      {!!texto && !isPlaceholder && (
        <div style={{ marginTop: "6px", fontSize: "14px", color: "#333" }}
          dangerouslySetInnerHTML={{ __html: parseWhatsAppText(texto) }}
        />
      )}
    </div>
  );
}


    if (msg.tipo_midia === "pdf" && msg.midia_url) {
      return (
        <a href={msg.midia_url} target="_blank" rel="noopener noreferrer" style={{ color: "#007bff" }}>
          📄 Abrir PDF
        </a>
      );
    }

    if (msg.tipo_midia === "documento" && msg.midia_url) {
      return (
        <a href={msg.midia_url} target="_blank" rel="noopener noreferrer" style={{ color: "#007bff" }}>
          📎 Baixar Documento
        </a>
      );
    }

    const match = msg.mensagem?.match(/\[(.*?)\]\s(https?:\/\/\S+)/);
    if (match) {
      const tipoTexto = match[1].toLowerCase();
      const url = match[2];

      if (tipoTexto.includes("áudio")) {
        return (
          <audio
            src={url}
            controls
            style={{ width: "280px", borderRadius: "6px", backgroundColor: "#f1f1f1" }}
          />
        );
      } else if (tipoTexto.includes("imagem")) {
        return (
          <img
            src={url}
            alt="Imagem recebida"
            style={{ maxWidth: "100%", borderRadius: "6px", cursor: "zoom-in" }}
            onClick={() => setImagemModal(url)}
          />
        );
      } else if (tipoTexto.includes("pdf")) {
        return (
          <a href={url} target="_blank" rel="noopener noreferrer">
            📄 Abrir PDF
          </a>
        );
      } else if (tipoTexto.includes("vídeo")) {
        return (
          <video controls width="300">
            <source src={url} type="video/mp4" />
          </video>
        );
      } else {
        return (
          <a href={url} target="_blank" rel="noopener noreferrer">
            📎 Baixar Arquivo
          </a>
        );
      }
    }

    if (msg.mensagem?.includes("🔹") || msg.mensagem?.includes("🔗")) {
      return <div style={{ whiteSpace: "pre-line", fontSize: "14px" }}>{msg.mensagem}</div>;
    }

    return <span style={{ whiteSpace: "pre-line" }}>{msg.mensagem}</span>;
  })();

  const isDeleted = String(msg.mensagem || '')
    .trim()
    .toLowerCase() === 'mensagem apagada';

  const quotedMsg = msg.reply_to_zapi_id
    ? mensagens.find((m) => m.id_mensagem_zapi === msg.reply_to_zapi_id)
    : null;

  const quotedBox = quotedMsg ? (
    <div
      onClick={() => {
        const el = document.querySelector(
          `[data-zapi-id="${quotedMsg.id_mensagem_zapi}"]`
        );
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }}
      style={{
        background: "#f4f6f8",
        borderLeft: "3px solid #8aa0ff",
        borderRadius: 6,
        padding: "6px 8px",
        marginBottom: 6,
        fontSize: 12,
        color: "#333",
        cursor: "pointer",
        maxWidth: 320,
      }}
      title="Ir para a mensagem citada"
    >
      <span
  dangerouslySetInnerHTML={{
    __html: parseWhatsAppText(previewDaMensagem(quotedMsg))
  }}
/>

    </div>
  ) : null;

return (
  <React.Fragment key={msg.id || index}>
    {mostrarDivisor && <div className="data-divisor">{rotuloData}</div>}

    <div
      className={`mensagem-row ${msg.tipo === "saida" ? "saida" : "entrada"}`}
      onDoubleClick={(e) => {
        if (!msg?.id_mensagem_zapi) return;

        if (
          e.target.closest(".mensagem") ||
          e.target.closest(".reply-actions") ||
          e.target.closest(".msg-actions-wrap") ||
          e.target.closest(".msg-actions-dropdown") ||
          e.target.closest(".emoji-reaction-pop") ||
          e.target.closest(".emoji-btn") ||
          e.target.closest(".msg-more-btn")
        ) {
          return;
        }

        abrirReplyDaMensagem(msg);
      }}
    >
<div
  className={`mensagem ${msg.tipo === "saida" ? "saida" : "entrada"}`}
  data-msg-idx={index}
  id={`msg-${index}`}
  data-zapi-id={msg.id_mensagem_zapi || undefined}
  onDoubleClick={(e) => e.stopPropagation()}
>

      {(() => {
        const isDeleted = String(msg.mensagem || "")
          .trim()
          .toLowerCase() === "mensagem apagada";

        return (
          <>

  {msg.id_mensagem_zapi && !isDeleted && (
    <div className="msg-actions-wrap">
    <button
      type="button"
      className="msg-more-btn"
      title="Mais ações"
      aria-label="Mais ações"
      onClick={(e) => {
        e.stopPropagation();
        setMsgMenuOpenFor((prev) =>
          prev === msg.id_mensagem_zapi ? null : msg.id_mensagem_zapi
        );
      }}
    >
      <ChevronDown size={10} />
    </button>

    {msgMenuOpenFor === msg.id_mensagem_zapi && (
  <div
    className="msg-actions-dropdown"
    onClick={(e) => e.stopPropagation()}
  >
    {msg.tipo === "saida" && (
      <>
        <button
          type="button"
          className="msg-actions-item"
          onClick={() => {
            setMsgMenuOpenFor(null);
            onDeleteMessageClick(msg);
          }}
        >
          <Trash2 size={16} />
          <span>Apagar</span>
        </button>

        <button
          type="button"
          className="msg-actions-item"
          onClick={() => startEditMessage(msg)}
          disabled={!!msg.tipo_midia && msg.tipo_midia !== "texto"}
        >
          <Pencil size={16} />
          <span>Editar</span>
        </button>
      </>
    )}

    <button
      type="button"
      className="msg-actions-item"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(String(msg.mensagem || ""));
          setMsgMenuOpenFor(null);
        } catch (err) {
          console.error("Erro ao copiar mensagem:", err);
          alert("Não foi possível copiar a mensagem.");
        }
      }}
      disabled={!String(msg.mensagem || "").trim()}
    >
      <Copy size={16} />
      <span>Copiar</span>
    </button>

    <button
      type="button"
      className="msg-actions-item"
      onClick={() => {
        setMsgMenuOpenFor(null);
        setReplyDraft({
          id: msg.id_mensagem_zapi,
          preview: previewDaMensagem(msg),
        });
        requestAnimationFrame(() => composerRef.current?.focus());
      }}
    >
      <CornerUpLeft size={16} />
      <span>Responder</span>
    </button>

    <button
      type="button"
      className="msg-actions-item"
      onClick={async () => {
        setMsgMenuOpenFor(null);
        setFwdMessage(msg);
        await carregarLeadsCompletos();
        setFwdOpen(true);
      }}
    >
      <CornerUpRight size={16} />
      <span>Encaminhar</span>
    </button>

    <button
      type="button"
      className="msg-actions-item"
      onClick={() => {
        setMsgMenuOpenFor(null);
        setEmojiMenuFor(
          emojiMenuFor === msg.id_mensagem_zapi ? null : msg.id_mensagem_zapi
        );
      }}
    >
      <Smile size={16} />
      <span>Reagir</span>
    </button>
  </div>
)}
  </div>
  
)}

            {/* Nome do remetente (em grupos, mensagens de entrada) */}
            {Number(msg.is_group) === 1 && msg.tipo === "entrada" && (
              <div className="remetente-grupo">
                {msg.remetente_nome || msg.remetente_numero || "Participante"}
              </div>
            )}

            {/* Flag "Encaminhada" */}
            {Number(msg.is_encaminhada) === 1 && (
              <div className="fwd-flag">
                <CornerUpRight size={14} strokeWidth={2} />
                <em>Encaminhada</em>
              </div>
            )}

            {/* Caixa de citação, se houver */}
            {quotedBox}

            {/* Conteúdo da mensagem */}
            {isDeleted ? (
              <span
                className="texto"
                style={{
                  opacity: 0.85,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                 <Ban size={16} />
                Mensagem apagada
              </span>
            ) : (
  (!msg.tipo_midia || msg.tipo_midia === "texto") ? (
    <span
      className="texto"
      onClick={handleMessageTextClick}
      dangerouslySetInnerHTML={{
    __html: parseWhatsAppText(msg.mensagem || "")
      }}
    />
  ) : (
    <div className="texto">{conteudo}</div>
  )
)}


            {/* Horário + checks */}
<div className="horario">
  {msg.editada === 1 || msg.editada === true || Number(msg.editada) === 1 ? (
    <span className="msg-edited-label">Editada</span>
  ) : null}

  {dateFromServer(msg.data_envio).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  })}

  {msg.tipo === "saida" && (
    <>
      {msg.status_envio === "sucesso" ? (
        <CheckCheck size={14} strokeWidth={2} className="check-icon" />
      ) : (
        <Clock4 size={14} strokeWidth={2} className="check-icon spin" />
      )}
    </>
  )}
</div>

            {/* Reações */}
            {Array.isArray(msg.reactions) && msg.reactions.length > 0 && (
              <div
                className={`reactions-bubble ${
                  msg.tipo === "saida" ? "saida" : "entrada"
                }`}
              >
                {Object.entries(
                  msg.reactions.reduce((acc, r) => {
                    if (!r || !r.reaction) return acc;
                    acc[r.reaction] = (acc[r.reaction] || 0) + 1;
                    return acc;
                  }, {})
                ).map(([emoji, count]) => (
                  <span key={emoji} className="reaction-item">
                    {emoji}
                    {count > 1 ? ` ${count}` : ""}
                  </span>
                ))}
              </div>
            )}

            {/* Ações (reply + reagir) */}
{msg.id_mensagem_zapi && (
  <div className={`reply-actions ${emojiMenuFor === msg.id_mensagem_zapi ? "is-open" : ""}`}>
    {msg.tipo !== "saida" && (
      <>
        <button
          type="button"
          className="reply-btn"
          title="Responder"
          onClick={() => {
            setReplyDraft({
              id: msg.id_mensagem_zapi,
              preview: previewDaMensagem(msg),
            });
            requestAnimationFrame(() => composerRef.current?.focus());
          }}
        >
          <CornerUpLeft size={18} />
        </button>

        <button
          type="button"
          className="reply-btn"
          title="Encaminhar"
          onClick={async () => {
            setFwdMessage(msg);
            setFwdLeadIds([]);
            setFwdFilter("");
            await carregarLeadsCompletos();
            setFwdOpen(true);
          }}
        >
          <CornerUpRight size={18} />
        </button>
      </>
    )}

    <button
      type="button"
      className="reply-btn"
      title="Reagir"
      onClick={() =>
        setEmojiMenuFor(
          emojiMenuFor === msg.id_mensagem_zapi ? null : msg.id_mensagem_zapi
        )
      }
    >
      <Smile size={18} />
    </button>

    {emojiMenuFor === msg.id_mensagem_zapi && (
      <div className="emoji-reaction-pop">
        {["👍", "❤️", "😂", "😮", "😢", "🙏"].map((em) => (
          <button
            key={em}
            className="emoji-btn"
            onClick={() => enviarOuRemoverReacao(msg.id_mensagem_zapi, em)}
          >
            {em}
          </button>
        ))}
        <button
          className="emoji-btn remover"
          title="Remover reação"
          onClick={() => enviarOuRemoverReacao(msg.id_mensagem_zapi, null)}
        >
          ⌫
        </button>
      </div>
    )}
  </div>
)}
          </>
        );
      })()}
    </div>
      </div>
    
  </React.Fragment>
);


        });
      })()}
      <div ref={chatEndRef} />
    </div>

{!carregandoInicial && telefone && (
  whatsappBloqueiaEnvio ? (
   <div className="composer-offline-shell">
  <div className="composer-offline-card">
    <div className="composer-offline-textos">
      <strong>WhatsApp desconectado do Nórea</strong>
      <span>
        Conecte novamente sua instância para voltar a enviar mensagens por este chat.
      </span>
    </div>

    <Link
      to="/wzp/painel-whatsapp"
      className="composer-offline-btn"
      onClick={(e) => e.stopPropagation()}
    >
      CONECTAR
    </Link>
  </div>
</div>
  ) : (
      <div className="formwzp-envio">
        <div className="composer">
          {/* Balão de resposta FIXO acima do input */}
          {replyDraft && (
            <div className="reply-draft">
              <div className="reply-draft-content">
                <CornerUpLeft size={16} strokeWidth={2} />
                <div className="reply-draft-text">
                  <div className="reply-draft-title">Respondendo mensagem</div>
                  <div className="reply-draft-preview"><span
  dangerouslySetInnerHTML={{
    __html: parseWhatsAppText(replyDraft.preview)
  }}
/>
</div>
                </div>
              </div>
              <button
                type="button"
                className="reply-draft-close"
                onClick={clearReply}
                aria-label="Cancelar resposta"
                title="Cancelar resposta (Esc)"
              >
                ×
              </button>
            </div>
          )}

          {editDraft && (
  <div className="reply-draft">
    <div className="reply-draft-content">
      <Pencil size={16} strokeWidth={2} />
      <div className="reply-draft-text">
        <div className="reply-draft-title">Editando mensagem</div>
        <div className="reply-draft-preview">
          <span
            dangerouslySetInnerHTML={{
              __html: parseWhatsAppText(editDraft.original || "")
            }}
          />
        </div>
      </div>
    </div>
    <button
      type="button"
      className="reply-draft-close"
      onClick={clearEditDraft}
      aria-label="Cancelar edição"
      title="Cancelar edição"
    >
      ×
    </button>
  </div>
)}

       {/* Anexo ou spinner */}
{uploading ? (
  <div className="spinner-envio" title="Enviando...">
    <Clock4 className="icone spin" size={20} strokeWidth={2} />
  </div>
) : (
  <>
    {/* === Clip + menu === */}
    <div ref={clipRef} className="clip-wrapper">
      <button
        type="button"
        className="btn-clip"
        title="Anexar"
        aria-haspopup="menu"
        aria-expanded={clipOpen}
        onClick={() => setClipOpen(v => !v)}
        disabled={!!replyDraft || !!editDraft}
      >
        <Paperclip {...ICON_CLIP} className="lucide clip-icon" />
      </button>

      {clipOpen && !replyDraft && (
        <div className="clip-menu" role="menu">
          <button
            type="button"
            className="clip-item"
            onClick={() => {
              setClipOpen(false);
              document.getElementById("file-doc-input")?.click();
            }}
          >
            📎 Documento
          </button>

          <button
            type="button"
            className="clip-item"
            onClick={() => {
              setClipOpen(false);
              document.getElementById("file-media-input")?.click();
            }}
          >
            🖼️ Fotos e vídeos
          </button>

          <button
            type="button"
            className="clip-item"
            onClick={async () => {
              setClipOpen(false);
              await carregarLeadsCompletos();
              setContactOpen(true);
            }}
          >
            👤 Contato
          </button>
        </div>
      )}
    </div>

    {/* Inputs escondidos acionados pelo menu do clipe */}
    <input
  id="file-doc-input"
  type="file"
  accept=".pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  style={{ display: "none" }}
  onChange={async (e) => {
    const f = e.target.files?.[0];
    if (f) await handleSendFile(f);
    e.target.value = "";
  }}
/>

    <input
      id="file-media-input"
      type="file"
      accept="image/*,video/*,audio/*"
      style={{ display: "none" }}
      onChange={async (e) => {
        const f = e.target.files?.[0];
        if (f) await handleSendFile(f);
        e.target.value = "";
      }}
    />

    {/* Emoji */}
    <div style={{ position: "relative" }}>
      <button
        className="btn-emoji"
        type="button"
        onClick={() => setEmojiPickerOpen(v => !v)}
        title="Inserir emoji"
      >
        <SmilePlus {...ICON_EMOJI} className="lucide emoji-icon" />
      </button>

      {emojiPickerOpen && (
        <div
          className="teste"
          style={{ position: "absolute", bottom: "40px", left: "0", zIndex: 1000 }}
        >
          <EmojiPicker onEmojiClick={onEmojiClick} autoFocusSearch={false} theme="light" />
        </div>
      )}
    </div>

    {/* Textarea / Composer */}
<Composer
  ref={composerRef}
  disabled={uploading}
  onSend={enviarMensagemComAtendente}
  fetchQuickReplies={fetchQuickRepliesCb}
  onQuickReplySelect={enviarRespostaRapida}
  isGroup={isGroup}
  fetchMentionParticipants={fetchMentionParticipants}
  inputClassName="composer-input"
  popupClassNames={{
    container: "rr-popup",
    list: "rr-list",
    item: "rr-item",
    itemAtivo: "is-active",
    titulo: "rr-item-title",
    corpo: "rr-item-body",
  }}
/>


<div style={{ position: "relative" }}>
<div className="atendente-composer-wrap">
  <button
    className={`btn-resposta-rapida btn-atendente-composer ${
      atendenteSelecionado ? "is-active" : ""
    }`}
    type="button"
    title={
      atendenteSelecionado
        ? `Atendente: ${atendenteSelecionado.nome}`
        : "Selecionar atendente"
    }
    onClick={() => {
      setModalAtendenteOpen(true);
      setShowTooltipAtendente(false);
      localStorage.setItem("norea_tooltip_atendente_v1", "1");
    }}
  >
    {atendenteSelecionado ? (
      <UserCheck size={18} className="zap-icon" />
    ) : (
      <UserRound size={18} className="zap-icon" />
    )}
  </button>

{showTooltipAtendente && (
  <div className="coachmark-atendente">
    <div className="coachmark">
      <div className="coachmark-arrow atendente-arrow" />

      <div className="coachmark-title">NOVO RECURSO</div>

      <div className="coachmark-text">
        Agora você pode identificar qual atendente está respondendo no WhatsApp.
      </div>

      <button
        type="button"
        className="coachmark-button"
        onClick={() => {
          setShowTooltipAtendente(false);
          localStorage.setItem("norea_tooltip_atendente_v1", "1");
        }}
      >
        Entendi
      </button>
    </div>
  </div>
)}
</div>
</div>

    {/* Botão RR */}
<button
  className="btn-resposta-rapida"
  type="button"
  title="Gerenciar respostas rápidas"
  onClick={() => navigate("/wzp/respostas-rapidas")}
>
  <Zap size={18} className="zap-icon" />
</button>

{/* Botão Agendar */}
<button
  className="btn-resposta-rapida"
  type="button"
  title="Agendar mensagem"
  onClick={() => {
     setMostrarModalAgendamento(true);
  }}
>
  <ClockPlus size={18} className="zap-icon" />
</button>

    {/* (LEGADO) Input file escondido original — mantido para compatibilidade */}
    <input
      id="file-upload"
      type="file"
      accept="image/*,.pdf,audio/*"
      style={{ display: "none" }}
      disabled={!!replyDraft}
      onChange={async (e) => {
        const file = e.target.files?.[0];
        if (!file || !telefone) return;

        const formData = new FormData();
        formData.append("arquivo", file);
        formData.append("telefone", toBackendPhone(telefone));
        formData.append("lead_id", leadId);

        try {
          setUploading(true);
          setLoading(true);
          const res = await fetch("/crm-wly/api/whatsapp/enviar_arquivo.php", {
            method: "POST",
            body: formData,
            credentials: "include",
          });
          const data = await res.json();
          if (data.success) {
            const novo = {
              tipo: "saida",
              mensagem: data.mensagem,
              tipo_midia: data.tipo_midia || null,
              midia_url: data.midia_url || data.url || null,
              id_mensagem_zapi: data.id_mensagem_zapi || null,
              data_envio: new Date().toISOString(),
              status_envio: "sucesso",
            };
            setMensagens(prev => dedupeMensagens([...(prev || []), novo]));
          }
        } catch (err) {
          console.error("Erro ao enviar arquivo:", err);
        } finally {
          setUploading(false);
          setLoading(false);
          e.target.value = "";
        }
      }}
    />

   {/* Mic / Gravação */}
{gravando ? (
  <>
    <button
      type="button"
      className="composer-action-btn composer-action-trash"
      title="Descartar gravação"
      onClick={cancelarGravacao}
    >
      <Trash2 size={18} strokeWidth={2} />
    </button>

    <div className="gravacao-timer">
      <span className="gravacao-rec-dot" />
      {formatarTempoGravacao(gravacaoSegundos)}
    </div>
  </>
) : (
  <Mic
    {...ICON}
    className="lucide mic-icon"
    title={replyDraft ? "Gravação desabilitada durante reply" : "Gravar áudio"}
    onClick={!replyDraft ? iniciarGravacao : undefined}
  />
)}

{modalAtendenteOpen && (
  <div className="pm-modal-backdrop" onClick={() => setModalAtendenteOpen(false)}>
    <div
      className="pm-modal atendente-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="pm-modal-header">
        <h3 style={{ margin: 0 }}>Atendente da mensagem</h3>

        <button
          className="pm-modal-close"
          onClick={() => setModalAtendenteOpen(false)}
          title="Fechar"
        >
          ×
        </button>
      </div>

      <div className="pm-modal-body">
        <label className="atendente-label">Selecionar atendente</label>

        <div className="atendente-select-row">
          <select
            className="atendente-select"
            value={atendenteSelecionado?.id || ""}
            onChange={(e) => {
              const id = Number(e.target.value || 0);
              const escolhido = atendentes.find((a) => Number(a.id) === id) || null;
              setAtendenteSelecionado(escolhido);
            }}
          >
            <option value="">Sem identificação</option>

            {atendentes.map((a) => (
              <option key={a.id} value={a.id}>
                {a.nome}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="atendente-add-btn"
            title="Cadastrar atendente"
            onClick={() => {
              setModalAtendenteOpen(false);
              setModalNovoAtendenteOpen(true);
            }}
          >
            <UserPlus size={20} />
          </button>
        </div>

{atendenteSelecionado?.nome && (
  <div className="atendente-preview">
    <button
      type="button"
      className="atendente-preview-remove"
      title="Remover identificação"
      onClick={() => setAtendenteSelecionado(null)}
    >
      ×
    </button>

    <div>
      As próximas mensagens sairão como:<br />
      <code>{atendenteSelecionado.nome}</code>
    </div>
  </div>
)}
      </div>

<div className="pm-modal-footer atendente-footer-unico">
<button
  type="button"
  className="pm-btn-primary"
  onClick={() => {
    setModalAtendenteOpen(false);

    requestAnimationFrame(() => {
      composerRef.current?.focus?.();

      setTimeout(() => {
        composerRef.current?.focus?.();
      }, 0);
    });
  }}
>
  Confirmar
</button>
</div>
    </div>
  </div>
)}

{modalNovoAtendenteOpen && (
  <div className="pm-modal-backdrop" onClick={() => setModalNovoAtendenteOpen(false)}>
    <div
      className="pm-modal atendente-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="pm-modal-header">
        <h3 style={{ margin: 0 }}>Novo atendente</h3>

        <button
          className="pm-modal-close"
          onClick={() => setModalNovoAtendenteOpen(false)}
          title="Fechar"
        >
          ×
        </button>
      </div>

      <div className="pm-modal-body">
        <label className="atendente-label">Nome do atendente</label>

        <input
          className="atendente-input"
          type="text"
          maxLength={80}
          value={novoAtendenteNome}
          onChange={(e) => setNovoAtendenteNome(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") salvarNovoAtendente();
            if (e.key === "Escape") setModalNovoAtendenteOpen(false);
          }}
          placeholder="Ex: Maria da Silva"
          autoFocus
        />

        <div className="atendente-lista-cadastrados">
  <div className="atendente-lista-titulo">Atendentes cadastrados</div>

  {atendentes.length === 0 ? (
    <div className="atendente-lista-vazio">
      Nenhum atendente cadastrado.
    </div>
  ) : (
    atendentes.map((a) => (
      <div key={a.id} className="atendente-lista-item">
        <span>{a.nome}</span>

        <button
          type="button"
          className="atendente-lista-excluir"
          title={`Excluir ${a.nome}`}
          onClick={() => excluirAtendente(a)}
        >
          ×
        </button>
      </div>
    ))
  )}
</div>

      </div>

      <div className="pm-modal-footer atendente-footer-duplo">
        <button
          type="button"
          className="pm-btn-cancel"
          onClick={() => {
            setModalNovoAtendenteOpen(false);
            setModalAtendenteOpen(true);
          }}
        >
          Voltar
        </button>

        <button
          type="button"
          className="pm-btn-primary"
          disabled={salvandoAtendente}
          onClick={salvarNovoAtendente}
        >
          {salvandoAtendente ? "Salvando..." : "Salvar"}
        </button>
      </div>
    </div>
  </div>
)}


    {/* Modal de envio de contato */}
    {contactOpen && (
<SelectLeadModal
  leads={fwdDestinos.filter((d) => Number(d.is_group) !== 1)}
    onClose={() => setContactOpen(false)}
    onPick={(lead) => {
      // monta vCard e envia
      const vcard = buildVCardFromLead(lead);
      const tels = [String(lead.telefone || "")].filter(Boolean);
      handleSendContact({
        nome: lead.nome || "Contato",
        telefones: tels,
        vcardText: vcard,
      });
      setContactOpen(false);
    }}
  />
)}

  </>
)}

                    
<Send
  {...ICON}
  className="lucide send-icon"
  onClick={() => {
    if (loading || uploading) return;

    if (gravando) {
      pararGravacao();
      return;
    }

    composerRef.current?.send();
  }}
  title={gravando ? "Enviar áudio" : "Enviar"}
  style={{ marginRight: 10, transform: "rotate(45deg)" }}
/>






              </div>
            </div>

)
          )}

           {fwdOpen && (
  <div className="fwd-backdrop" onClick={() => setFwdOpen(false)}>
    <div className="fwd-modal" onClick={(e)=>e.stopPropagation()}>
      <div className="fwd-header">
        <h3>Encaminhar mensagem</h3>
        <button type="button" className="fwd-close" onClick={() => { setFwdOpen(false); setFwdLeadIds([]); setFwdSending(false); }} title="Fechar">
          <XIcon size={18}/>
        </button>
      </div>
      {/* prévia em balão */}
      <div className="fwd-preview">
        <div className="mensagem entrada">
          <div className="texto">
            {(!fwdMessage?.tipo_midia || fwdMessage.tipo_midia === "texto") ? (
  <span
    dangerouslySetInnerHTML={{
      __html: parseWhatsAppText(fwdMessage?.mensagem || "")
    }}
  />
) : (
  `[${
    fwdMessage.tipo_midia === "imagem"    ? "🖼️ imagem"   :
    fwdMessage.tipo_midia === "audio"     ? "🎤 áudio"     :
    fwdMessage.tipo_midia === "video"     ? "🎞️ vídeo"    :
    fwdMessage.tipo_midia === "pdf"       ? "📄 PDF"       :
    fwdMessage.tipo_midia === "documento" ? "📎 documento" :
    fwdMessage.tipo_midia === "sticker"   ? "🌟 figurinha" :
    fwdMessage.tipo_midia
  }]`
)}

          </div>

        </div>
      </div>

      <label className="fwd-label">Enviar para contatos e grupos:</label>

      {/* linha da busca + botão enviar */}
      <div className="fwd-toolbar">
        <input
          className="fwd-search"
          placeholder="Buscar por nome ou telefone…"
          value={fwdFilter}
          onChange={e=>setFwdFilter(e.target.value)}
        />
        <button
          type="button"
          className="fwd-send-btn"
          title={fwdSending ? 'Enviando…' : 'Encaminhar'}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            encaminharParaSelecionados();
          }}
          disabled={fwdSending || fwdLeadIds.length === 0}
        >
          <Send size={20} style={{ transform: 'rotate(45deg)' }} />
        </button>
      </div>

      {/* lista com checkbox */}
      <div className="fwd-list">
        {Array.from(
  new Map(
    fwdDestinos.map((ld) => {
      const isGrupo = Number(ld.is_group) === 1;

      const key = isGrupo
        ? `grupo:${String(ld.telefone || "").replace(/@g\.us$/i, "")}`
        : Number(ld.id) > 0
          ? `lead:${Number(ld.id)}`
          : `tel:${String(ld.telefone || "").replace(/\D+/g, "")}`;

      return [key, ld];
    })
  ).values()
)
  .filter((ld) => {
    const normalizar = (v) =>
      String(v || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();

    const q = normalizar(fwdFilter);
    const qDigits = String(fwdFilter || "").replace(/\D+/g, "");

    if (!q && !qDigits) return true;

    const nome = normalizar(ld.nome);
    const telefone = String(ld.telefone || "").replace(/\D+/g, "");

return (
  (q && nome.includes(q)) ||
  (qDigits && telefone.includes(qDigits))
);
  })
  .map((ld) => {
            const checked = fwdLeadIds.includes(String(ld.id));
            return (
              <label key={ld.id} className="fwd-item">
                <input
                  type="checkbox"
                  className="fwd-check"
                  checked={checked}
                  onChange={(e) => {
                    setFwdLeadIds((prev) =>
                      e.target.checked
                        ? [...prev, String(ld.id)]
                        : prev.filter((id) => id !== String(ld.id))
                    );
                  }}
                />

                <span className="fwd-item-name">
                  {ld.is_group ? "👥 " : ""}
                  {ld.nome || "(sem nome)"}
                </span>

                <span className="fwd-item-phone">
                  {ld.is_group ? "Grupo" : ld.telefone}
                </span>
              </label>
            );
          })}
      </div>
    </div>
  </div>
)}


{prefsOpen && (
  <div className="pm-prefs-overlay" onClick={() => setPrefsOpen(false)}>
    <div className="pm-prefs-modal" onClick={(e) => e.stopPropagation()}>
      <div className="pm-prefs-head">
        <div className="pm-prefs-title">Configurações</div>

        <button
          type="button"
          className="pm-prefs-close"
          onClick={() => setPrefsOpen(false)}
          aria-label="Fechar"
        >
          <XIcon size={18} />
        </button>
      </div>


      <div className="pm-prefs-body">
        {!!prefsError && <div className="pm-prefs-error">{prefsError}</div>}

        <div className="pm-prefs-row">
          <div className="pm-prefs-rowtext">
            <div className="pm-prefs-rowtitle">Manter conversas arquivadas</div>
            <div className="pm-prefs-rowdesc">
              Se desligado, conversas arquivadas voltam para a lista principal quando chegarem mensagens não lidas.
            </div>
          </div>

          {/* ✅ não renderiza até carregar -> evita “liga e depois desliga” */}
          {manterArquivadas !== null && (
            <label className="switch-toggle pm-prefs-switch" title="Manter arquivadas com não lidas">
              <input
                type="checkbox"
                checked={Number(manterArquivadas) === 1}
                disabled={prefsLoading}
                onChange={(e) => salvarManterArquivadas(e.target.checked ? 1 : 0)}
              />
              <span className="slider" />
            </label>
          )}
        </div>
      </div>
    </div>
  </div>
)}



    {/* Modal da imagem */}
    {imagemModal && (
      <div
        onClick={() => setImagemModal(null)}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0,0,0,0.8)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          cursor: "zoom-out",
        }}
      >
        <img
          src={imagemModal}
          alt="Imagem ampliada"
          style={{
            width: "auto",
            height: "auto",
            maxHeight: "95vh",
            maxWidth: "95vw",
            borderRadius: "8px",
            boxShadow: "0 0 30px rgba(0,0,0,0.6)",
            objectFit: "contain",
          }}
        />
      </div>
    )}

    <ModalListaAcoes
      visible={mostrarModalAcoes}
      onClose={fecharModalAcoes}
      acoes={acoes}
      carregandoAcoes={carregandoAcoes}
      editandoId={editandoId}
      edicaoBuffer={edicaoBuffer}
      setEdicaoBuffer={setEdicaoBuffer}
      setEditandoId={setEditandoId}
      iniciarEdicao={iniciarEdicao}
      salvarEdicao={salvarEdicao}
      excluirAcao={excluirAcao}
      toggleStatus={toggleStatus}
      togglingId={togglingId}
      setExibirModalAcao={setExibirModalAcao}
      formatPtBr={formatPtBr}
    />
  </div>

<ModalArquivos
  visible={mostrarModalArquivos && !!leadId}
  onClose={() => setMostrarModalArquivos(false)}
  leadId={leadId}
/>

  {/* Lightbox de imagem com 2º zoom */}
<ZoomLightbox
  open={!!imagemModal}
  src={imagemModal}
  onClose={() => setImagemModal(null)}
  maxScale={2.6}
/>

{mostrarModalNovaFuncaoEditar && (
  <div
    className="popup-backdrop popup-backdrop-nova-funcao"
    onClick={fecharModalNovaFuncaoEditar}
    style={{ zIndex: 12000 }}
  >
    <div
      className="popup-content popup-nova-funcao-editar tooltip-like"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="popup-nova-funcao-badge">NOVA FUNÇÃO</div>

      <h3 className="popup-nova-funcao-title">Editar mensagens</h3>

      <p className="popup-nova-funcao-text">
        Agora você pode editar o nome do lead diretamente pelo topo da conversa.
      </p>

      <div className="popup-nova-funcao-gif-wrap">
        <video
          className="popup-nova-funcao-gif"
          autoPlay
          loop
          muted
          playsInline
        >
          <source
            src="https://welaunchyou.com.br/crm-wly/uploads/gifs/nova-funcao-editar.mp4"
            type="video/mp4"
          />
        </video>
      </div>

      <div className="popup-nova-funcao-footer">
        <button
          className="popup-nova-funcao-btn"
          type="button"
          onClick={fecharModalNovaFuncaoEditar}
        >
          Entendi
        </button>
      </div>
    </div>
  </div>
)}

{mostrarModalAgendamento && (
  <div className="popup-backdrop" onClick={() => setMostrarModalAgendamento(false)}>
    <div className="popup-content" onClick={(e) => e.stopPropagation()}>
      <h3>Agendar mensagem</h3>
      <p>
       <strong>{isGrupoHeader ? "Grupo:" : "Lead:"}</strong>{" "}
                {isGrupoHeader ? grupoTitulo : (nomeLeadTopo || "Lead selecionado")}  
      </p>

      <div style={{ position: "relative", marginBottom: 12, paddingBottom: 8 }}>
        <textarea
          ref={msgAgendamentoRef}
          value={mensagemFila}
          onChange={(e) => setMensagemFila(e.target.value)}
          rows={6}
          placeholder="Digite a mensagem"
          style={{
            width: "100%",
            boxSizing: "border-box",
            borderRadius: 8,
            padding: "12px 44px 12px 12px",
            fontSize: 15,
            border: "1px solid #ccc",
            backgroundColor: "#ece5dd",
          }}
        />

        <button
          type="button"
          onClick={() => setMostrarEmojiPickerAgendamento((prev) => !prev)}
          style={{
            position: "absolute",
            bottom: 12,
            right: 12,
            background: "none",
            border: "none",
            fontSize: 20,
            lineHeight: 1,
            cursor: "pointer"
          }}
          aria-label="Inserir emoji"
        >
          😊
        </button>

        {mostrarEmojiPickerAgendamento && (
          <div style={{ position: "absolute", top: "100%", right: 0, zIndex: 1000 }}>
            <EmojiPicker
              onEmojiClick={({ emoji }) => {
                setMensagemFila((prev) => prev + emoji);
                setMostrarEmojiPickerAgendamento(false);
              }}
              autoFocusSearch={false}
              height={350}
              width={300}
              theme="light"
            />
          </div>
        )}
      </div>

      <div className="form-group" style={{ marginBottom: 16 }}>
        <label htmlFor="fileUploadAgendamento">Anexar arquivo:</label>
        <input
          id="fileUploadAgendamento"
          type="file"
          accept="image/*,.pdf,audio/*,video/*"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;

            const formData = new FormData();
            formData.append("arquivo", file);

            setUploadEmAndamento(true);

            try {
              const res = await fetch("/crm-wly/api/whatsapp/upload_temp.php", {
                method: "POST",
                body: formData,
                credentials: "include",
              });

              const data = await res.json();

              if (data.success) {
                setArquivoUrl(data.url);
                setTipoMidia(data.tipo_midia);
                alert("Arquivo pronto para envio.");
              } else {
                alert("Erro ao processar o arquivo: " + (data.message || ""));
              }
            } catch (err) {
              console.error(err);
              alert("Erro ao enviar arquivo.");
            } finally {
              setUploadEmAndamento(false);
            }
          }}
        />
      </div>

      <div className="agendamento-section">
        <label>Agendar envio para:</label>
        <div style={{ display: "flex", gap: 10 }}>
          <input
            type="date"
            value={dataAgendamento}
            onChange={(e) => setDataAgendamento(e.target.value)}
          />
          <input
            type="time"
            value={horaAgendamento}
            onChange={(e) => setHoraAgendamento(e.target.value)}
          />
        </div>
      </div>

      <div className="delay-section">
        <label htmlFor="delaySelectAgendamento">Delay entre mensagens:</label>
        <select
          id="delaySelectAgendamento"
          value={delaySegundos}
          onChange={(e) => setDelaySegundos(Number(e.target.value))}
          style={{
            padding: "6px 10px",
            borderRadius: 6,
            border: "1px solid #ccc",
            fontSize: 14
          }}
        >
          {[3, 4, 5, 6, 7, 8, 9, 10].map((seg) => (
            <option key={seg} value={seg}>
              {seg} segundos
            </option>
          ))}
        </select>
      </div>

      <div className="alerta-delay">
        ⚠️ Intervalos muito curtos aumentam o risco de bloqueio do número de WhatsApp.
      </div>

      <div className="botoes-envio">
        <button
          onClick={() => setMostrarModalAgendamento(false)}
          className="cancelar"
        >
          Cancelar
        </button>

        <button
          onClick={handleEnviarAgendamentoLead}
          className="confirmar"
          disabled={enviandoAgendamento || uploadEmAndamento}
        >
          {uploadEmAndamento
            ? "Aguardando upload..."
            : enviandoAgendamento
            ? "Agendando..."
            : "Confirmar Envio"}
        </button>
      </div>
    </div>
  </div>
)}



</div>  
  </>

);
}
